import { useEffect, useRef, useState } from "react";
import { Drawer, Modal } from "antd";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import CareRichTextEditor from "../../components/care/CareRichTextEditor";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { careService } from "../../services/careService";
import { conversionWarningFor, convertHeicToJpegResult } from "../../utils/heicConversion";
import {
  assessmentRecordSlug,
  formatDate,
  processId,
  processRecords,
  processResidentName,
  recordLabel,
  recordSlug,
} from "./AssessmentsPage";
import { richTextMarkup } from "./richTextUtils";

const meetAndGreetQuestions = [
  "What daily routines make you feel most comfortable?",
  "What support do you need with transfers, walking, bathing, dressing, or toileting?",
  "What medications, allergies, or health concerns should the care team understand?",
  "What behaviors, triggers, or calming approaches should caregivers know?",
  "Who should be contacted first when care needs change?",
  "What activities, foods, faith practices, hobbies, or preferences matter to you?",
];

const signedDocumentOptions = [
  "Admission agreement",
  "Resident rights and responsibilities",
  "Medication management consent",
  "HIPAA/privacy acknowledgment",
  "House policies",
  "Emergency contact authorization",
  "Financial agreement/payment policy",
  "Care service agreement",
];

const defaultCareCircleMembers = [
  ["Elaine Johnson", "Daughter / PoA"],
  ["Robert Johnson", "Son"],
  ["Maya Torres", "Care taker"],
  ["Linda Green", "Guardian contact"],
];

const defaultCareProviderStaff = [
  ["Sarah Mitchell", "Lead caregiver"],
  ["Daniel Reed", "Care aide"],
];

const dailyNoteCategories = [
  "Behavior",
  "Health",
  "Welfare",
  "Medication",
  "Care discrepancy",
  "Family update",
  "Mobility",
  "Meals and hydration",
  "Other",
];

const dateInputValue = (date) => new Date(date).toISOString().slice(0, 10);

const dailyNoteKey = (note = {}) => {
  const value = note || {};
  return value.id || `${value.title || ""}-${value.date || value.noteDate || ""}`;
};

const sameDailyNote = (first = {}, second = {}) => dailyNoteKey(first) === dailyNoteKey(second);

const isMissingDailyNoteEndpoint = (err) => [404, 405].includes(err?.response?.status);

const monthStartInputValue = (date) => {
  const current = new Date(date);
  return dateInputValue(new Date(current.getFullYear(), current.getMonth(), 1));
};

const monthEndInputValue = (date) => {
  const current = new Date(date);
  return dateInputValue(new Date(current.getFullYear(), current.getMonth() + 1, 0));
};

const shiftDate = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const formatDateTime = (date) =>
  new Date(date).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const formatUsDateOnly = (date) => {
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "";
  return parsedDate.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const isWithinDateRange = (date, startDate, endDate) => {
  const target = dateInputValue(date);
  return target >= startDate && target <= endDate;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const negotiatedTemplateSections = [
  {
    title: "Provider and Resident Information",
    fields: [
      ["providerName", "Provider's Name"],
      ["dateNcpStarted", "Date NCP Started"],
      ["movedInDate", "Moved In Date"],
      ["dateCompleted", "Date Completed"],
      ["dateDischarged", "Date Discharged"],
      ["residentName", "Resident's Name"],
      ["pronouns", "Pronouns"],
      ["dateOfBirthAge", "Date of Birth / Age"],
      ["primaryLanguage", "Primary Language"],
      ["speaksEnglish", "Speaks English?"],
      ["interpreterNeeded", "Interpreter needed?"],
      ["allergies", "Allergies"],
    ],
  },
  {
    title: "Legal Documents and Specialty Needs",
    fields: [
      ["legalDocuments", "Legal Documents: None, Advanced Directives, POLST Form, Other"],
      ["specialtyNeeds", "Specialty Needs: No, Dementia, Mental Health, Developmental Disability, Other"],
    ],
  },
  {
    title: "Emergency Evacuation",
    fields: [
      ["evacuationAssistance", "Evacuation Assistance Required"],
      ["evacuationSafetyInstructions", "Resident's Evacuation and Safety Instructions"],
      ["evacuationCaregiverInstructions", "Caregiver will"],
    ],
  },
  {
    title: "Mental/Physical Health - Current Medical Status/Diagnosis",
    fields: [
      ["medicalDiagnosis", "Current Medical Status / Diagnosis"],
      ["healthIndicatorsSummary", "Health indicators, pain, allergies, risks, and concerns"],
    ],
  },
  {
    title: "Responsible Parties - Contacts",
    fields: [
      ["contact1", "Contact 1: Name, relationship, phone/fax, address/email"],
      ["contact2", "Contact 2: Name, relationship, phone/fax, address/email"],
      ["contact3", "Contact 3: Name, relationship, phone/fax, address/email"],
      ["contact4", "Contact 4: Name, relationship, phone/fax, address/email"],
      ["contact5", "Contact 5: Name, relationship, phone/fax, address/email"],
      ["contact6", "Contact 6: Name, relationship, phone/fax, address/email"],
    ],
  },
  {
    title: "Communication, Speech, Hearing, and Vision",
    fields: [
      ["communicationModes", "Modes of Expression"],
      ["expressionProblems", "Problems with Mode of Expression"],
      ["expressionEquipment", "Expression equipment"],
      ["selfUnderstood", "How resident makes self-understood"],
      ["understandsOthers", "How resident understands others"],
      ["communicationStrengths", "Strengths and Abilities"],
      ["communicationAssistance", "Assistance Required / Caregiver will"],
      ["hearingProblems", "Problems with Hearing / equipment"],
      ["visionProblems", "Problems with Vision / equipment"],
      ["phoneAbility", "Ability to Use the Phone"],
      ["preferredLanguageComments", "Preferred language / comments"],
    ],
  },
  {
    title: "Medication Management - Overview",
    fields: [
      ["medicationAllergies", "Medication allergies"],
      ["medicationAssistanceNeeds", "Medication assistance needs"],
      ["psychopharmacologicMeds", "Psychopharmacologic medications"],
      ["marReference", "MAR reference"],
      ["medsOrderedBy", "Meds are ordered by"],
      ["medsDeliveredBy", "Meds are delivered by"],
      ["pharmacyPacked", "Pharmacy packed / note"],
    ],
  },
  {
    title: "Medication Management",
    fields: [
      ["medicationManagementType", "Self-administration / self-administration with assistance / medication administration"],
      ["medicationRoutes", "Oral, topical, eye drops, inhalers, sprays, injections, allergy kits, suppositories, other"],
      ["nurseDelegation", "Requires nurse delegation / RN delegator contact"],
      ["medicationStrengths", "Strengths and Abilities"],
      ["medicationAssistance", "Assistance Required / Caregiver will"],
      ["medicationOutsideAfh", "Medication plan when resident is not in the AFH"],
      ["medicationRefusalPlan", "Medication refusal plan"],
    ],
  },
  {
    title: "Health Indicators",
    fields: [
      ["healthIndicatorMonitoring", "Pain, weight, vital signs, hospitalization, emergency visits, other"],
      ["currentWeightHeight", "Current weight / height"],
      ["healthStrengths", "Strengths and Abilities"],
      ["healthAssistance", "Assistance Required"],
      ["monitoringReporting", "Monitoring/reporting significant changes and concerns"],
      ["allergySubstanceReaction", "Allergy substance / reaction"],
    ],
  },
  {
    title: "Treatments, Programs, Therapies",
    fields: [
      ["treatmentTypes", "Oxygen, dialysis, blood thinners, INR/lab, glucose monitoring, CPAP/BIPAP, nebulizer, other"],
      ["rangeOfMotionTherapies", "Range of motion, PT/OT/ST, nurse delegation tasks"],
      ["treatmentStrengths", "Strengths and Abilities"],
      ["treatmentAssistance", "Assistance Required / Caregiver will"],
      ["programsAttended", "Home Health, Adult Day Health, Hospice, other programs"],
      ["meaningfulDay", "Meaningful Day / interventions"],
      ["physicalEnablers", "Physical enablers"],
      ["treatmentRefusalPlan", "Treatment/program/therapy refusal plan"],
    ],
  },
  {
    title: "Psych/Social/Cognitive Status",
    fields: [
      ["sleepSupport", "Sleep disturbance and nighttime assistance"],
      ["memoryImpairment", "Memory impairment: short-term, long-term, oriented to person"],
      ["decisionMaking", "Impaired decision making"],
      ["behaviors", "Disruptive, assaultive, resistive, depression, anxiety, irritability, disorientation, wandering, exit seeking, hallucinations, delusions, agitation, unsafe behavior, suicidal ideation, disrobing, crying, fears, spitting, throwing, other"],
      ["behaviorAssistance", "Behavior assistance required / caregiver instructions"],
      ["psychMeds", "Psychopharmacological medications and symptoms addressed"],
      ["behavioralHealthSupport", "Behavioral Health Support Crisis Plan, counseling, MHP, past behaviors"],
      ["specializedBehaviorPrograms", "DSHS specialized behavioral programs / contact info"],
      ["specializedBehaviorProgramStrengths", "Specialized behavior program strengths / goals"],
      ["specializedBehaviorProgramAssistance", "Specialized behavior program assistance"],
      ["psychInterventions", "Meaningful Day / other interventions"],
      ["typicalDayNarrative", "Narrative - typical day"],
    ],
  },
  {
    title: "Left Alone and Universal Precautions",
    fields: [
      ["leftAloneAbility", "Ability of Resident to Be Left Unattended"],
      ["leftAloneStrengths", "Strengths and Abilities"],
      ["leftAloneAssistance", "Assistance Required / Caregiver will"],
      ["universalPrecautions", "Always / special precautions / alternative visitation / mask ability / sanitizer safety / vaccinations / shared equipment / other"],
      ["universalPrecautionsStrengths", "Strengths and Abilities"],
      ["universalPrecautionsCaregiver", "Caregiver will"],
      ["universalPrecautionsAssistance", "Assistance Required"],
    ],
  },
  {
    title: "Activities of Daily Living - Ambulation/Mobility",
    fields: [
      ["functionalLimitations", "Resident functional limitations that impact ADL functioning"],
      ["locomotionRoom", "Locomotion in room and immediate living environment"],
      ["locomotionOutside", "Locomotion outside immediate living environment"],
      ["fallRisks", "Risks for falls / fall prevention plan"],
      ["bedroomDoorLock", "Resident chooses bedroom door lock"],
      ["mobilityEquipment", "Equipment/supplies and vendor"],
      ["mobilityLimitations", "Limitations"],
      ["mobilityStrengths", "Strengths and Abilities"],
      ["mobilityAssistance", "Assistance Required / Caregiver will"],
    ],
  },
  {
    title: "Bed Mobility/Transfer",
    fields: [
      ["transferLevel", "Independent / supervision-cueing / assistance needed / totally dependent"],
      ["skinCarePositioning", "Skin care required due to inability to position self"],
      ["turningRepositioning", "Turning and repositioning frequency"],
      ["transferSafetyPlan", "Safety assessment, fall prevention, safety plan"],
      ["medicalDevices", "Medical devices/enablers, Hoyer lift, transfer pole, risk assessment, nighttime needs"],
      ["transferEquipment", "Equipment/supplies"],
      ["transferStrengths", "Strengths and Abilities"],
      ["transferAssistance", "Assistance Required / Caregiver will"],
    ],
  },
  {
    title: "Eating",
    fields: [
      ["eatingLevel", "Independent / supervision-cueing / assistance needed / totally dependent"],
      ["specialDiet", "Special diet/supplements, eating habits, food allergies"],
      ["eatingEquipment", "Equipment/supplies/procedures"],
      ["eatingLimitations", "Limitations"],
      ["eatingStrengths", "Strengths and Abilities"],
      ["eatingAssistance", "Assistance Required / Caregiver will"],
    ],
  },
  {
    title: "Toileting/Continence Issues",
    fields: [
      ["toiletingLevel", "Independent / supervision-cueing / assistance needed / totally dependent"],
      ["toiletingFrequency", "Frequency/how often"],
      ["continenceIssues", "Bladder, bowel, skin care, catheter, ostomy, equipment, limitations"],
      ["toiletingStrengths", "Strengths and Abilities"],
      ["toiletingAssistance", "Assistance Required / Caregiver will"],
    ],
  },
  {
    title: "Dressing, Personal Hygiene, Bathing, Foot Care, and Skin Care",
    fields: [
      ["dressing", "Dressing support, equipment, procedures, limitations, strengths, assistance"],
      ["personalHygiene", "Hair, teeth, shaving, makeup, face/hands/perineum, dentures, oral hygiene, menses care"],
      ["bathing", "Shower/sponge bath/tub transfer, when/how often, equipment, limitations, strengths, assistance"],
      ["footCare", "Foot care, diabetic foot care, nail care, home health agency, equipment, limitations"],
      ["skinCare", "Routine/special skin care, skin problems, pressure injuries, dressing changes, nurse delegation, home health, equipment"],
    ],
  },
  {
    title: "Instrumental Activities of Daily Living",
    fields: [
      ["finances", "Managing finances, financial records, payee name/contact"],
      ["shopping", "Shopping assistance, special transportation needs, frequency, equipment, limitations"],
      ["transportation", "Medical services, special transportation, escort required, frequency, equipment, limitations"],
      ["activitiesSocial", "Social/cultural considerations, interests, relationships, employment support, groups/day health, special arrangements, participation issues"],
      ["activityPreferences", "Activity preferences at a glance and activity narrative"],
      ["smoking", "Resident smokes, safety concerns, smoking policy, cigarette/lighter storage"],
    ],
  },
  {
    title: "Case Management and Other Issues",
    fields: [
      ["caseManagement", "Case management name, agency, phone, email, fax"],
      ["cmContactedWhen", "When CM will be contacted"],
      ["otherIssues", "Other issues, concerns, or problems"],
    ],
  },
  {
    title: "Negotiated Care Plan Review and Approval",
    fields: [
      ["reviewSchedule", "Original plan date and dates of review/revision"],
      ["residentParticipation", "Resident participation in NCP development/reviews"],
      ["reviewRevisionSchedule", "Required NCP review/revision schedule"],
      ["involvedInDevelopment", "Involved in NCP development"],
      ["approvingPerson", "Person approving plan"],
      ["providerSignature", "Provider signature and date"],
      ["residentSignature", "Resident signature and date"],
      ["residentRepresentativeSignature", "Resident representative signature and date"],
      ["residentVerbalAgreement", "Resident verbally agreed to NCP - date"],
      ["sentToDshs", "NCP sent to DSHS CM on"],
      ["residentRecommendations", "Resident recommendations"],
    ],
  },
];

const ncpContactColumns = [
  { key: "name", label: "Name" },
  { key: "relationship", label: "Relationship" },
  { key: "homeBusinessPhone", label: "Home/Business Phone" },
  { key: "cellFax", label: "Cell Phone/FAX" },
  { key: "addressEmail", label: "Address/Email" },
];

const ncpBehaviorCheckboxLabels = [
  "Disruptive behavior",
  "Assaultive",
  "Resistive to care",
  "Depression",
  "Anxiety",
  "Irritability",
  "Disorientation",
  "Wandering in home/Pacing",
  "Exit seeking",
  "Hallucinations",
  "Delusions",
  "Verbally agitated/aggressive",
  "Physically agitated/aggressive",
  "Inappropriate or unsafe behavior",
  "Suicidal Ideation",
  "Difficulty in new or unfamiliar situations",
  "Disrobing",
  "Weeping/Crying",
  "Unaware of Consequences",
  "Unrealistic fears and suspicions",
  "Inappropriate spitting",
  "Breaks/throws things",
  "Other",
];

const ncpContactFieldKey = (rowNumber, columnKey) => `contact${rowNumber}${columnKey.charAt(0).toUpperCase()}${columnKey.slice(1)}`;

const ncpPageOneIndexLinks = [
  "Activities/Social",
  "Allergies",
  "Ambulation/Mobility",
  "Bathing",
  "Bed Mobility/Transfer",
  "Behavior",
  "Case Management",
  "Communication",
  "Decision Making",
  "Dressing",
  "Eating",
  "Falls (Ambulation)",
  "Falls (Bed)",
  "Finances",
  "Foot Care",
  "Health Indicators",
  "Left Alone",
  "Medication Management",
  "Memory",
  "Mental/Phy. Health/Diag.",
  "NCP Review/Signatures",
  "Other Issues/Concerns",
  "Pain",
  "Personal Hygiene",
  "Shopping",
  "Skin Care",
  "Sleep",
  "Smoking",
  "Specialized Beh. Prog.",
  "Toilet Use/Continence",
  "Transportation",
  "Treat/Prog/Therapies",
  "Universal Precautions",
  "Vision",
];

const medicationManagementRouteOptions = [
  "Oral",
  "Topical",
  "Eye drops/ointment",
  "Inhalers",
  "Sprays",
  "Injections",
  "Allergy Kits",
  "Suppositories",
  "Other",
];

const ncpPageThreeMedicationRouteOptions = medicationManagementRouteOptions.slice(0, 5);
const ncpPageFourMedicationRouteOptions = medicationManagementRouteOptions.slice(5);
const injectionSupportOptions = [
  "Resident",
  "Surrogate",
  "By Family",
  "Licensed Professional",
  "Qualified CG under Nurse Delegation",
  "(Insulin Only)",
];
const healthIndicatorOptions = [
  { text: "Pain", checkIndex: 0 },
  { text: "Pain Impact:", nested: true },
  { text: "Weight Loss/Gain", checkIndex: 1 },
  { text: "Current Weight:", nested: true },
  { text: "Current Height:", nested: true },
  { text: "Vital Signs", checkIndex: 2 },
  { text: "Hospitalization or Emergency Visits", checkIndex: 3 },
  { text: "Other:", checkIndex: 4 },
];
const treatmentTypeOptions = [
  "Oxygen Use - Vendor:",
  "Dialysis - Health Provider:",
  "Blood Thinners",
  "INR/LAB - Health Provider:",
  "Easily bruised/Anti coagulation therapy",
  "Blood Glucose Monitoring",
  "Injection",
  "CPAP/BIPAP",
  "Nebulizer",
];
const treatmentTherapyOptions = [
  "Range of Motion",
  "PT/OT/ST",
  "Nurse Delegation for",
  "Treatments/Therapies - Tasks:",
  "Other:",
];
const treatmentProgramOptions = [
  "Home Health",
  "Adult Day Health",
  "Hospice - Agency:",
  "Hospice Plan",
  "Other:",
];
const psychStatusCells = [
  {
    className: "ncp-psych-group ncp-psych-sleep",
    lines: [
      { text: "Sleep", section: true },
      { text: "Sleep disturbance", blue: true, checkbox: true, checkKey: "sleepSupport", checkIndex: 0 },
      { text: "Nighttime", suffix: " assistance needed", blue: true, checkbox: true, checkKey: "sleepSupport", checkIndex: 1 },
    ],
  },
  {
    className: "ncp-psych-group ncp-psych-memory",
    lines: [
      { text: "Memory Impairment", section: true },
      { text: "Short-term", blue: true, checkbox: true, checkKey: "memoryImpairment", checkIndex: 0 },
      { text: "Long-term", blue: true, checkbox: true, checkKey: "memoryImpairment", checkIndex: 1 },
      { text: "Orientated to Person", checkbox: true, checkKey: "memoryImpairment", checkIndex: 2 },
    ],
  },
  {
    className: "ncp-psych-group ncp-psych-decision",
    lines: [
      { text: "If any behavior is checked, describe in the column to the right.", note: true },
      { text: "Impaired decision making", blue: true, checkbox: true, checkKey: "decisionMaking", checkIndex: 0 },
    ],
  },
  ...ncpBehaviorCheckboxLabels.slice(0, 15).map((text, index) => ({
    className: "ncp-psych-row",
    lines: [{ text, blue: text !== "Wandering in home/Pacing", blueText: text === "Wandering in home/Pacing" ? "Wandering in home" : null, suffix: text === "Wandering in home/Pacing" ? "/Pacing" : "", checkbox: true, checkKey: "behaviorCheckboxes", checkIndex: index }],
  })),
];
const psychPageEightBehaviorRows = ncpBehaviorCheckboxLabels.slice(15);
const psychPageEightSupportLines = [
  { text: "Requires psychopharmacological medications. If checked, describe symptoms each medication is addressing) See Current MAR ", checkbox: true, strong: true, checkKey: "psychMeds", checkIndex: 0 },
  { text: "WAC 388-76-10463", suffix: " (link)", blue: true, indent: true },
  { text: "Behavioral Health Support Crisis Plan (See attached crisis plan)", checkbox: true, checkKey: "behavioralHealthSupport", checkIndex: 0 },
  { text: "Counseling", checkbox: true, checkKey: "behavioralHealthSupport", checkIndex: 1 },
  { text: "Mental Health Provider (MHP):" },
  { text: "Past Behaviors:", spacer: true },
];
const specializedBehaviorProgramLines = [
  { text: "DSHS Specialized Behavioral Programs:", strong: true },
  { text: "Meaningful Day", checkIndex: 0 },
  { text: "Expanded Community Services", checkIndex: 1 },
  { text: "(HCS ONLY)" },
  { text: "Specialized Behavior Services (HCS ONLY)", checkIndex: 2 },
  { text: "Mental Health Provider/Program", checkIndex: 3 },
  { text: "Contact info:", spacer: true },
];
const universalPrecautionLines = [
  { text: "Always", checkIndex: 0 },
  { text: "Special Precautions:", blue: true, checkIndex: 1 },
  { text: "Alternative method for visitation", checkIndex: 2 },
  { text: "Mask - Resident is:", noBox: true },
  { text: "Able to wear a mask", nested: true, checkIndex: 3 },
  { text: "Not able to wear a mask", nested: true, checkIndex: 4 },
  { text: "Resident is safe to have sanitizer or disinfectant wipes left out for caregiver and client use.", checkIndex: 5 },
];
const universalPrecautionPageTenLines = [
  { text: "Resident has been/or is up to date on", suffix: " vaccinations:", blueSuffix: true, checkIndex: 6 },
  { text: "Resident shares the following medical equipment;", checkIndex: 7 },
  { text: "Other:", checkIndex: 8 },
];

const countNcpLabelChecks = (label) => String(label || "").match(/\[\s*\]/g)?.length || 0;

const negotiatedTemplateDefinitionFromBackend = (template) => {
  if (!Array.isArray(template?.pages) || !template.pages.length) return null;
  return template.pages.map((page) => ({
    pageNumber: page.pageNumber,
    title: page.title || `Page ${page.pageNumber} of ${template.pageCount || 19}`,
    sections: (page.sections || []).map((section, sectionIndex) => ({
      id: section.id || `page-${page.pageNumber}-section-${sectionIndex + 1}`,
      title: section.title,
      fields: (section.fields || []).map((field) => ({
        id: field.id,
        key: field.key,
        label: field.label,
        checkCount: field.checkCount,
        source: field.source,
      })).filter((field) => field.key && field.label),
    })),
  }));
};

const negotiatedTemplateDefinition = (template) => {
  const definition = negotiatedTemplateDefinitionFromBackend(template) || [];
  return definition.map((page) => {
    if (page.pageNumber !== 9) return page;
    let inserted = false;
    return {
      ...page,
      sections: page.sections.map((section) => {
        const hasIntervention = section.fields.some((field) => field.key === "psychInterventions");
        const hasAssistance = section.fields.some((field) => field.key === "psychInterventionAssistance");
        if (!hasIntervention || hasAssistance || inserted) return section;
        inserted = true;
        return {
          ...section,
          fields: [
            ...section.fields,
            {
              id: "page-9-psych-intervention-assistance",
              key: "psychInterventionAssistance",
              label: "Intervention assistance",
              checkCount: 0,
              source: "local",
            },
          ],
        };
      }),
    };
  });
};

const createNegotiatedPlanPayload = ({ assessmentId, recordId, initialValues = {}, template = null }) => ({
  version: template?.version || "AFH-HCS-NCP-3.12.2024",
  templateKey: template?.templateKey || "afh-hcs-ncp",
  assessmentId,
  recordId,
  residentId: assessmentId?.replace(/^ap-/, "") || "",
  status: "draft",
  pages: negotiatedTemplateDefinition(template).map((page) => ({
    pageNumber: page.pageNumber,
    title: page.title || `Page ${page.pageNumber} of ${template?.pageCount || 19}`,
    sections: page.sections.map((section, sectionIndex) => ({
      id: section.id || `page-${page.pageNumber}-section-${sectionIndex + 1}`,
      title: section.title,
      fields: section.fields.map((field, fieldIndex) => ({
        id: field.id || `page-${page.pageNumber}-section-${sectionIndex + 1}-field-${fieldIndex + 1}-${field.key}`,
        key: field.key,
        label: field.label,
        source: field.source || null,
        value: initialValues[field.key] || "",
        checks: Array.from({ length: field.checkCount ?? countNcpLabelChecks(field.label) }, (_, index) => ({
          index,
          checked: false,
        })),
      })),
    })),
  })),
});

const cloneNegotiatedPlanPayload = (payload) => JSON.parse(JSON.stringify(payload));

const findNegotiatedPayloadField = (payload, key) => {
  for (const page of payload?.pages || []) {
    for (const section of page.sections || []) {
      const field = (section.fields || []).find((item) => item.key === key);
      if (field) return field;
    }
  }
  return null;
};

const ensureNegotiatedPlanPayloadFields = (payload, template = null) => {
  const normalized = createNegotiatedPlanPayload({
    assessmentId: payload?.assessmentId,
    recordId: payload?.recordId,
    template,
  });
  const existingById = new Map();
  const existingByKey = new Map();
  (payload?.pages || []).forEach((page) => {
    (page.sections || []).forEach((section) => {
      (section.fields || []).forEach((field) => {
        if (field.id) existingById.set(field.id, field);
        if (!existingByKey.has(field.key)) existingByKey.set(field.key, field);
      });
    });
  });

  return {
    ...payload,
    pages: normalized.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const existing = existingById.get(field.id) || existingByKey.get(field.key);
          return existing ? {
            ...field,
            ...existing,
            id: field.id,
            key: field.key,
            label: field.label,
            source: existing.source || field.source || null,
            value: cleanGeneratedNcpFieldValue(field.key, existing.value || ""),
          } : field;
        }),
      })),
    })),
  };
};

const updateNegotiatedPayloadField = (payload, key, updater) => ({
  ...payload,
  pages: (payload.pages || []).map((page) => ({
    ...page,
    sections: (page.sections || []).map((section) => ({
      ...section,
      fields: (section.fields || []).map((field) => (
        field.key === key ? updater(field) : field
      )),
    })),
  })),
});

const defaultNegotiatedPlanRecord = () => ({
  status: "Draft",
  templateName: "AFH HCS NCP-Template 10.11.23.docx",
  formVersion: "3.12.2024",
  dataSources: [
    "State Assessment",
    "Meet and Greet",
    "Onboarding",
    "Preliminary Plan",
    "30 Days Probation",
    "Daily Notes",
  ],
  attachedFiles: [],
});

const normalizeSourceText = (value) => String(value || "")
  .replace(/<[^>]+>/g, " ")
  .replace(/\[REVIEW\]\s*/gi, "")
  .replace(/\s+/g, " ")
  .trim();

const parseRecordObject = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const firstFilled = (...values) =>
  values.find((value) => String(value ?? "").trim()) ?? "";

const joinFilled = (...values) => values.map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const compactLines = (...lines) => lines.map(normalizeSourceText).filter(Boolean).join("\n");

const compactSentence = (...parts) => parts.map(normalizeSourceText).filter(Boolean).join("; ");

const personName = (person = {}) => {
  const value = person || {};
  return firstFilled(
    value.displayName,
    value.fullName,
    value.name,
    [value.firstName, value.middleName, value.lastName].filter(Boolean).join(" "),
    value.email,
  );
};

const digitalSignatureRoleOptions = [
  { value: "provider", label: "Provider", fieldKey: "providerSignature", title: "Care Provider", involvedIndex: 3 },
  { value: "resident", label: "Resident", fieldKey: "residentSignature", title: "Resident", involvedIndex: 0 },
  { value: "residentRepresentative", label: "Resident Representative", fieldKey: "residentRepresentativeSignature", title: "Resident Representative", involvedIndex: 1 },
];

const digitalSignatureRoleByValue = (value) =>
  digitalSignatureRoleOptions.find((option) => option.value === value) || digitalSignatureRoleOptions[0];

const browserSignatureLocation = () =>
  new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ status: "unavailable", label: "Browser location not available" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        status: "captured",
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracyMeters: position.coords.accuracy,
        label: `${position.coords.latitude.toFixed(5)}, ${position.coords.longitude.toFixed(5)} (${Math.round(position.coords.accuracy || 0)}m accuracy)`,
      }),
      (error) => resolve({
        status: "not_shared",
        label: error?.message ? `Location not shared: ${error.message}` : "Location not shared",
      }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });

const ageFromDate = (value) => {
  if (!value) return "";
  const birthDate = new Date(value);
  if (Number.isNaN(birthDate.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDelta = today.getMonth() - birthDate.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) age -= 1;
  return age > -1 ? String(age) : "";
};

const dateValueFromText = (value = "") => {
  const text = String(value || "").trim();
  const iso = text.match(/\b\d{4}-\d{2}-\d{2}\b/);
  if (iso) return iso[0];
  const numeric = text.match(/\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/);
  if (numeric) {
    const parsed = new Date(numeric[0]);
    return Number.isNaN(parsed.getTime()) ? numeric[0] : parsed.toISOString().slice(0, 10);
  }
  const longDate = text.match(/\b\d{1,2}\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\b/i);
  if (longDate) {
    const [day, monthName, year] = longDate[0].split(/\s+/);
    const monthIndex = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ].indexOf(monthName.toLowerCase()) + 1;
    return monthIndex
      ? `${year}-${String(monthIndex).padStart(2, "0")}-${String(Number(day)).padStart(2, "0")}`
      : longDate[0];
  }
  return text;
};

const listFromMaybeArray = (value) => Array.isArray(value) ? value : [];

const negotiatedInitialValuesFromAssessment = (assessmentInput = {}, dailyNotes = [], extractionDocument = null, weeklyReports = []) => {
  const assessment = assessmentInput || {};
  const initialPayload = parseRecordObject(assessment.initialStateAssessment || assessment.stateAssessment);
  const onboardingPayload = parseRecordObject(assessment.onboardingPayload || assessment.clientOnboarding || assessment.onboarding);
  const preliminaryPayload = parseRecordObject(assessment.preliminaryPlan || assessment.preliminaryPlanPayload);
  const resident = {
    ...(parseRecordObject(assessment.residentData)),
    ...(parseRecordObject(assessment.residentDetails)),
    ...(parseRecordObject(assessment.resident)),
  };
  const bio = {
    ...(parseRecordObject(initialPayload.bioData)),
    ...(parseRecordObject(initialPayload.residentBioData)),
    ...(parseRecordObject(initialPayload.bio)),
    ...(parseRecordObject(onboardingPayload.bio)),
    ...(parseRecordObject(onboardingPayload.resident)),
    ...(parseRecordObject(resident.personalInformation)),
  };
  const snapshot = {
    ...(parseRecordObject(initialPayload.initialCareSnapshot)),
    ...(parseRecordObject(initialPayload.careSnapshot)),
    ...(parseRecordObject(onboardingPayload.careSnapshot)),
    ...(parseRecordObject(preliminaryPayload.summaryForm)),
    ...(parseRecordObject(preliminaryPayload.preliminaryPlanForm)),
  };
  const contactInfo = {
    ...(parseRecordObject(resident.contactInformation)),
    ...(parseRecordObject(bio.residentContactInformation)),
    ...(parseRecordObject(bio.contactInformation)),
  };
  const contacts = [
    ...listFromMaybeArray(initialPayload.contacts),
    ...listFromMaybeArray(bio.collateralContacts),
    ...listFromMaybeArray(bio.contacts),
    ...listFromMaybeArray(resident.contacts),
    ...listFromMaybeArray(resident.collateralContacts),
    ...listFromMaybeArray(contactInfo.contacts),
  ].filter(Boolean);
  const birthDate = dateValueFromText(firstFilled(
    extractedValueFor(extractionDocument, [/client\s+date\s+of\s+birth/i, /resident\s+date\s+of\s+birth/i, /^date\s+of\s+birth$/i, /^dob$/i], ["client_profile"]),
    bio.dateOfBirth,
    bio.birthDate,
    bio.dob,
    resident.dateOfBirth,
    resident.birthDate,
    resident.dob,
    assessment.residentDateOfBirth,
    assessment.residentBirthDate,
  ));
  const age = ageFromDate(birthDate);
  const primaryLanguage = firstFilled(
    extractedValueFor(extractionDocument, [/preferred\s+language/i, /^language$/i], ["client_profile"]),
    bio.primaryLanguage,
    bio.language,
    resident.primaryLanguage,
    resident.language,
    assessment.residentPrimaryLanguage,
  );
  const allergies = firstFilled(
    compactLines(...extractedValuesFor(extractionDocument, [/allerg/i], ["health_services", "medications"], 8)),
    snapshot.allergies,
    bio.allergies,
    resident.allergies,
    assessment.allergies,
  );
  const diagnosis = firstFilled(
    compactLines(...extractedValuesFor(extractionDocument, [/diagnos|condition|medical\s+history|health\s+condition|problem/i], ["mental_physical_health", "health_services"], 14)),
    snapshot.diagnosis,
    snapshot.conditionSummary,
    resident.diagnosis,
    assessment.diagnosis,
  );
  const careNeeded = firstFilled(
    compactLines(
      extractedTextForSections(extractionDocument, [
        "activities_daily_living",
        "mobility_transfers",
        "continence",
        "personal_care",
        "nutrition_diet",
      ], 900),
    ),
    snapshot.careNeeded,
    snapshot.careNeeds,
    snapshot.careLevel,
    assessment.careNeeded,
  );
  const behaviorNotes = firstFilled(
    extractedTextForSections(extractionDocument, ["behaviour_emotional", "safety_communication", "sleep_memory"], 900),
    snapshot.behaviorSafetyNotes,
    snapshot.behaviorNotes,
    snapshot.safetyNotes,
    assessment.behaviorSafetyNotes,
  );
  const medicationNotes = firstFilled(
    extractedTextForSections(extractionDocument, ["medications"], 1000),
    snapshot.medicationNotes,
    snapshot.medications,
    snapshot.medicationSummary,
    assessment.medicationNotes,
  );
  const dailyBehaviorSummaries = ncpDailyFieldSummariesFromNotes(dailyNotes);
  const behaviorEvidenceSummary = dailyBehaviorSummaries.behaviors || dailyBehaviorSummaries.behavioralSymptoms || "";
  const behaviorInterventionSummary = dailyBehaviorSummaries.behaviorAssistance || "";
  const clientProfileText = extractedTextForSections(extractionDocument, ["client_profile"], 900);
  const providerText = extractedTextForSections(extractionDocument, ["provider_information"], 600);
  const communicationText = extractedTextForSections(extractionDocument, ["safety_communication"], 900);
  const healthText = extractedTextForSections(extractionDocument, ["health_services", "mental_physical_health"], 1000);
  const treatmentText = compactLines(
    ...extractedValuesFor(extractionDocument, [/treatment|therapy|program|oxygen|dialysis|inr|blood\s+glucose|cpap|nebulizer|delegation/i], ["health_services"], 12),
  ) || healthText;
  const specializedBehaviorProgramText = compactLines(
    ...extractedValuesFor(extractionDocument, [/specialized\s+behavior|expanded\s+community|behavior\s+services|mental\s+health\s+provider\/program|dshs\s+specialized|meaningful\s+day/i], ["behaviour_emotional", "health_services"], 8),
  );
  const sleepMemoryText = extractedTextForSections(extractionDocument, ["sleep_memory"], 900);
  const mobilityText = extractedTextForSections(extractionDocument, ["mobility_transfers"], 1000);
  const eatingText = extractedTextForSections(extractionDocument, ["nutrition_diet"], 900);
  const toiletingText = extractedTextForSections(extractionDocument, ["continence"], 900);
  const personalCareText = extractedTextForSections(extractionDocument, ["personal_care"], 1000);
  const skinFootCareText = extractedTextForSections(extractionDocument, ["skin_foot_care"], 1000);
  const instrumentalText = extractedTextForSections(extractionDocument, ["instrumental_daily_living", "transportation"], 1000);
  const activityText = compactLines(
    ...extractedValuesFor(extractionDocument, [/activit|social|cultural|tradition|preference|interest|routine|employment|community|church|music|garden|visitor/i], [
      "client_profile",
      "activities_daily_living",
      "instrumental_daily_living",
      "safety_communication",
    ], 14),
  );
  const financeText = compactLines(
    ...extractedValuesFor(extractionDocument, [/financ|money|payee|bill|benefit|representative\s+payee/i], ["client_profile", "instrumental_daily_living"], 10),
  );
  const shoppingText = compactLines(
    ...extractedValuesFor(extractionDocument, [/shopping|shop|errand|store/i], ["instrumental_daily_living"], 8),
  );
  const transportationText = compactLines(
    ...extractedValuesFor(extractionDocument, [/transport|escort|appointment|vehicle|ride/i], ["transportation", "instrumental_daily_living"], 10),
  );
  const smokingText = compactLines(
    ...extractedValuesFor(extractionDocument, [/smok|cigarette|lighter|tobacco/i], ["health_services", "safety_communication"], 8),
  );
  const caseManagementText = compactLines(
    ...extractedValuesFor(extractionDocument, [/case\s+manager|social\s+worker|dshs|hcs|agency|assessor/i], ["client_profile", "provider_information"], 10),
  );
  const contactRows = ncpContactRowsFromExtraction(extractionDocument);
  const extractedResidentName = firstFilled(
    extractedValueFor(extractionDocument, [/client\s+name/i, /resident\s+name/i], ["client_profile"]),
    processResidentName(assessment),
  );
  const notesSource = compactLines(
    normalizeSourceText(preliminaryPayload.description || preliminaryPayload.preliminaryPlanDescription),
    normalizeSourceText(snapshot.preliminaryPlan?.goals || snapshot.goals),
    normalizeSourceText(snapshot.preliminaryPlan?.careApproach || snapshot.careApproach),
  );

  const initialValues = {
    providerName: firstFilled(
      extractedValueFor(extractionDocument, [/provider\s+facility\s+name/i, /^facility$/i, /facility\s+name/i], ["provider_information", "client_profile"]),
      assessment.providerName,
      assessment.careProviderName,
      assessment.organizationName,
      assessment.facilityName,
    ),
    dateNcpStarted: firstFilled(assessment.negotiatedCarePlanStartDate, assessment.ncpStartDate, assessment.processStartDate, assessment.startDate),
    movedInDate: dateValueFromText(firstFilled(
      extractedValueFor(extractionDocument, [/admission\s+date/i, /move.?in/i], ["client_profile"]),
      resident.moveInDate,
      resident.admissionDate,
      bio.moveInDate,
      assessment.moveInDate,
      assessment.admissionDate,
    )),
    residentName: extractedResidentName,
    pronouns: /female/i.test(extractedValueFor(extractionDocument, [/client\s+gender|resident\s+gender|sex|gender/i], ["client_profile"])) ? "She/Her" : "",
    dateOfBirthAge: [birthDate ? formatDate(birthDate) : "", age ? `${age} years` : ""].filter(Boolean).join(" / "),
    primaryLanguage,
    speaksEnglish: primaryLanguage ? (/english/i.test(primaryLanguage) ? "Yes" : "No") : "",
    interpreterNeeded: primaryLanguage ? (/english/i.test(primaryLanguage) ? "No" : "Yes") : "",
    allergies,
    legalDocuments: compactSentence(
      extractedValueFor(extractionDocument, [/polst|advanced?\s+directives?|living\s+will|durable\s+power\s+of\s+attorney|guardian/i], ["client_profile", "health_services"]),
      bio.advancedDirectives,
      bio.codeStatus,
      resident.advancedDirectives,
      resident.codeStatus,
    ),
    specialtyNeeds: firstFilled(snapshot.specialtyNeeds, bio.specialtyNeeds, resident.specialtyNeeds, diagnosis),
    evacuationAssistance: firstFilled(
      extractedValueFor(extractionDocument, [/evacuation|emergency\s+classification|fall\s+risk/i], ["safety_communication", "client_profile"]),
    ),
    evacuationSafetyInstructions: firstFilled(communicationText, behaviorNotes),
    evacuationCaregiverInstructions: firstFilled(
      compactLines(...extractedValuesFor(extractionDocument, [/caregiver.*safety|safety.*instruction|evacuat|fall/i], ["safety_communication"], 8)),
      behaviorNotes,
    ),
    medicalDiagnosis: diagnosis,
    healthIndicatorsSummary: compactLines(allergies ? `Allergies: ${allergies}` : "", careNeeded, notesSource),
    medicationAllergies: allergies,
    medicationAssistanceNeeds: medicationNotes,
    medicationManagementType: medicationNotes,
    medicationRoutes: medicationNotes,
    marReference: medicationNotes ? "See current MAR and extracted assessment medication notes." : "",
    medsOrderedBy: compactLines(...extractedValuesFor(extractionDocument, [/ordered\s+by|physician|doctor|provider/i], ["medications", "health_services"], 6)),
    medsDeliveredBy: compactLines(...extractedValuesFor(extractionDocument, [/pharmacy|delivered|packed/i], ["medications", "health_services"], 6)),
    pharmacyPacked: compactLines(...extractedValuesFor(extractionDocument, [/pharmacy|packed|bubble|medication/i], ["medications"], 8)),
    medicationStrengths: medicationNotes,
    medicationAssistance: medicationNotes,
    medicationRefusalPlan: firstFilled(snapshot.medicationRefusalPlan, snapshot.refusalPlan),
    medicationOutsideAfhStrengths: medicationNotes,
    medicationOutsideAfh: medicationNotes,
    medicationRefusalStrengths: medicationNotes,
    healthIndicatorMonitoring: firstFilled(healthText, allergies),
    healthStrengths: careNeeded,
    healthAssistance: careNeeded,
    monitoringReporting: firstFilled(
      compactLines(...(extractionDocument?.caregiverSummary?.reportingInstructions || [])),
      "Caregiver will monitor for significant changes and report concerns to the provider, POA, case manager, or nurse as appropriate.",
    ),
    allergySubstanceReaction: allergies,
    treatmentTypes: treatmentText,
    rangeOfMotionTherapies: treatmentText,
    treatmentStrengths: treatmentText,
    treatmentAssistance: treatmentText,
    programsAttended: treatmentText,
    physicalEnablers: mobilityText,
    treatmentRefusalPlan: treatmentText,
    communicationComments: firstFilled(communicationText, clientProfileText),
    communicationModes: firstFilled(bio.communicationModes, snapshot.communicationModes, communicationText),
    expressionProblems: communicationText,
    expressionEquipment: communicationText,
    selfUnderstood: communicationText,
    understandsOthers: communicationText,
    communicationStrengths: firstFilled(clientProfileText, communicationText),
    communicationAssistance: firstFilled(communicationText, careNeeded),
    hearingProblems: extractedValueFor(extractionDocument, [/hearing/i], ["safety_communication", "health_services"]),
    visionProblems: extractedValueFor(extractionDocument, [/vision|glasses|eye/i], ["safety_communication", "health_services"]),
    phoneAbility: firstFilled(contactInfo.phone, contactInfo.mobilePhone, bio.mobilePhone, communicationText),
    preferredLanguageComments: primaryLanguage,
    sleepSupport: sleepMemoryText,
    sleepStrengths: sleepMemoryText,
    sleepAssistance: sleepMemoryText,
    memoryImpairment: sleepMemoryText,
    memoryStrengths: sleepMemoryText,
    memoryAssistance: sleepMemoryText,
    decisionMaking: firstFilled(
      extractedValueFor(extractionDocument, [/decision|capacity/i], ["client_profile", "sleep_memory"]),
      sleepMemoryText,
    ),
    behaviorSupportPlan: compactLines(behaviorNotes, dailyBehaviorSummaries.behaviorSupportPlan || behaviorEvidenceSummary),
    behavioralSymptoms: compactLines(behaviorNotes, dailyBehaviorSummaries.behavioralSymptoms || behaviorEvidenceSummary),
    behaviors: compactLines(behaviorNotes, behaviorEvidenceSummary),
    behaviorAssistance: compactLines(behaviorInterventionSummary),
    psychMeds: medicationNotes,
    behavioralHealthSupport: compactLines(behaviorNotes, dailyBehaviorSummaries.behavioralHealthSupport, treatmentText),
    specializedBehaviorPrograms: treatmentText,
    specializedBehaviorProgramStrengths: specializedBehaviorProgramText,
    specializedBehaviorProgramAssistance: specializedBehaviorProgramText,
    psychInterventions: compactLines(dailyBehaviorSummaries.psychInterventions, behaviorNotes),
    psychInterventionAssistance: dailyBehaviorSummaries.psychInterventionAssistance,
    typicalDayNarrative: notesSource,
    leftAloneAbility: firstFilled(
      extractedValueFor(extractionDocument, [/left\s+alone|unattended|supervision/i], ["safety_communication"]),
      communicationText,
    ),
    leftAloneStrengths: communicationText,
    leftAloneAssistance: communicationText,
    universalPrecautions: communicationText,
    universalPrecautionsStrengths: communicationText,
    universalPrecautionsAssistance: communicationText,
    functionalLimitations: firstFilled(mobilityText, careNeeded),
    locomotionRoom: mobilityText,
    locomotionOutside: mobilityText,
    fallRisks: firstFilled(
      compactLines(...extractedValuesFor(extractionDocument, [/fall|mobility|ambulat|walker|wheelchair/i], ["mobility_transfers", "safety_communication"], 10)),
      mobilityText,
    ),
    mobilityEquipment: mobilityText,
    mobilityLimitations: mobilityText,
    mobilityStrengths: mobilityText,
    mobilityAssistance: mobilityText,
    transferLevel: mobilityText,
    skinCarePositioning: firstFilled(extractedTextForSections(extractionDocument, ["skin_foot_care"], 700), mobilityText),
    turningRepositioning: mobilityText,
    transferSafetyPlan: mobilityText,
    medicalDevices: mobilityText,
    transferEquipment: mobilityText,
    transferStrengths: mobilityText,
    transferAssistance: mobilityText,
    eatingLevel: eatingText,
    specialDiet: eatingText,
    eatingEquipment: eatingText,
    eatingStrengths: eatingText,
    eatingAssistance: eatingText,
    toiletingLevel: toiletingText,
    continenceIssues: toiletingText,
    toiletingStrengths: toiletingText,
    toiletingAssistance: toiletingText,
    dressing: firstFilled(
      compactLines(...extractedValuesFor(extractionDocument, [/dressing|dress\b/i], ["personal_care"], 8)),
      personalCareText,
    ),
    personalHygiene: firstFilled(
      compactLines(...extractedValuesFor(extractionDocument, [/hygiene|groom|oral|teeth|dentures|hair|shav/i], ["personal_care"], 10)),
      personalCareText,
    ),
    bathing: firstFilled(
      compactLines(...extractedValuesFor(extractionDocument, [/bath|shower|tub/i], ["personal_care"], 8)),
      personalCareText,
    ),
    footCare: firstFilled(
      compactLines(...extractedValuesFor(extractionDocument, [/foot|feet|nail|diabetic\s+foot|podiat/i], ["skin_foot_care", "health_services"], 8)),
      skinFootCareText,
    ),
    skinCare: firstFilled(
      compactLines(...extractedValuesFor(extractionDocument, [/skin|pressure|wound|ulcer|rash|dressing\s+change|position/i], ["skin_foot_care", "personal_care", "health_services"], 12)),
      skinFootCareText,
    ),
    finances: firstFilled(financeText, instrumentalText),
    shopping: firstFilled(shoppingText, instrumentalText),
    transportation: firstFilled(transportationText, instrumentalText),
    activitiesSocial: firstFilled(activityText, notesSource, clientProfileText),
    activityPreferences: firstFilled(activityText, snapshot.activityPreferences, snapshot.activities, snapshot.residentPreferences),
    activityNarrative: firstFilled(activityText, notesSource),
    specialArrangements: firstFilled(activityText, careNeeded),
    smoking: firstFilled(smokingText, "No smoking concerns extracted from the assessment."),
    caseManagement: caseManagementText,
    cmContactedWhen: firstFilled(
      compactLines(...(extractionDocument?.caregiverSummary?.reportingInstructions || [])),
      caseManagementText,
      "Contact the case manager when care needs, equipment, services, or the negotiated care plan require review.",
    ),
    dailyObservationSummary: conciseDailyBehaviorEvidenceText(dailyNotes, 4),
    residentPreferences: firstFilled(snapshot.residentPreferences, snapshot.preferences, bio.preferences, preliminaryPayload.residentPreferences),
    residentRecommendations: firstFilled(snapshot.residentGoals, snapshot.goals, preliminaryPayload.residentGoals),
    careApproach: firstFilled(snapshot.careApproach, snapshot.preliminaryPlan?.careApproach),
    otherIssues: compactLines(notesSource, providerText),
    reviewSchedule: "",
    residentParticipation: "The resident will participate in their NCP development/reviews to the extent they are able.",
    reviewRevisionSchedule: "This NCP will be reviewed/revised after a significant change in physical or mental condition, when the plan no longer addresses needs and preferences, at the request of the resident or resident representative, or at least every twelve months.",
    abbreviations: "ADL, DPOA, MD, W/c, AFH, PCP, CG, Hx, PRN, MAR, RN Delegator.",
    reviewDates: firstFilled(assessment.negotiatedCarePlanStartDate, assessment.ncpStartDate),
    involvedInDevelopment: compactSentence(
      extractedResidentName ? "Resident" : "",
      contactRows.length ? "Resident representative / care team contacts from assessment" : "",
      caseManagementText ? "Case manager" : "",
    ),
    approvingPerson: firstFilled(contactRows[0]?.name, processResidentName(assessment)),
    providerSignature: firstFilled(
      extractedValueFor(extractionDocument, [/provider\s+facility\s+name|facility\s+name/i], ["provider_information", "client_profile"]),
      assessment.providerName,
      assessment.careProviderName,
    ),
    residentSignature: firstFilled(
      extractedValueFor(extractionDocument, [/client\s+name|resident\s+name/i], ["client_profile"]),
      processResidentName(assessment),
    ),
    residentRepresentativeSignature: firstFilled(contactRows.find((contact) => /power|guardian|emergency|daughter|son|representative/i.test(contact.relationship))?.name),
    residentVerbalAgreement: "Confirm during NCP review.",
    sentToDshs: caseManagementText,
    signatureNote: "Confirm signatures, dates, verbal agreement, and recommended changes during negotiated care plan review.",
  };
  const contactValues = contactRows.length ? contactRows : contacts.map((contact) => ({
    name: joinFilled(contact.name, joinFilled(contact.firstName, contact.lastName)),
    relationship: contact.relationship || contact.type || contact.role,
    homeBusinessPhone: contact.phone || contact.homePhone || contact.officePhone,
    cellFax: contact.mobilePhone || contact.preferredPhone,
    addressEmail: compactSentence(contact.email, contact.address || contact.formattedAddress),
  }));
  contactValues.slice(0, 6).forEach((contact, index) => {
    const row = index + 1;
    ncpContactColumns.forEach((column) => {
      initialValues[ncpContactFieldKey(row, column.key)] = contact[column.key] || "";
    });
  });
  return initialValues;
};

const applyNegotiatedInitialValues = (payload, initialValues = {}) => ({
  ...payload,
  pages: (payload.pages || []).map((page) => ({
    ...page,
    sections: (page.sections || []).map((section) => ({
      ...section,
      fields: (section.fields || []).map((field) => (
        field.value ? field : { ...field, value: initialValues[field.key] || "" }
      )),
    })),
  })),
});

const extractedAssessmentFields = (document) => (document?.sections || []).flatMap((section) =>
  (section.subsections || []).flatMap((subsection) =>
    (subsection.fields || []).map((field) => ({
      sectionKey: section.sectionKey,
      sectionTitle: section.title,
      subsectionTitle: subsection.title,
      label: field.label,
      value: normalizeSourceText(field.value),
    })),
  ),
);

const ncpNoisySourceTerms = [
  /assessment page \d+ notes/i,
  /page \d+ of \d+/i,
  /page\s+\d+\s+header/i,
  /section\s+\d+\s+of\s+\d+/i,
  /form identifier:/i,
  /assessment status:/i,
  /licensed capacity/i,
  /survey cycle/i,
  /assessing clinician/i,
  /resident name:/i,
  /client gender:/i,
  /responses?\s+1\./i,
  /responses?\s+\d+\./i,
];

const ncpSourceHeadingTerms = [
  /form identifier:/gi,
  /assessment status:/gi,
  /facility:/gi,
  /license:/gi,
  /resident name:/gi,
  /client gender:/gi,
  /date of birth:/gi,
  /client age:/gi,
  /preferred name:/gi,
  /admission date:/gi,
  /household:/gi,
  /support before admission:/gi,
  /resident goal:/gi,
  /reason for admission:/gi,
  /attending physician:/gi,
  /primary physician:/gi,
  /pharmacy:/gi,
  /monitoring:/gi,
  /wandering/gi,
  /skin care:/gi,
  /toileting:/gi,
  /restorative target:/gi,
  /safety narrative/gi,
  /history of falling:/gi,
  /post-fall response:/gi,
  /interventions:/gi,
  /medication allergies/gi,
  /rehabilitation focus:/gi,
  /cam interpretation/gi,
  /medications:/gi,
  /finances/gi,
  /total:/gi,
  /iadl narrative/gi,
  /emergency classification:/gi,
  /polst:/gi,
  /living will:/gi,
  /preferred notification:/gi,
  /index event:/gi,
  /mobility barrier:/gi,
  /mobility:/gi,
  /transfers:/gi,
];

const ncpSourceHeadingCount = (value = "") =>
  ncpSourceHeadingTerms.reduce((count, pattern) => count + (String(value).match(pattern)?.length || 0), 0);

const isNoisyNcpExtractedField = (field = {}) => {
  const label = String(field.label || "");
  const value = String(field.value || "");
  if (/^assessment page \d+ notes/i.test(label)) return true;
  if (/^page\s+\d+\s+(notes|header|footer)/i.test(label)) return true;
  if (ncpNoisySourceTerms.some((pattern) => pattern.test(value))) return true;
  return value.length > 260 && ncpSourceHeadingCount(value) >= 3;
};

const isMultiTopicNcpChunk = (value = "") => {
  const text = normalizeSourceText(value);
  const labelLikeCount = (text.match(/\b[A-Z][A-Za-z /().-]{2,45}:\s/g) || []).length;
  return text.length > 220 && (ncpSourceHeadingCount(text) >= 2 || labelLikeCount >= 3);
};

const ncpAdministrativeRecommendationTerms = [
  /^form identifier:/i,
  /^assessment status:/i,
  /^facility:/i,
  /^license:/i,
  /^date of birth:/i,
  /^client age:/i,
  /^resident name:/i,
  /^client gender:/i,
];

const isUsableNcpDisplayText = (value = "") => {
  const text = normalizeSourceText(value);
  if (!text) return false;
  if (isMultiTopicNcpChunk(text)) return false;
  if (ncpAdministrativeRecommendationTerms.some((pattern) => pattern.test(text))) return false;
  return !ncpNoisySourceTerms.some((pattern) => pattern.test(text));
};

const extractionFieldMatches = (field = {}, labelPatterns = [], sectionKeys = []) => {
  if (sectionKeys.length && !sectionKeys.includes(field.sectionKey)) return false;
  const label = String(field.label || "");
  return labelPatterns.some((pattern) => pattern.test(label));
};

const extractedValueFor = (document, labelPatterns = [], sectionKeys = []) =>
  extractedAssessmentFields(document)
    .find((field) => (
      field.value
      && extractionFieldMatches(field, labelPatterns, sectionKeys)
      && !isNoisyNcpExtractedField(field)
      && !isMultiTopicNcpChunk(`${field.label}: ${field.value}`)
    ))
    ?.value || "";

const extractedValuesFor = (document, labelPatterns = [], sectionKeys = [], limit = 12) => {
  const values = extractedAssessmentFields(document)
    .filter((field) => field.value && extractionFieldMatches(field, labelPatterns, sectionKeys))
    .filter((field) => !isNoisyNcpExtractedField(field))
    .map((field) => `${field.label}: ${field.value}`)
    .filter((value) => !isMultiTopicNcpChunk(value));
  return [...new Set(values)].slice(0, limit);
};

const extractedTextForSections = (document, sectionKeys, maxLength = 1200) => {
  const lines = extractedAssessmentFields(document)
    .filter((field) => sectionKeys.includes(field.sectionKey) && field.value && !isNoisyNcpExtractedField(field))
    .map((field) => `${field.label}: ${field.value}`);
  const deduplicated = [...new Set(lines)].filter((value) => !isMultiTopicNcpChunk(value));
  const value = deduplicated.join("\n");
  return value.length > maxLength ? `${value.slice(0, maxLength - 1).trimEnd()}…` : value;
};

const safeRegExp = (pattern) => {
  try {
    return new RegExp(pattern, "i");
  } catch {
    return null;
  }
};

const ncpFieldSourceGraphFromTemplate = (template) => {
  if (!template?.fieldSourceGraph || typeof template.fieldSourceGraph !== "object") return null;
  return Object.entries(template.fieldSourceGraph).reduce((graph, [key, source]) => {
    graph[key] = {
      page: source.page,
      sections: Array.isArray(source.sections) ? source.sections : [],
      labels: (Array.isArray(source.labelPatterns) ? source.labelPatterns : [])
        .map(safeRegExp)
        .filter(Boolean),
      limit: source.limit,
      includeDaily: Boolean(source.includeDaily),
      valueLength: source.valueLength,
      maxLength: source.maxLength,
    };
    return graph;
  }, {});
};

const relatedNcpBaseKeyFromTemplate = (key = "") => key;

const ncpCaregiverInstructionKeySet = (template = null) =>
  new Set(Array.isArray(template?.caregiverInstructionKeys) ? template.caregiverInstructionKeys : []);

const trimNcpValue = (value = "", maxLength = 520) => {
  const text = normalizeSourceText(value);
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
};

const isNcpApprovalFieldKey = (key = "") =>
  /Signature$/.test(key)
  || ["approvingPerson", "residentVerbalAgreement", "signatureNote", "involvedInDevelopment"].includes(key);

const mentionsExplicitNcpApproval = (value = "") => {
  const text = normalizeSourceText(value);
  if (!text) return false;
  if (/state assessment|assessment status|training specimen|assessing clinician|clinician|extraction|extract|resident goal|capacity|guardianship|healthcare proxy|stored in chart|financial matters/i.test(text)) {
    return false;
  }
  return /\bNCP\b|negotiated care plan/i.test(text)
    && /signed|signature|approved|agreed|agreement|consent|read and agreed|verbal|development/i.test(text);
};

const mentionsExplicitNcpDispatch = (value = "") => {
  const text = normalizeSourceText(value);
  if (!text) return false;
  if (/state assessment|assessment status|training specimen|assessing clinician|extraction|extract/i.test(text)) {
    return false;
  }
  return /\bNCP\b/i.test(text)
    && /sent|submitted|provided|delivered/i.test(text)
    && /DSHS|case manager|\bCM\b/i.test(text);
};

const mentionsExplicitNcpReview = (value = "") => {
  const text = normalizeSourceText(value);
  if (!text) return false;
  if (/assessment status|training specimen|assessing clinician|clinician|extraction|extract/i.test(text)) {
    return false;
  }
  return /\bNCP\b|negotiated care plan/i.test(text)
    && /review|revision|revised|updated|annual|twelve months|significant change/i.test(text);
};

const hasExplicitPsychMedicationEvidence = (value = "") => {
  const text = normalizeSourceText(value);
  if (!text || /\b(?:confirm|verify|if\s+checked|see\s+(?:current\s+)?mar|see\s+behavior\s+section)\b/i.test(text)) return false;
  return /\b(?:psychopharmacologic|psychotropic|antipsychotic|antidepressant|anxiolytic|mood\s+stabilizer|behavior(?:al)?\s+med(?:ication)?|sertraline|zoloft|citalopram|celexa|escitalopram|lexapro|fluoxetine|prozac|paroxetine|paxil|duloxetine|cymbalta|venlafaxine|effexor|bupropion|wellbutrin|mirtazapine|remeron|trazodone|buspirone|buspar|lorazepam|ativan|alprazolam|xanax|diazepam|valium|clonazepam|klonopin|quetiapine|seroquel|risperidone|risperdal|olanzapine|zyprexa|aripiprazole|abilify|haloperidol|haldol|divalproex|depakote|valproate|lithium|lamotrigine|lamictal)\b/i.test(text);
};

const stripNcpSourceLabels = (value = "") => normalizeSourceText(value)
  .replace(/\b(?:Medication allergies|Secondary diagnosis|Primary diagnosis|Reason for Admission|Monitoring|POLST|Living will|Communication|Resident Goal|Post-fall response|Pharmacy|Attending physician)\s*[:-]\s*/gi, "")
  .replace(/^\s*(?:Medication allergies|Secondary diagnosis|Primary diagnosis|Reason for Admission|Monitoring|POLST|Living will|Communication|Resident Goal|Post-fall response)\s+/i, "")
  .replace(/\b\d{1,2}\s+[–-]\s+(?=[a-zA-Z])/g, "")
  .replace(/\s+/g, " ")
  .trim();

const isLowValueNcpCoding = (value = "") =>
  /^(more than one active diagnosis|no active diagnosis|none documented|not documented|unknown|n\/?a)$/i.test(normalizeSourceText(value));

const normalizeNcpGeneratedText = (value = "") => normalizeSourceText(value)
  .replace(/\bbruhes\b/gi, "brushes")
  .replace(/\bbruh\b/gi, "brush")
  .replace(/\baudio\s*book(s?)\b/gi, "audiobook$1")
  .replace(/\bMethdist\b/gi, "Methodist")
  .replace(/\s+/g, " ")
  .trim();

const cleanNcpCaregiverInstruction = (value = "") => {
  const text = normalizeNcpGeneratedText(stripNcpSourceLabels(value))
    .replace(/^(?:caregiver\s+will|cg\s+will|caregiver\s+is\s+to|cg\s+is\s+to)\s+/i, "")
    .replace(/^will\s+/i, "")
    .trim();
  return isLowValueNcpCoding(text) ? "" : text;
};

const cleanNcpContactRelationship = (value = "") => {
  const firstPart = normalizeSourceText(value).replace(/\s*\|.*$/, "").trim();
  const match = firstPart.match(/\b(power\s+of\s+attorney|dpoa|daughter|son|spouse|guardian|case\s+manager|social\s+worker|pharmacy|health\s+professional|primary\s+care\s+doctor|emergency\s+contact|niece|nephew|representative)\b/i);
  if (!match) return firstPart;
  if (/^dpoa$/i.test(match[1])) return "Power of Attorney";
  return match[1].replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const cleanNcpFieldByKey = (key = "", value = "") => {
  const text = normalizeNcpGeneratedText(stripNcpSourceLabels(value));
  if (isLowValueNcpCoding(text)) return "";
  if (key === "providerName") {
    const providerOnly = text
      .replace(/\s*,\s*\d{2,6}\s+[^,]+,\s*[^,]+,\s*(?:[A-Z]{2}|[A-Za-z]+)\s+\d{5}(?:-\d{4})?\b.*$/i, "")
      .trim();
    return providerOnly || text;
  }
  if (["allergies", "medicationAllergies", "allergySubstanceReaction"].includes(key)) {
    const allergyText = text.replace(/^allergies?\s*[:-]?\s*/i, "").trim();
    return isLowValueNcpCoding(allergyText) ? "" : allergyText;
  }
  if (["medicalDiagnosis", "specialtyNeeds"].includes(key)) {
    const diagnosisText = text.replace(/\bhealth\s+conditions?\s*[:-]\s*/i, "").trim();
    return isLowValueNcpCoding(diagnosisText) ? "" : diagnosisText;
  }
  if (key === "legalDocuments") {
    const legalText = text
      .replace(/\s*stored in chart\b/gi, "")
      .replace(/\bactivated for financial matters only;?\s*/gi, "")
      .trim();
    const parts = [];
    if (/\b(?:durable\s+power\s+of\s+attorney|dpoa|power\s+of\s+attorney)\b/i.test(legalText)) parts.push("DPOA");
    if (/\bhealthcare\s+proxy\b/i.test(legalText)) parts.push("Healthcare proxy");
    if (/\bliving\s+will\b/i.test(legalText)) parts.push("Living will");
    if (/\bguardianship\b/i.test(legalText) && /\bno\b/i.test(legalText)) parts.push("No guardian");
    if (parts.length) return parts.join("; ");
    return isLowValueNcpCoding(legalText) ? "" : legalText;
  }
  if (key === "medsDeliveredBy") {
    return text.replace(/^pharmacy\s*[:-]?\s*/i, "").trim();
  }
  if (/Assistance$|Caregiver$/.test(key)
      || ["evacuationCaregiverInstructions", "medicationOutsideAfh", "medicationRefusalPlan", "treatmentRefusalPlan"].includes(key)) {
    return cleanNcpCaregiverInstruction(text);
  }
  return text;
};

const behaviorNcpDailyFieldKeys = new Set([
  "behaviorSupportPlan",
  "behavioralSymptoms",
  "behaviors",
  "behaviorAssistance",
  "behavioralHealthSupport",
  "psychInterventions",
  "psychInterventionAssistance",
  "typicalDayNarrative",
]);

const cleanRawDailyObservationForBehaviorField = (value = "") => {
  const text = normalizeSourceText(value);
  if (!/Selected words\/categories exactly as captured|Daily observations:/i.test(text)) return "";
  const selectedText = text
    .split(/Selected words\/categories exactly as captured:/i)
    .slice(1)
    .join("; ");
  const labels = selectedText
    .split(/;|\n|·/g)
    .map((part) => normalizeSourceText(part)
      .replace(/^Behavior\s*>\s*/i, "")
      .replace(/\s*-\s*frequency:.*$/i, "")
      .replace(/\s*-\s*magnitude:.*$/i, "")
      .replace(/\s*-\s*trend:.*$/i, ""))
    .filter(Boolean)
    .filter((label, index, labelsList) => labelsList.findIndex((candidate) => candidate.toLowerCase() === label.toLowerCase()) === index)
    .slice(0, 8);
  return labels.length ? `Selected Behavior evidence: ${labels.join("; ")}.` : "";
};

const stripRawDailyObservationText = (value = "") => normalizeSourceText(value)
  .replace(/\s*Daily observations:\s*\d{4}-\d{2}-\d{2}\s*·\s*Behavior:.*?(?=(?:\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3}\s*:)|$)/gi, "")
  .replace(/\s*\d{4}-\d{2}-\d{2}\s*·\s*Behavior:\s*Selected words\/categories exactly as captured:.*?(?=(?:\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3}\s*:)|$)/gi, "")
  .replace(/\s*Selected words\/categories exactly as captured:.*$/i, "")
  .trim();

const cleanGeneratedNcpFieldValue = (key = "", value = "") => {
  const text = normalizeNcpGeneratedText(value);
  if (!text) return "";
  if (key === "psychInterventions" && /Interventions documented:/i.test(text)) {
    return trimNcpValue(text.replace(/\s*Interventions documented:.*$/i, "").trim() || "Meaningful Day: See Activity Narrative below.", 180);
  }
  if (key === "psychInterventionAssistance" && /Interventions documented:\s*(?:yes|yes he is|ok|okay|n\/?a)\b/i.test(text)) {
    return "Caregiver will support the activity plan, offer cueing and reassurance, and document resident response.";
  }
  if (key === "typicalDayNarrative" && /\bBehavior\s*-|frequency:|magnitude:|trend:/i.test(text)) {
    const cleanedNarrative = text
      .replace(/\s*Daily routine narrative:\s*\d{4}-\d{2}-\d{2}: Behavior\s*-.*$/i, "")
      .replace(/\s*\d{4}-\d{2}-\d{2}: Behavior\s*-.*$/i, "")
      .replace(/\s*Behavior\s*>?\s*[^.;]*(?:frequency:|magnitude:|trend:).*$/i, "")
      .trim();
    return trimNcpValue(cleanedNarrative, 260);
  }
  if (behaviorNcpDailyFieldKeys.has(key)) {
    const cleanedDailyEvidence = cleanRawDailyObservationForBehaviorField(text);
    if (cleanedDailyEvidence) return trimNcpValue(cleanedDailyEvidence, 320);
  }
  if (["specializedBehaviorProgramStrengths", "specializedBehaviorProgramAssistance"].includes(key)
      && !/\b(?:specialized\s+behavior|expanded\s+community|behavior\s+services|mental\s+health\s+provider\/program|dshs\s+specialized|meaningful\s+day)\b/i.test(text)) {
    return "";
  }
  if (/^contact\d+AddressEmail$/.test(key)) {
    const email = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (email) return email[0];
    const address = text.match(/\d{2,6}\s+[^|,]+(?:\s+[^|,]+){0,6},?\s+[A-Za-z .'-]+,?\s+[A-Z]{2}\s+\d{5}(?:-\d{4})?/);
    if (address) return address[0].trim();
    if (/\||activated|healthcare proxy|stored in chart|lives\s+\d|first call|second call|secure fax|guardian|power\s+of\s+attorney|\bpoa\b|dpoa|financial matters|primary emergency contact/i.test(text)) {
      return "";
    }
  }
  if (/^contact\d+Relationship$/.test(key)) {
    return cleanNcpContactRelationship(text);
  }
  if (isNcpApprovalFieldKey(key)) {
    return mentionsExplicitNcpApproval(text) ? text : "";
  }
  if (key === "sentToDshs") {
    return mentionsExplicitNcpDispatch(text) ? text : "";
  }
  if (key === "reviewDates") {
    return mentionsExplicitNcpReview(text) ? cleanNcpFieldByKey(key, text) : "";
  }
  if (["reviewSchedule", "residentParticipation", "reviewRevisionSchedule"].includes(key)) {
    return mentionsExplicitNcpReview(text) ? cleanNcpFieldByKey(key, text) : "";
  }
  return cleanNcpFieldByKey(key, stripRawDailyObservationText(text));
};

const ncpGraphValueForKey = (key, extractionDocument, dailyAnalysis = "", template = null, dailyFieldSummaries = {}) => {
  const graph = ncpFieldSourceGraphFromTemplate(template) || {};
  const graphKey = relatedNcpBaseKeyFromTemplate(key, template);
  const node = graph[graphKey];
  if (!node) return "";
  const values = extractedValuesFor(extractionDocument, node.labels, node.sections, node.limit || 6)
    .map((value) => trimNcpValue(value, node.valueLength || 260));
  const extractionText = values.join("\n");
  const dailySummary = normalizeSourceText(dailyFieldSummaries[key]);
  if (dailySummary && behaviorNcpDailyFieldKeys.has(key)) {
    return trimNcpValue(dailySummary, 320);
  }
  const observationSource = dailySummary;
  const observationText = node.includeDaily && observationSource
    ? trimNcpValue(observationSource, 320)
    : "";
  const value = compactLines(extractionText, observationText);
  if (!value) return "";
  if (ncpCaregiverInstructionKeySet(template).has(key)) {
    return trimNcpValue(`support this need as documented: ${value}`, node.maxLength || 520);
  }
  return trimNcpValue(value, node.maxLength || 520);
};

const ncpValuesFromSourceGraph = (extractionDocument, dailyAnalysis = "", template = null, dailyFieldSummaries = {}) => {
  const fieldKeys = new Set(negotiatedTemplateDefinition(template).flatMap((page) =>
    page.sections.flatMap((section) => section.fields.map((field) => field.key)),
  ));
  return [...fieldKeys].reduce((values, key) => {
    const value = ncpGraphValueForKey(key, extractionDocument, dailyAnalysis, template, dailyFieldSummaries);
    if (value) values[key] = value;
    return values;
  }, {});
};

const applyNcpFieldValues = (payload, fieldValues = {}, fieldChecks = {}) => {
  const values = fieldValues && typeof fieldValues === "object" ? fieldValues : {};
  const checksByKey = fieldChecks && typeof fieldChecks === "object" ? fieldChecks : {};
  return {
    ...payload,
    pages: (payload.pages || []).map((page) => ({
      ...page,
      sections: (page.sections || []).map((section) => ({
        ...section,
        fields: (section.fields || []).map((field) => {
          const hasValue = Object.prototype.hasOwnProperty.call(values, field.key);
          const generatedChecks = checksByKey[field.key];
          const nextChecks = Array.isArray(generatedChecks) && Array.isArray(field.checks)
            ? field.checks.map((check, index) => ({ ...check, checked: Boolean(generatedChecks[index]) }))
            : field.checks;
          if (!hasValue) return { ...field, checks: nextChecks };
          const value = cleanGeneratedNcpFieldValue(field.key, values[field.key]);
          if (!value) return { ...field, checks: nextChecks };
          return { ...field, value, checks: nextChecks };
        }),
      })),
    })),
  };
};

const ncpPreferInitialValueKeys = new Set([
  "providerName",
  "dateNcpStarted",
  "movedInDate",
  "dateCompleted",
  "dateDischarged",
  "residentName",
  "pronouns",
  "dateOfBirthAge",
  "primaryLanguage",
  "speaksEnglish",
  "interpreterNeeded",
]);

const ncpContactValueKeyPattern = /^contact\d+(Name|Relationship|HomeBusinessPhone|CellFax|AddressEmail)$/;

const ncpInitialValueKeys = new Set([
  ...ncpPreferInitialValueKeys,
]);

const initialValuesForNcpTemplate = (initialValues = {}) =>
  Object.entries(initialValues).reduce((values, [key, value]) => {
    if (ncpInitialValueKeys.has(key) || ncpContactValueKeyPattern.test(key)) {
      values[key] = value;
    }
    return values;
  }, {});

const weeklyReportTextValue = (report = {}) => {
  const source = report.report && typeof report.report === "object" ? report.report : report;
  const paragraph = firstFilled(source.reportParagraph, source.paragraph, source.summary, report.summary);
  const sectionLines = Object.values(source.sections || {})
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .map(normalizeSourceText)
    .filter(Boolean);
  return compactLines(paragraph, ...sectionLines.slice(0, 12));
};

const weeklyReportsAnalysisText = (weeklyReports = [], maxLength = 1200) => {
  const value = (Array.isArray(weeklyReports) ? weeklyReports : [])
    .map((report) => {
      const start = report.weekStart || report.startDate || report.from || "";
      const end = report.weekEnd || report.endDate || report.to || "";
      const range = [start, end].filter(Boolean).join(" to ");
      const text = weeklyReportTextValue(report);
      return text ? compactLines(range ? `Weekly report ${range}` : "Weekly report", text) : "";
    })
    .filter(Boolean)
    .join("\n\n");
  return value.length > maxLength ? `${value.slice(0, maxLength - 1).trimEnd()}…` : value;
};

const normalizeDailyNoteSelectionItems = (note = {}) => {
  const rawSelections = [
    note.quickSelections,
    note.selections,
    note.selectedSelections,
    note.selectedCategories,
    note.categorySelections,
    note.behaviorSelections,
    note.qualifyingSelections,
  ].flatMap((value) => Array.isArray(value) ? value : []);
  const fallbackCategory = note.category || note.type || "";
  const seen = new Set();
  return rawSelections
    .map((item) => {
      if (typeof item === "string") {
        return { label: normalizeSourceText(item), category: fallbackCategory, frequency: "", magnitude: "", trend: "" };
      }
      return {
        label: normalizeSourceText(item?.label || item?.value || item?.name),
        category: normalizeSourceText(item?.category || item?.group || item?.section || fallbackCategory),
        frequency: normalizeSourceText(item?.frequency),
        magnitude: normalizeSourceText(item?.magnitude),
        trend: normalizeSourceText(item?.trend),
      };
    })
    .filter((item) => item.label)
    .filter((item) => {
      const key = `${item.category}::${item.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const dailyNoteSelectionEvidenceText = (note = {}) => {
  const selections = normalizeDailyNoteSelectionItems(note);
  if (!selections.length) return "";
  return selections
    .map((selection) => [
      selection.category ? `${selection.category} > ${selection.label}` : selection.label,
      selection.frequency ? `frequency: ${selection.frequency}` : "",
      selection.magnitude ? `magnitude: ${selection.magnitude}` : "",
      selection.trend ? `trend: ${selection.trend}` : "",
    ].filter(Boolean).join(" - "))
    .join("; ");
};

const dailyNoteBehaviorEvidenceItems = (dailyNotes = []) => {
  const items = [];
  (Array.isArray(dailyNotes) ? dailyNotes : []).forEach((note) => {
    const noteCategory = normalizeSourceText(note.category || note.type || "");
    const date = note.noteDate || note.date || "";
    normalizeDailyNoteSelectionItems(note)
      .filter((selection) => /behavior/i.test(selection.category || noteCategory))
      .forEach((selection) => {
        items.push({
          label: selection.label,
          frequency: selection.frequency,
          magnitude: selection.magnitude,
          trend: selection.trend,
          date,
        });
      });
  });
  const byLabel = new Map();
  items.forEach((item) => {
    const key = item.label.toLowerCase();
    const existing = byLabel.get(key);
    byLabel.set(key, {
      label: item.label,
      frequency: existing?.frequency || item.frequency,
      magnitude: existing?.magnitude || item.magnitude,
      trend: existing?.trend || item.trend,
      date: existing?.date || item.date,
      count: (existing?.count || 0) + 1,
    });
  });
  return [...byLabel.values()];
};

const conciseDailyBehaviorEvidenceText = (dailyNotes = [], maxItems = 4) => {
  const items = dailyNoteBehaviorEvidenceItems(dailyNotes);
  if (!items.length) return "";
  return `Selected Behavior evidence: ${items.slice(0, maxItems).map((item) => {
    const magnitude = item.magnitude && !/^none$/i.test(item.magnitude) ? item.magnitude : "";
    const details = [item.frequency, magnitude].filter(Boolean).join(", ");
    return details ? `${item.label} (${details})` : item.label;
  }).join("; ")}.`;
};

const conciseDailyBehaviorLabelsText = (dailyNotes = [], maxItems = 8) => {
  const labels = dailyNoteBehaviorEvidenceItems(dailyNotes).slice(0, maxItems).map((item) => item.label);
  return labels.length ? `Behavior selections exactly as captured: ${labels.join("; ")}.` : "";
};

const conciseDailyInterventionText = (dailyNotes = []) => {
  const interventions = (Array.isArray(dailyNotes) ? dailyNotes : [])
    .map((note) => normalizeSourceText(note.interventionAction || note.intervention || note.actionTaken))
    .filter(Boolean);
  if (interventions.length) {
    return trimNcpValue(`Interventions documented: ${[...new Set(interventions)].slice(0, 3).join("; ")}.`, 260);
  }
  return "Caregiver will document triggers, frequency, intervention used, resident response, staffing support, and any escalation.";
};

const conciseDailyNarrativeText = (dailyNotes = []) => {
  const entries = (Array.isArray(dailyNotes) ? dailyNotes : [])
    .filter((note) => !/behavior/i.test(normalizeSourceText(note.category || note.type || "")))
    .map((note) => {
      const date = note.noteDate || note.date || "";
      const category = normalizeSourceText(note.category || note.type || "Daily note");
      const body = normalizeSourceText(note.summary || note.notes || note.note || note.evaluationNotes)
        .replace(/Selected words\/categories exactly as captured:\s*/gi, "")
        .replace(/\bBehavior\s*>\s*/gi, "");
      if (!body) return "";
      return `${date ? `${date}: ` : ""}${category} - ${body}`;
    })
    .filter(Boolean);
  return entries.length ? trimNcpValue(`Daily routine narrative: ${entries.slice(0, 2).join(" ")}`, 320) : "";
};

const ncpDailyFieldSummariesFromNotes = (dailyNotes = []) => {
  const behaviorEvidence = conciseDailyBehaviorEvidenceText(dailyNotes);
  const behaviorLabels = conciseDailyBehaviorLabelsText(dailyNotes);
  const interventionText = conciseDailyInterventionText(dailyNotes);
  const narrativeText = conciseDailyNarrativeText(dailyNotes);
  return {
    behaviorSupportPlan: compactLines(behaviorEvidence, interventionText),
    behavioralSymptoms: behaviorLabels || behaviorEvidence,
    behaviors: behaviorEvidence || behaviorLabels,
    behaviorAssistance: compactLines(
      behaviorEvidence ? "Caregiver will use redirection, cueing, reassurance, and de-escalation when selected behaviors occur." : "",
      interventionText,
    ),
    behavioralHealthSupport: behaviorLabels || behaviorEvidence,
    psychInterventions: "Meaningful Day: See Activity Narrative below.",
    psychInterventionAssistance: "Caregiver will support the activity plan, offer cueing and reassurance, and document resident response.",
    typicalDayNarrative: narrativeText,
  };
};

const ncpDailyNoteGenerationRules = [
  "Use daily-note category names and selected quick-selection labels exactly as captured.",
  "Do not rename, soften, sugar-coat, or paraphrase selected behavior/category wording.",
  "If explanation is needed, add it after the exact selected words instead of replacing those words.",
  "Prefer dated daily-note evidence when completing NCP behavior, safety, psychosocial, supervision, ADL, IADL, and activity fields.",
];

const dailyNotesAnalysisText = (dailyNotes = [], maxLength = 2400) => {
  const lines = (Array.isArray(dailyNotes) ? dailyNotes : [])
    .map((note) => {
      const date = note.noteDate || note.date || "";
      const category = note.category || note.type || "Observation";
      const selectionEvidence = dailyNoteSelectionEvidenceText(note);
      const body = normalizeSourceText(note.summary || note.notes || note.note || note.evaluationNotes);
      const intervention = normalizeSourceText(note.interventionAction || note.intervention || note.actionTaken);
      const bodyRepeatsSelectionEvidence = selectionEvidence && body && selectionEvidence
        .split("; ")
        .filter(Boolean)
        .every((item) => body.includes(item));
      const details = compactSentence(
        selectionEvidence ? `Selected words/categories exactly as captured: ${selectionEvidence}` : "",
        bodyRepeatsSelectionEvidence ? "" : body,
        intervention ? `Intervention/action: ${intervention}` : "",
      );
      return details ? `${date ? `${date} · ` : ""}${category}: ${details}` : "";
    })
    .filter(Boolean);
  const value = lines.join("\n");
  return value.length > maxLength ? `${value.slice(0, maxLength - 1).trimEnd()}…` : value;
};

const sourceObservationText = (dailyNotes = [], weeklyReports = [], maxLength = 2400) =>
  compactLines(
    dailyNotesAnalysisText(dailyNotes, Math.floor(maxLength / 2)),
    weeklyReportsAnalysisText(weeklyReports, Math.floor(maxLength / 2)),
  );

const ncpContactRowsFromExtraction = (document) => {
  const contactLabels = [
    [/primary\s+emergency/i, "Emergency Contact"],
    [/secondary\s+contact/i, "Emergency Contact"],
    [/durable\s+power\s+of\s+attorney|power\s+of\s+attorney|poa/i, "Power of Attorney"],
    [/guardian/i, "Legal Guardian"],
    [/case\s+manager|social\s+worker/i, "Case Manager"],
    [/attending\s+physician|primary\s+care|doctor|physician|neurology|consultant|specialist/i, "Health Professional"],
    [/pharmacy/i, "Pharmacy"],
  ];
  const phonePattern = /(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/;
  return extractedAssessmentFields(document)
    .filter((field) => ["client_profile", "provider_information", "health_services", "medications"].includes(field.sectionKey))
    .map((field) => {
      const match = contactLabels.find(([pattern]) => pattern.test(String(field.label || "")));
      if (!match || !field.value) return null;
      if (/guardian/i.test(match[1]) && /\bno\b|no court-appointed|not appointed|none/i.test(field.value)) return null;
      const phone = field.value.match(phonePattern)?.[0] || "";
      const [namePart] = field.value.split(/\s*\|\s*/);
      const name = normalizeSourceText(namePart
        .split(/\s*,\s*(activated|healthcare proxy|financial|lives|first call|second call|stored in chart|designated)/i)[0]
        .replace(phonePattern, "")
        .replace(/\b(daughter|son|niece|nephew|sibling|spouse|brother|sister|guardian)\b$/i, "")
        .replace(/[,\s]+$/g, ""));
      return {
        name: name || field.value,
        relationship: match[1],
        homeBusinessPhone: phone,
        cellFax: phone,
        addressEmail: field.value,
      };
    })
    .filter(Boolean)
    .filter((contact, index, contacts) =>
      contacts.findIndex((candidate) => `${candidate.name}-${candidate.cellFax}` === `${contact.name}-${contact.cellFax}`) === index)
    .slice(0, 6);
};

const sourceValueForNcpField = (field, extractionDocument, dailyAnalysis, template = null, dailyFieldSummaries = {}) => {
  return ncpGraphValueForKey(field.key, extractionDocument, dailyAnalysis, template, dailyFieldSummaries);
};

const buildNegotiatedPlanFromSources = ({
  assessmentId,
  currentPayload,
  dailyNotes,
  extractionDocument,
  initialValues = {},
  recordId,
  residentName,
  preserveExistingValues = true,
  startDate,
  template = null,
  weeklyReports = [],
}) => {
  const templateInitialValues = initialValuesForNcpTemplate(initialValues);
  const sourcePayload = preserveExistingValues && currentPayload?.pages?.length
    ? currentPayload
    : createNegotiatedPlanPayload({ assessmentId, recordId, template });
  const base = applyNegotiatedInitialValues(ensureNegotiatedPlanPayloadFields(sourcePayload, template), templateInitialValues);
  const dailyAnalysis = sourceObservationText(dailyNotes, weeklyReports);
  const dailyFieldSummaries = ncpDailyFieldSummariesFromNotes(dailyNotes);
  const graphValues = ncpValuesFromSourceGraph(extractionDocument, dailyAnalysis, template, dailyFieldSummaries);
  const caregiverSummary = [
    ...(extractionDocument?.caregiverSummary?.immediatePriorities || []),
    ...(extractionDocument?.caregiverSummary?.risksAndWarnings || []),
    ...(extractionDocument?.caregiverSummary?.requiredAssistance || []),
    ...(extractionDocument?.caregiverSummary?.reportingInstructions || []),
  ].map(normalizeSourceText).filter(isUsableNcpDisplayText).join("\n");

  return {
    ...base,
    assessmentId,
    recordId,
    status: "draft",
    generatedAt: new Date().toISOString(),
    generatedFrom: [
      extractionDocument ? "Saved assessment extraction" : null,
      Object.values(initialValues).some(Boolean) ? "Assessment and resident information" : null,
      dailyAnalysis ? "Daily-note analysis" : null,
    ].filter(Boolean),
    sourceData: {
      assessmentExtraction: extractionDocument || null,
      dailyNotes: Array.isArray(dailyNotes) ? dailyNotes : [],
      weeklyReports: Array.isArray(weeklyReports) ? weeklyReports : [],
      dailyNotesAnalysis: dailyAnalysis,
      dailyNoteGenerationRules: ncpDailyNoteGenerationRules,
      caregiverSummary,
    },
    pages: base.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          if (field.key === "residentName") return { ...field, value: residentName || initialValues.residentName || "" };
          if (field.key === "dateNcpStarted") return { ...field, value: startDate || initialValues.dateNcpStarted || "" };
          const sourceValue = graphValues[field.key] || dailyFieldSummaries[field.key] || sourceValueForNcpField(field, extractionDocument, dailyAnalysis, template, dailyFieldSummaries);
          if (ncpPreferInitialValueKeys.has(field.key)) {
            return { ...field, value: templateInitialValues[field.key] || sourceValue || (preserveExistingValues ? field.value : "") || "" };
          }
          if (ncpContactValueKeyPattern.test(field.key)) {
            return { ...field, value: templateInitialValues[field.key] || (preserveExistingValues ? field.value : "") || "" };
          }
          return { ...field, value: sourceValue || (preserveExistingValues ? field.value : "") || "" };
        }),
      })),
    })),
  };
};

export default function AssessmentRecordDetailPage({
  embedded = false,
  assessmentId: embeddedAssessmentId = "",
  recordId: embeddedRecordId = "",
  assessmentOverride = null,
  extractedAssessmentDocument = null,
  dailyNotesAnalysis = [],
  weeklyReports = [],
}) {
  const { user } = useAuth();
  const routeParams = useParams();
  const assessmentId = embeddedAssessmentId || routeParams.assessmentId;
  const recordId = embeddedRecordId || routeParams.recordId;
  const [searchParams] = useSearchParams();
  const [assessment, setAssessment] = useState(null);
  const [record, setRecord] = useState(null);
  const [recordName, setRecordName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customQuestions, setCustomQuestions] = useState([]);
  const [customQuestion, setCustomQuestion] = useState("");
  const [customDocuments, setCustomDocuments] = useState([]);
  const [customDocument, setCustomDocument] = useState("");
  const [careCircleMembers, setCareCircleMembers] = useState(defaultCareCircleMembers);
  const [careProviderStaffMembers, setCareProviderStaffMembers] = useState(defaultCareProviderStaff);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [meetNotes, setMeetNotes] = useState("");
  const [recordNotes, setRecordNotes] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preliminaryPreviewOpen, setPreliminaryPreviewOpen] = useState(false);
  const [negotiatedPreviewOpen, setNegotiatedPreviewOpen] = useState(false);
  const negotiatedPreviewRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const signatureDrawingRef = useRef(false);
  const signatureLastPointRef = useRef(null);
  const [dailyNoteDrawerOpen, setDailyNoteDrawerOpen] = useState(false);
  const [dailyNoteEditingNote, setDailyNoteEditingNote] = useState(null);
  const [dailyNoteDate, setDailyNoteDate] = useState(dateInputValue(new Date()));
  const [dailyNoteCategory, setDailyNoteCategory] = useState("Behavior");
  const [dailyNoteOtherCategory, setDailyNoteOtherCategory] = useState("");
  const [dailyNoteRichText, setDailyNoteRichText] = useState("");
  const [dailyNotePhotos, setDailyNotePhotos] = useState([]);
  const [dailyNotePhotoProcessing, setDailyNotePhotoProcessing] = useState(false);
  const [dailyNoteSaving, setDailyNoteSaving] = useState(false);
  const [selectedDailyNote, setSelectedDailyNote] = useState(null);
  const [probationAssignedStaff, setProbationAssignedStaff] = useState(null);
  const [probationStaffDrawer, setProbationStaffDrawer] = useState(null);
  const [preliminaryPlanDescription, setPreliminaryPlanDescription] = useState("");
  const [preliminaryGenerationLocked, setPreliminaryGenerationLocked] = useState(false);
  const [preliminaryPlanHistory, setPreliminaryPlanHistory] = useState([]);
  const [preliminaryAttachedFiles, setPreliminaryAttachedFiles] = useState([]);
  const [negotiatedPlanPayload, setNegotiatedPlanPayload] = useState(() => createNegotiatedPlanPayload({ assessmentId, recordId }));
  const [negotiatedPlanTemplate, setNegotiatedPlanTemplate] = useState(null);
  const [negotiatedPlanTemplateError, setNegotiatedPlanTemplateError] = useState("");
  const [negotiatedPlanHistory, setNegotiatedPlanHistory] = useState([]);
  const [negotiatedPlanSaving, setNegotiatedPlanSaving] = useState(false);
  const [negotiatedPlanError, setNegotiatedPlanError] = useState("");
  const [negotiatedPlanSuccess, setNegotiatedPlanSuccess] = useState("");
  const [digitalSignatureOpen, setDigitalSignatureOpen] = useState(false);
  const [digitalSignatureSaving, setDigitalSignatureSaving] = useState(false);
  const [digitalSignatureForm, setDigitalSignatureForm] = useState({
    role: "provider",
    signerName: "",
    signerTitle: "Care Provider",
    note: "",
    includeLocation: true,
    acknowledged: false,
  });
  const [digitalSignatureHasInk, setDigitalSignatureHasInk] = useState(false);
  const [probationNoteRange, setProbationNoteRange] = useState({
    start: monthStartInputValue(new Date()),
    end: monthEndInputValue(new Date()),
  });
  const [visibleProbationNoteCount, setVisibleProbationNoteCount] = useState(10);

  useEffect(() => {
    if (recordId !== "negotiated-plan") return undefined;
    let isMounted = true;
    setNegotiatedPlanTemplateError("");
    careService.negotiatedCarePlans.template()
      .then((template) => {
        if (!isMounted) return;
        const hasTemplatePages = Array.isArray(template?.pages) && template.pages.length;
        setNegotiatedPlanTemplate(hasTemplatePages ? template : null);
        if (!hasTemplatePages) {
          setNegotiatedPlanTemplateError("Backend NCP template graph did not include any pages.");
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setNegotiatedPlanTemplate(null);
        setNegotiatedPlanTemplateError(err?.response?.data?.message || err?.message || "Unable to load the backend NCP template graph.");
      });
    return () => {
      isMounted = false;
    };
  }, [recordId]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    if (embedded && assessmentOverride && recordId === "negotiated-plan") {
      setAssessment(assessmentOverride);
      setRecord(defaultNegotiatedPlanRecord());
      setRecordName("Negotiated Care Plan");
      setError("");
      careService.assessmentRecords
        .byProcessAndType(assessmentId, recordId)
        .then((recordData) => {
          if (!isMounted) return;
          const payload = recordData?.payload && typeof recordData.payload === "object" ? recordData.payload : {};
          setRecord({ ...defaultNegotiatedPlanRecord(), ...payload, payload });
          setRecordName(recordData?.title || "Negotiated Care Plan");
        })
        .catch((err) => {
          if (!isMounted) return;
          if (err?.response?.status !== 404) {
            setError(err?.response?.data?.message || err?.message || "Unable to load negotiated care plan.");
          }
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
      return () => {
        isMounted = false;
      };
    }
    const loadRecord = (process) => {
      if (recordId === "negotiated-plan") {
        return Promise.resolve(defaultNegotiatedPlanRecord());
      }
      return careService.assessmentRecords.byProcessAndType(assessmentId, recordId);
    };
    careService.assessmentProcesses
      .get(assessmentId)
      .then((process) => Promise.all([process, loadRecord(process)]))
      .then(([process, recordData]) => {
        if (!isMounted) return;
        const matchedRecord = processRecords(process).find((item) => recordSlug(item) === recordId || assessmentRecordSlug(recordLabel(item)) === recordId);
        setAssessment(process || null);
        setRecord(recordData || {});
        setRecordName(recordData?.name || recordData?.title || recordLabel(matchedRecord) || (recordId === "negotiated-plan" ? "Negotiated Care Plan" : recordId));
        setError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setAssessment(null);
        setRecord(null);
        setRecordName("");
        setError(err?.response?.data?.message || err?.message || "Unable to load assessment record.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [assessmentId, assessmentOverride, embedded, recordId]);

  useEffect(() => {
    if (!record) return;
    setMeetNotes(record.notes || "");
    setRecordNotes(record.notes || "");
    setCareCircleMembers(record.careCircle?.length ? record.careCircle : defaultCareCircleMembers);
    setCareProviderStaffMembers(record.careProviderStaff?.length ? record.careProviderStaff : defaultCareProviderStaff);
    setPreliminaryPlanDescription(record.description || record.preliminaryPlanDescription || "");
    setPreliminaryGenerationLocked(Boolean(record.generationLocked || record.preventFurtherGeneration));
    setPreliminaryPlanHistory(record.history || record.planHistory || []);
    setPreliminaryAttachedFiles(record.attachedFiles || []);
    setNegotiatedPlanHistory(record.history || record.planHistory || []);
    setProbationAssignedStaff(record.assignedStaff || null);
  }, [record]);

  useEffect(() => {
    if (recordId !== "negotiated-plan") return;
    const payload = record?.payload && typeof record.payload === "object" ? record.payload : record;
    const savedSourceData = record?.sourceData || record?.payload?.sourceData || {};
    const sourceDailyNotes = dailyNotesAnalysis.length ? dailyNotesAnalysis : (savedSourceData.dailyNotes || []);
    const sourceWeeklyReports = weeklyReports.length ? weeklyReports : (savedSourceData.weeklyReports || []);
    const sourceExtractionDocument = extractedAssessmentDocument || savedSourceData.assessmentExtraction || null;
    const initialValues = negotiatedInitialValuesFromAssessment(assessment, sourceDailyNotes, sourceExtractionDocument, sourceWeeklyReports);
    const nextPayload = buildNegotiatedPlanFromSources({
      assessmentId,
      currentPayload: payload?.pages?.length ? payload : createNegotiatedPlanPayload({ assessmentId, recordId, template: negotiatedPlanTemplate }),
      dailyNotes: sourceDailyNotes,
      extractionDocument: sourceExtractionDocument,
      initialValues,
      recordId,
      residentName: initialValues.residentName || processResidentName(assessment),
      startDate: initialValues.dateNcpStarted || assessment?.processStartDate || assessment?.startDate || "",
      template: negotiatedPlanTemplate,
      weeklyReports: sourceWeeklyReports,
    });
    setNegotiatedPlanPayload(nextPayload);
  }, [assessment, assessmentId, dailyNotesAnalysis, extractedAssessmentDocument, negotiatedPlanTemplate, record, recordId, weeklyReports]);

  useEffect(() => {
    if (loading || recordId !== "negotiated-plan") return;
    const shouldPreview = searchParams.get("view") === "preview";
    const shouldPrint = searchParams.get("print") === "true";
    if (shouldPreview || shouldPrint) {
      setNegotiatedPreviewOpen(true);
      if (shouldPrint) {
        window.setTimeout(() => window.print(), 350);
      }
    }
  }, [loading, recordId, searchParams]);

  if (loading) {
    return (
        <CareShell embedded={embedded} className="dashboard-content residents-page assessments-page">
        <Panel title="Assessment Record">Loading assessment record...</Panel>
      </CareShell>
    );
  }
  if (error) {
    return (
      <CareShell embedded={embedded} className="dashboard-content residents-page assessments-page">
        <div className="residents-actions">
          <Link className="mini-btn" to={`/assessments/${assessmentId}/new`}><i className="ti ti-arrow-left" /> Assessment</Link>
        </div>
        <p className="form-error">{error}</p>
      </CareShell>
    );
  }
  if (!assessment) return <Navigate to="/assessments" replace />;
  const assessmentProcessId = processId(assessment) || assessmentId;
  if (!recordName) return <Navigate to={`/assessments/${assessmentProcessId}/new`} replace />;

  const isMeetAndGreet = recordId === "meet-and-greet";
  const isClientOnboarding = recordId === "client-onboarding";
  const isResidentProbation = recordId === "resident-30-day-probation";
  const isAnnualResidencyReview = recordId === "annual-residency-progress";
  const isPreliminaryPlan = recordId === "preliminary-plan";
  const isNegotiatedPlan = recordId === "negotiated-plan";
  const isCareMeetingRecord = isMeetAndGreet || isClientOnboarding;
  if (isNegotiatedPlan && !negotiatedPlanTemplate) {
    return (
      <CareShell embedded={embedded} className="dashboard-content residents-page assessments-page">
        <Panel title="Negotiated Care Plan">
          {negotiatedPlanTemplateError || "Loading backend NCP template graph..."}
        </Panel>
      </CareShell>
    );
  }
  const savedNcpSourceData = record?.sourceData || record?.payload?.sourceData || {};
  const ncpExtractionDocument = extractedAssessmentDocument || savedNcpSourceData.assessmentExtraction || null;
  const ncpDailyNotes = dailyNotesAnalysis.length ? dailyNotesAnalysis : (savedNcpSourceData.dailyNotes || []);
  const ncpWeeklyReports = weeklyReports.length ? weeklyReports : (savedNcpSourceData.weeklyReports || []);
  const ncpDigitalSignatures = Array.isArray(negotiatedPlanPayload?.digitalSignatures)
    ? negotiatedPlanPayload.digitalSignatures
    : [];
  const ncpDailyAnalysis = sourceObservationText(ncpDailyNotes, ncpWeeklyReports);
  const ncpVisibleDailySummary = firstFilled(
    conciseDailyBehaviorEvidenceText(ncpDailyNotes, 4),
    ncpDailyNotes.length ? `${ncpDailyNotes.length} daily-note observation${ncpDailyNotes.length === 1 ? "" : "s"} included for generation.` : "",
  );
  const ncpRecommendationGroups = [
    {
      key: "immediatePriorities",
      title: "Immediate care priorities",
      icon: "ti-flag",
      items: (ncpExtractionDocument?.caregiverSummary?.immediatePriorities || []).map(normalizeSourceText).filter(isUsableNcpDisplayText),
    },
    {
      key: "risksAndWarnings",
      title: "Risks and safeguards",
      icon: "ti-shield-exclamation",
      items: (ncpExtractionDocument?.caregiverSummary?.risksAndWarnings || []).map(normalizeSourceText).filter(isUsableNcpDisplayText),
    },
    {
      key: "requiredAssistance",
      title: "Recommended assistance",
      icon: "ti-user-heart",
      items: (ncpExtractionDocument?.caregiverSummary?.requiredAssistance || []).map(normalizeSourceText).filter(isUsableNcpDisplayText),
    },
    {
      key: "reportingInstructions",
      title: "Monitoring and reporting",
      icon: "ti-clipboard-heart",
      items: (ncpExtractionDocument?.caregiverSummary?.reportingInstructions || []).map(normalizeSourceText).filter(isUsableNcpDisplayText),
    },
  ].filter((group) => group.items.length);
  const canPreviewDocument = record && recordId === "initial-assessment";
  const probationNotes = (record?.dailyNotes || [])
    .filter((note) => isWithinDateRange(note.date, probationNoteRange.start, probationNoteRange.end))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const visibleProbationNotes = probationNotes.slice(0, visibleProbationNoteCount);
  const baseProbationStaff = probationAssignedStaff || record?.assignedStaff || [];
  const assignedProbationStaff = baseProbationStaff.map((staff, originalIndex) => ({ ...staff, originalIndex })).sort(
    (a, b) => new Date(b.to).getTime() - new Date(a.to).getTime(),
  );

  const addCustomQuestion = () => {
    const nextQuestion = customQuestion.trim();
    if (!nextQuestion) return;
    setCustomQuestions((current) => [...current, nextQuestion]);
    setCustomQuestion("");
  };

  const addCustomDocument = () => {
    const nextDocument = customDocument.trim();
    if (!nextDocument) return;
    setCustomDocuments((current) => [...current, nextDocument]);
    setCustomDocument("");
  };

  const addCustomCareCircleMember = () => {
    setCareCircleMembers((current) => [...current, ["", ""]]);
  };

  const removeCareCircleMember = (indexToRemove) => {
    setCareCircleMembers((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addCareProviderStaffMember = () => {
    setCareProviderStaffMembers((current) => [...current, ["", ""]]);
  };

  const removeCareProviderStaffMember = (indexToRemove) => {
    setCareProviderStaffMembers((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addUploadedDocuments = (event) => {
    const files = Array.from(event.target.files || []).map((file) => ({ fileName: file.name, name: file.name }));
    if (!files.length) return;
    setUploadedDocuments((current) => [...current, ...files]);
    event.target.value = "";
  };

  const updateUploadedDocumentName = (indexToUpdate, name) => {
    setUploadedDocuments((current) =>
      current.map((document, index) => (index === indexToUpdate ? { ...document, name } : document)),
    );
  };

  const removeUploadedDocument = (indexToRemove) => {
    setUploadedDocuments((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addPreliminaryAttachedFiles = (event) => {
    const addedAt = new Date().toISOString();
    const files = Array.from(event.target.files || []).map((file) => ({
      type: file.type?.startsWith("image/") ? "photo" : "document",
      name: file.name,
      caption: file.name,
      addedAt,
      previewUrl: file.type?.startsWith("image/") ? URL.createObjectURL(file) : "",
    }));
    if (!files.length) return;
    setPreliminaryAttachedFiles((current) => [...current, ...files]);
    event.target.value = "";
  };

  const updatePreliminaryAttachedFile = (indexToUpdate, field, value) => {
    setPreliminaryAttachedFiles((current) =>
      current.map((file, index) => (index === indexToUpdate ? { ...file, [field]: value } : file)),
    );
  };

  const removePreliminaryAttachedFile = (indexToRemove) => {
    setPreliminaryAttachedFiles((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addDailyNotePhotos = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) return;
    const hasHeicFile = selectedFiles.some((file) => /\.(heic|heif)$/i.test(file.name) || String(file.type || "").toLowerCase().includes("hei"));
    if (hasHeicFile) {
      setDailyNotePhotoProcessing(true);
    }
    try {
      const files = await Promise.all(selectedFiles.map(async (selectedFile) => {
        const conversionResult = await convertHeicToJpegResult(selectedFile);
        const file = conversionResult.file;
        return {
          fileName: file.name,
          originalFileName: selectedFile.name,
          description: file.name,
          type: file.type,
          contentType: file.type,
          convertedFromHeic: conversionResult.converted,
          conversionWarning: conversionWarningFor(conversionResult),
          previewUrl: URL.createObjectURL(file),
        };
      }));
      setDailyNotePhotos((current) => [...current, ...files]);
    } finally {
      if (hasHeicFile) {
        setDailyNotePhotoProcessing(false);
      }
    }
  };

  const updateDailyNotePhotoDescription = (indexToUpdate, description) => {
    setDailyNotePhotos((current) =>
      current.map((photo, index) => (index === indexToUpdate ? { ...photo, description } : photo)),
    );
  };

  const removeDailyNotePhoto = (indexToRemove) => {
    setDailyNotePhotos((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const recordFromDailyNoteResponse = (response, nextRecord, fallbackNote) => {
    const payload = response?.payload && typeof response.payload === "object" ? response.payload : response;
    if (payload?.dailyNotes || response?.dailyNotes) {
      return response?.payload ? { ...nextRecord, ...response.payload } : { ...nextRecord, ...payload };
    }
    const savedNote = payload?.dailyNote || payload?.note || payload;
    const shouldMergeNote = savedNote && typeof savedNote === "object" && (
      savedNote.id ||
      savedNote.noteDate ||
      savedNote.date ||
      savedNote.summary ||
      savedNote.notes
    );
    if (!shouldMergeNote) {
      return nextRecord;
    }
    return {
      ...nextRecord,
      dailyNotes: (nextRecord.dailyNotes || []).map((note) =>
        sameDailyNote(note, fallbackNote) ? { ...fallbackNote, ...savedNote } : note,
      ),
    };
  };

  const saveDailyNote = async () => {
    if (dailyNoteSaving || dailyNotePhotoProcessing) return;
    setDailyNoteSaving(true);
    const now = new Date().toISOString();
    const category = dailyNoteCategory === "Other" ? dailyNoteOtherCategory || "Other" : dailyNoteCategory;
    const notePayload = {
      id: dailyNoteEditingNote?.id || `daily-note-${Date.now()}`,
      noteDate: dailyNoteDate,
      date: dailyNoteEditingNote?.date || `${dailyNoteDate}T${new Date().toTimeString().slice(0, 8)}`,
      title: dailyNoteEditingNote?.title || `${category} note`,
      category,
      summary: dailyNoteRichText.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() || "Daily note captured.",
      notes: dailyNoteRichText,
      photos: dailyNotePhotos,
      addedBy: dailyNoteEditingNote?.addedBy || "Provider",
      updatedBy: "Provider",
      updatedAt: now,
    };
    const dailyNotes = [...(record?.dailyNotes || [])];
    const existingIndex = dailyNoteEditingNote
      ? dailyNotes.findIndex((note) => sameDailyNote(note, dailyNoteEditingNote))
      : -1;
    if (existingIndex > -1) {
      dailyNotes[existingIndex] = notePayload;
    } else {
      dailyNotes.unshift(notePayload);
    }
    const nextRecord = { ...(record || {}), dailyNotes };
    try {
      setRecord(nextRecord);
      let savedRecord;
      try {
        const savedNote = await careService.assessmentRecords.submitDailyNoteForProcess(assessmentProcessId, recordId, {
          dailyNote: notePayload,
        });
        savedRecord = recordFromDailyNoteResponse(savedNote, nextRecord, notePayload);
      } catch (err) {
        if (!isMissingDailyNoteEndpoint(err)) throw err;
        const fallbackRecord = await careService.assessmentRecords.saveForProcess(assessmentProcessId, recordId, {
          payload: nextRecord,
        });
        savedRecord = fallbackRecord?.payload && typeof fallbackRecord.payload === "object" ? { ...nextRecord, ...fallbackRecord.payload } : nextRecord;
      }
      setRecord(savedRecord);
      setDailyNoteDrawerOpen(false);
      setDailyNoteEditingNote(null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Unable to save daily note.");
    } finally {
      setDailyNoteSaving(false);
    }
  };

  const deleteDailyNote = async (noteToDelete) => {
    const noteId = noteToDelete?.id || dailyNoteKey(noteToDelete);
    if (!noteId || dailyNoteSaving) return;
    const previousRecord = record;
    const nextRecord = {
      ...(record || {}),
      dailyNotes: (record?.dailyNotes || []).filter((note) => !sameDailyNote(note, noteToDelete)),
    };
    setDailyNoteSaving(true);
    setRecord(nextRecord);
    setSelectedDailyNote(null);
    try {
      let savedRecord = nextRecord;
      try {
        const response = await careService.assessmentRecords.deleteDailyNoteForProcess(assessmentProcessId, recordId, noteId);
        const payload = response?.payload && typeof response.payload === "object" ? response.payload : response;
        savedRecord = payload?.dailyNotes || response?.dailyNotes ? recordFromDailyNoteResponse(response, nextRecord, noteToDelete) : nextRecord;
      } catch (err) {
        if (!isMissingDailyNoteEndpoint(err)) throw err;
        const fallbackRecord = await careService.assessmentRecords.saveForProcess(assessmentProcessId, recordId, {
          payload: nextRecord,
        });
        savedRecord = fallbackRecord?.payload && typeof fallbackRecord.payload === "object" ? { ...nextRecord, ...fallbackRecord.payload } : nextRecord;
      }
      setRecord(savedRecord);
    } catch (err) {
      setRecord(previousRecord);
      setError(err?.response?.data?.message || err?.message || "Unable to delete daily note.");
    } finally {
      setDailyNoteSaving(false);
    }
  };

  const openAddDailyNoteDrawer = () => {
    setDailyNoteEditingNote(null);
    setDailyNoteDate(dateInputValue(new Date()));
    setDailyNoteCategory("Behavior");
    setDailyNoteOtherCategory("");
    setDailyNoteRichText("");
    setDailyNotePhotos([]);
    setDailyNoteDrawerOpen(true);
  };

  const openEditDailyNoteDrawer = (note) => {
    const knownCategory = dailyNoteCategories.includes(note.category);
    setDailyNoteEditingNote(note);
    setDailyNoteDate(dateInputValue(note.date));
    setDailyNoteCategory(knownCategory ? note.category : "Other");
    setDailyNoteOtherCategory(knownCategory ? "" : note.category);
    setDailyNoteRichText(note.notes || `<p>${note.summary || ""}</p>`);
    setDailyNotePhotos(note.photos || []);
    setSelectedDailyNote(null);
    setDailyNoteDrawerOpen(true);
  };

  const addProbationStaff = () => {
    setProbationStaffDrawer({
      index: null,
      values: { name: "", role: "", from: `${dateInputValue(new Date())}T07:00:00`, to: `${dateInputValue(new Date())}T15:00:00` },
    });
  };

  const removeProbationStaff = (indexToRemove) => {
    setProbationAssignedStaff((current) => (current || record?.assignedStaff || []).filter((_, index) => index !== indexToRemove));
    setProbationStaffDrawer(null);
  };

  const editProbationStaff = (staff) => {
    const { originalIndex, ...values } = staff;
    setProbationStaffDrawer({ index: originalIndex, values });
  };

  const updateProbationStaffDrawerField = (field, value) => {
    setProbationStaffDrawer((current) => current ? { ...current, values: { ...current.values, [field]: value } } : current);
  };

  const saveProbationStaffDrawer = () => {
    if (!probationStaffDrawer) return;
    setProbationAssignedStaff((current) => {
      const nextStaff = [...(current || record?.assignedStaff || [])];
      if (probationStaffDrawer.index === null) {
        nextStaff.push(probationStaffDrawer.values);
      } else {
        nextStaff[probationStaffDrawer.index] = probationStaffDrawer.values;
      }
      return nextStaff;
    });
    setProbationStaffDrawer(null);
  };

  const confirmMarkCompleted = () => {
    Modal.confirm({
      title: `Mark ${recordName} as completed?`,
      content: `This will move the ${recordName} record to completed status for this assessment process.`,
      okText: "Mark Completed",
      cancelText: "Keep Editing",
      okButtonProps: { className: "assessment-confirm-primary" },
    });
  };

  const planDescription = preliminaryPlanDescription || record?.description || "";
  const generationLocked = preliminaryGenerationLocked || Boolean(record?.generationLocked);
  /*
  const legacyNegotiatedTemplateDefaults = {
    providerName: "Olreadia Care",
    dateNcpStarted: assessment.startDate,
    movedInDate: "2024-05-08",
    residentName: processResidentName(assessment),
    primaryLanguage: "English",
    speaksEnglish: "Yes",
    interpreterNeeded: "No",
    allergies: "Verify against state assessment and medication list.",
    legalDocuments: "PoA documentation received; confirm Advanced Directives and POLST status.",
    specialtyNeeds: "No specialty need confirmed yet; monitor dementia, mental health, and developmental disability indicators from state assessment.",
    evacuationAssistance: "Assistance required. Resident needs standby assistance and clear cueing.",
    evacuationSafetyInstructions: "Keep walker within reach, remove barriers, and guide resident with calm one-step instructions.",
    evacuationCaregiverInstructions: "Caregiver will keep walkways clear and assist resident through the safest exit route.",
    medicalDiagnosis: "Review state assessment diagnosis and medication history. Early notes show transfer fatigue and medication prompting needs.",
    contact1: "Amanda Parker - Social Worker / Case Manager.",
    contact1Name: "Amanda Parker",
    contact1Relationship: "Social Worker / Case Manager",
    contact2: "Elaine Johnson - Daughter / PoA, preferred family contact.",
    contact2Name: "Elaine Johnson",
    contact2Relationship: "Daughter / PoA",
    contact3: "Robert Johnson - Son.",
    contact3Name: "Robert Johnson",
    contact3Relationship: "Son",
    communicationModes: "Resident communicates verbally and responds to calm explanations.",
    selfUnderstood: "Resident makes needs known verbally.",
    understandsOthers: "Resident understands short, respectful instructions.",
    communicationStrengths: "Resident is receptive to routine, music, and family phone calls.",
    communicationAssistance: "Caregiver will use calm tone, one-step cueing, and allow time for response.",
    medicationAssistanceNeeds: "Medication prompts and monitoring are needed.",
    medicationManagementType: "Medication administration/support per MAR and physician orders.",
    medicationAssistance: "Caregiver will follow the five rights plus documentation and report adverse reactions or refusal.",
    medicationRefusalPlan: "If resident refuses medication, document refusal and notify PCP at the time of refusal.",
    healthIndicatorMonitoring: "Monitor hydration, transfer fatigue, mobility changes, welfare concerns, and pain indicators.",
    monitoringReporting: "Report significant changes to PCP, PoA/family, social worker, and care team as appropriate.",
    treatmentTypes: "Confirm PT/OT/ST, nurse delegation, home health, oxygen, glucose monitoring, wound care, and hospice status.",
    meaningfulDay: "Use meaningful day routines including warm tea, quiet breakfast, music after lunch, and family contact.",
    sleepSupport: "Continue observing sleep and nighttime assistance needs.",
    behaviors: "No disruptive behavior observed in current notes. Continue documenting wandering, agitation, refusal, mood, and safety concerns.",
    typicalDayNarrative: "Quiet morning routine, coffee, light breakfast, music after lunch, warm tea before lunch, and family call after dinner.",
    leftAloneAbility: "Do not leave resident unattended until ability is confirmed by assessment and provider review.",
    universalPrecautionsCaregiver: "Caregiver will use standard precautions, hand hygiene, gloves as appropriate, and report infection concerns.",
    functionalLimitations: "Mild transfer fatigue and walker use noted.",
    locomotionRoom: "Walker with standby support.",
    fallRisks: "Maintain walker within reach and cue non-slip footwear.",
    mobilityEquipment: "Walker; confirm additional equipment needs.",
    mobilityAssistance: "Caregiver will provide standby transfer and mobility support.",
    transferLevel: "Assistance needed / standby support.",
    transferAssistance: "Caregiver will monitor fatigue and provide safe transfer cueing.",
    eatingLevel: "Resident eats by mouth; confirm assistance level and special diet.",
    specialDiet: "Low-salt meals preferred; confirm physician diet orders.",
    continenceIssues: "Continue observing continence and toileting patterns.",
    dressing: "Confirm self/assist/supervise level for shoes, stockings, and clothing.",
    personalHygiene: "Confirm oral hygiene, grooming, hair care, shaving, nails, dentures, and assistance level.",
    bathing: "Confirm shower/bed bath preference, bath days, and assistance level.",
    skinCare: "Monitor skin concerns, pressure areas, and any dressing or nurse delegation needs.",
    finances: "Confirm who manages finances and financial records.",
    shopping: "Confirm shopping assistance and transportation needs.",
    transportation: "Confirm medical transportation, escort requirements, and frequency.",
    activitiesSocial: "Resident prefers quiet routines, music, warm tea, and family contact.",
    activityPreferences: "Music, phone conversation/visiting, quiet one-on-one support, and gentle walking as tolerated.",
    caseManagement: "Amanda Parker - Social Worker / Case Manager.",
    cmContactedWhen: "Contact CM when care needs change, assistive devices are needed, or plan updates require coordination.",
    reviewSchedule: "Review after significant change, when plan no longer meets needs/preferences, at resident/representative request, and at least every twelve months.",
    involvedInDevelopment: "Resident, resident representative/PoA, provider, social worker/case manager, and care team.",
    dateCompleted: "Pending completion.",
    dateDischarged: "Not applicable.",
    evacuationIndependent: "Not selected.",
    assessmentSectionIndex: "Complete all applicable NCP sections from state assessment, preliminary service plan, MaG, onboarding, annual progress, and daily notes.",
    hearingProblems: "Confirm hearing-aid/deafness status from assessment and resident/family interview.",
    visionProblems: "Confirm eyesight, glasses, blindness, cataracts, and legal blindness status.",
    phoneAbility: "Confirm whether resident uses phone independently and whether resident has own phone.",
    preferredLanguageComments: "English preferred; confirm any communication accommodations.",
    communicationComments: "Continue documenting communication preferences and changes noted by the care team.",
    medicationAllergies: "Verify medication allergies from MAR/state assessment.",
    psychopharmacologicMeds: "Confirm psychopharmacologic medications from MAR and behavior section.",
    marReference: "See MAR for current medications, dosage, frequency, and route.",
    medsOrderedBy: "Confirm ordering provider.",
    medsDeliveredBy: "Confirm pharmacy/delivery source.",
    pharmacyPacked: "Confirm whether medications are pharmacy packed.",
    medicationRoutes: "Confirm oral, topical, eye drops, inhalers, sprays, injections, allergy kits, suppositories, and other routes.",
    injectionSupport: "No injection support identified yet; confirm against MAR and nurse delegation.",
    nurseDelegation: "Confirm whether nurse delegation is required.",
    medicationStrengths: "Resident responds to routine medication prompts.",
    medicationOutsideAfhStrengths: "Confirm resident strengths when away from AFH.",
    medicationOutsideAfh: "Medication plan outside AFH to be confirmed with PoA/family and pharmacy.",
    medicationRefusalStrengths: "Resident can express preferences; monitor refusals.",
    healthStrengths: "Resident participates in calm routines and accepts reminders.",
    healthAssistance: "Caregiver monitors changes and documents concerns.",
    allergySubstanceReaction: "Verify allergy substances and reactions from state assessment.",
    rangeOfMotionTherapies: "Confirm ROM, PT/OT/ST, and delegated therapy tasks.",
    treatmentStrengths: "Resident can participate with cueing as tolerated.",
    treatmentAssistance: "Caregiver will follow provider and therapy instructions.",
    programsAttended: "Confirm community programs, home health, adult day health, hospice, and other services.",
    physicalEnablers: "Confirm any physical enablers and risk assessments.",
    treatmentRefusalPlan: "Document refusals and notify responsible provider/team as appropriate.",
    sleepStrengths: "Resident benefits from quiet nighttime routine.",
    sleepAssistance: "Caregiver will monitor nighttime needs and document changes.",
    memoryImpairment: "Confirm memory impairment details from assessment.",
    memoryStrengths: "Resident responds to familiar routines.",
    memoryAssistance: "Caregiver will provide cueing and reassurance.",
    decisionMaking: "Confirm decision-making support needs.",
    psychMeds: "Confirm psychopharmacological medication use and symptoms addressed.",
    behavioralHealthSupport: "Confirm crisis plan, counseling, mental health provider, and past behaviors.",
    specializedBehaviorPrograms: "Meaningful Day noted; confirm other DSHS specialized behavior programs.",
    psychInterventions: "Use calm routines, meaningful day activities, and resident-centered redirection.",
    leftAloneStrengths: "Resident can communicate needs but requires safety review before being left alone.",
    leftAloneAssistance: "Caregiver will provide supervision level indicated by assessment.",
    universalPrecautions: "Use standard precautions; confirm mask, vaccination, sanitizer, shared equipment, and special precautions.",
    universalPrecautionsStrengths: "Resident can follow simple infection-control cues.",
    universalPrecautionsAssistance: "Assist resident with precautions as needed.",
    locomotionOutside: "Confirm outdoor/community mobility level.",
    bedroomDoorLock: "Confirm resident choice.",
    mobilityLimitations: "Mild fatigue observed with transfers.",
    mobilityStrengths: "Resident can ambulate with walker and standby support.",
    skinCarePositioning: "No current positioning skin issue identified; monitor.",
    turningRepositioning: "Confirm repositioning frequency if needed.",
    transferSafetyPlan: "Use walker, clear pathways, and calm cueing.",
    medicalDevices: "Walker currently identified; confirm lift/enabler needs.",
    transferEquipment: "Walker; confirm gait belt, transfer board, lift, or other supplies.",
    transferStrengths: "Resident can follow transfer cues.",
    eatingEquipment: "Confirm clothing protector, adaptive utensils, supplements, thickened liquids, and hydration plan.",
    eatingStrengths: "Resident participates in meals and prefers warm tea.",
    eatingAssistance: "Caregiver will cue hydration and follow diet orders.",
    toiletingLevel: "Confirm toileting assistance level and frequency.",
    toiletingStrengths: "Resident can communicate toileting needs; confirm.",
    toiletingAssistance: "Caregiver will provide assistance per assessed level.",
    footCare: "Confirm diabetic foot care, nail care, podiatry, home health, equipment, and assistance.",
    activityNarrative: "Resident benefits from predictable low-stimulation daily rhythm.",
    specialArrangements: "Confirm special arrangements and participation assistance.",
    smoking: "Confirm smoking status, policy review, safety concerns, and storage.",
    otherIssues: "Other issues, concerns, or problems to be updated from assessment and care team review.",
    abbreviations: "ADL, DPOA, MD, W/c, AFH, D/t, PCP, CG, Hx, PRN, Dr., MAR, RND.",
    reviewDates: "Original plan and review/revision dates pending final completion.",
    approvingPerson: "To be confirmed during final review.",
    providerSignature: "Pending signature/date.",
    residentSignature: "Pending signature/date.",
    residentRepresentativeSignature: "Pending signature/date.",
    residentVerbalAgreement: "Not yet recorded.",
    sentToDshs: "Not yet sent.",
    residentRecommendations: "Confirm resident recommendations during final review.",
    signatureNote: "Signer writes the date they actually read and agreed to the plan; document verbal agreement details if applicable.",
  };
  */
  const negotiatedTemplateDefaults = {};
  const normalizedNegotiatedPlanPayload = ensureNegotiatedPlanPayloadFields(negotiatedPlanPayload, negotiatedPlanTemplate);
  const ncpTemplateFields = negotiatedTemplateDefinition(negotiatedPlanTemplate).flatMap((page) =>
    page.sections.flatMap((section) => section.fields.map((field) => ({
      ...field,
      pageNumber: page.pageNumber,
      sectionTitle: section.title,
    }))),
  );
  const ncpTemplateSourceGraph = ncpFieldSourceGraphFromTemplate(negotiatedPlanTemplate) || {};
  const ncpFilledKeys = new Set((normalizedNegotiatedPlanPayload.pages || []).flatMap((page) =>
    (page.sections || []).flatMap((section) =>
      (section.fields || []).filter((field) => normalizeSourceText(field.value)).map((field) => field.key),
    ),
  ));
  const ncpMappedFieldRows = ncpTemplateFields.map((field) => ({
    ...field,
    sourceKey: relatedNcpBaseKeyFromTemplate(field.key, negotiatedPlanTemplate),
  })).filter((field) => ncpTemplateSourceGraph[field.sourceKey]);
  const ncpEmptyMappedFields = ncpMappedFieldRows
    .filter((field) => !ncpFilledKeys.has(field.key))
    .slice(0, 10);
  const negotiatedTemplatePages = (normalizedNegotiatedPlanPayload.pages || []).map((page) => ({
    ...page,
    sections: (page.sections || []).map((section) => ({
      ...section,
      fields: (section.fields || []).map((field) => ({
        ...field,
        checks: field.checks || [],
        value: field.value || negotiatedTemplateDefaults[field.key] || "",
      })),
    })),
  }));
  const negotiatedPlanApiPayload = {
    ...normalizedNegotiatedPlanPayload,
    pages: negotiatedTemplatePages.map((page) => ({
      pageNumber: page.pageNumber,
      title: page.title || `Page ${page.pageNumber} of 19`,
      sections: page.sections.map((section) => ({
        id: section.id,
        title: section.title,
        fields: section.fields.map((field) => ({
          id: field.id,
          key: field.key,
          label: field.label,
          value: field.value,
          checks: field.checks || [],
        })),
      })),
    })),
  };
  const negotiatedPayloadField = (key) => findNegotiatedPayloadField(normalizedNegotiatedPlanPayload, key);
  const negotiatedFieldValue = (key) => {
    const value = negotiatedPayloadField(key)?.value || negotiatedTemplateDefaults[key] || "";
    return key === "providerName" ? cleanNcpFieldByKey(key, value) : value;
  };
  const ncpFieldHtml = (key) => escapeHtml(negotiatedFieldValue(key)).replace(/\n/g, "<br />");
  const ncpCompactFieldHtml = (key, maxLength = 180) => {
    const value = normalizeSourceText(negotiatedFieldValue(key));
    if (!value) return "";
    const clipped = value.length <= maxLength
      ? value
      : `${value.slice(0, maxLength).trim().replace(/[.,;:]+$/, "")}...`;
    return escapeHtml(clipped);
  };
  const latestNcpDigitalSignature = (roleValue) =>
    [...ncpDigitalSignatures].reverse().find((signature) => signature.role === roleValue && signature.signatureImageDataUrl);
  const ncpSignatureCellHtml = (roleValue, fieldKey) => {
    const signature = latestNcpDigitalSignature(roleValue);
    const signedDate = signature?.signedAt ? formatUsDateOnly(signature.signedAt) : "";
    const imageHtml = signature?.signatureImageDataUrl
      ? `<img class="ncp-handwritten-signature" alt="${escapeHtml(`${signature.roleLabel || "NCP"} signature`)}" src="${escapeHtml(signature.signatureImageDataUrl)}" />`
      : "";
    const dateHtml = signedDate ? `<span class="ncp-signature-date">${escapeHtml(signedDate)}</span>` : "";
    return `<div class="ncp-signature-line">${imageHtml}${dateHtml}</div>`;
  };
  const ncpFieldParagraph = (key) => ncpFieldHtml(key) ? `<p>${ncpFieldHtml(key)}</p>` : "";
  const ncpFieldBlock = (key, label) => `<strong${label ? "" : ' class="ncp-blue"'}>${escapeHtml(label || "Details")}</strong>${ncpFieldParagraph(key) || "<p>&nbsp;</p>"}`;
  const ncpCaregiverHtml = (key) => {
    const rawValue = normalizeSourceText(negotiatedFieldValue(key));
    if (!rawValue) return "";
    const escapedValue = escapeHtml(rawValue).replace(/\n/g, "<br />");
    return /^(?:cg|caregiver)\s+(?:will|is|to|should)\b/i.test(rawValue)
      ? escapedValue
      : `Caregiver will ${escapedValue}`;
  };
  const ncpCaregiverBlock = (key) => `<strong class="ncp-blue">Assistance Required</strong>${ncpCaregiverHtml(key) ? `<p>${ncpCaregiverHtml(key)}</p>` : "<p>&nbsp;</p>"}`;
  const ncpMonitoringCaregiverBlock = (key) => `<p><strong>Monitoring/Reporting significant changes and/or concerns:</strong> Caregiver is to monitor the resident during care, report concerns and significant changes immediately to relevant individuals (health care provider, POA, CM, etc.).</p>${ncpCaregiverBlock(key)}`;
  const ncpCheckValue = (key, index) => {
    const field = negotiatedPayloadField(key);
    if (isNcpApprovalFieldKey(key) && !mentionsExplicitNcpApproval(field?.value || "")) return false;
    if (key === "sentToDshs" && !mentionsExplicitNcpDispatch(field?.value || "")) return false;
    if (["psychopharmacologicMeds", "psychMeds"].includes(key) && index === 0 && !hasExplicitPsychMedicationEvidence(field?.value || "")) return false;
    if (key === "pharmacyPacked" && index === 0 && /\b(?:pharmacy\s+packed|pre-?packaged|bubble\s+pack|blister\s+pack|meds?\s+are\s+packed|medications?\s+are\s+packed|packed\s+and\s+delivered)\b/i.test(field?.value || "")) return true;
    if (key === "behaviorCheckboxes") {
      const behaviorText = normalizeSourceText([
        negotiatedFieldValue("behaviors"),
        negotiatedFieldValue("behaviorAssistance"),
        negotiatedFieldValue("behavioralHealthSupport"),
      ].join(" "));
      if (index === 6 && /\b(?:sundowning|disorient(?:ed|ation)|confus(?:ed|ion)|orientation)\b/i.test(behaviorText)) return true;
      if (index === 13 && /\b(?:unsafe|risk\s+observed|without\s+walker|stand(?:ing)?\s+without|poor\s+safety|impaired\s+safety)\b/i.test(behaviorText)) return true;
      if (index === 22 && !/\bother\b/i.test(field?.value || "")) return false;
    }
    return Boolean(field?.checks?.[index]?.checked);
  };
  const ncpCheckText = (key, index) => ncpCheckValue(key, index) ? "[x]" : "[ ]";
  const ncpCheckLine = (key, index, label, className = "") =>
    `<p${className ? ` class="${className}"` : ""}>${ncpCheckText(key, index)} ${escapeHtml(label)}</p>`;
  const makeNcpCheckboxesFillable = (html) => {
    return html.replace(/\[\s*(x?)\s*\]/gi, (_, checked) => {
      return `<input class="ncp-fillable-check" type="checkbox" aria-label="checkbox"${checked ? " checked" : ""} />`;
    });
  };
  const ncpIndexHtml = ncpPageOneIndexLinks.map((link) => `<span>${escapeHtml(link)}</span>`).join("");
  const medicationRouteCheckboxHtml = ncpPageThreeMedicationRouteOptions.map((option, index) => ncpCheckLine("medicationRoutes", index, option)).join("");
  const pageFourMedicationRouteCheckboxHtml = ncpPageFourMedicationRouteOptions.map((option, index) => ncpCheckLine("medicationRoutes", index + ncpPageThreeMedicationRouteOptions.length, option)).join("");
  const injectionSupportCheckboxHtml = injectionSupportOptions.map((option, index) => `<p class="${option === "(Insulin Only)" ? "ncp-indent-line" : "ncp-nested-checkbox"}">${option === "(Insulin Only)" ? "" : `${ncpCheckText("injectionSupport", index)} `}${escapeHtml(option)}</p>`).join("");
  const healthIndicatorCheckboxHtml = healthIndicatorOptions.map((option) => {
    const isBlue = option.text === "Pain" || option.text === "Other:";
    const prefix = Number.isInteger(option.checkIndex) ? `${ncpCheckText("healthIndicatorMonitoring", option.checkIndex)} ` : "";
    return `<p class="${option.nested ? "ncp-nested-checkbox" : ""}">${prefix}<span class="${isBlue ? "ncp-blue" : ""}">${escapeHtml(option.text)}</span></p>`;
  }).join("");
  const treatmentTypeCheckboxHtml = treatmentTypeOptions.map((option, index) => {
    const isNested = option === "INR/LAB - Health Provider:" || option === "Easily bruised/Anti coagulation therapy";
    return `<p class="${isNested ? "ncp-nested-checkbox" : ""}">${ncpCheckText("treatmentTypes", index)} ${escapeHtml(option)}</p>`;
  }).join("");
  const treatmentTherapyCheckboxHtml = treatmentTherapyOptions.map((option) => {
    const isBlue = option === "Range of Motion" || option === "Other:";
    const isLabelOnly = option === "Treatments/Therapies - Tasks:";
    const checkIndex = option === "PT/OT/ST" ? 0 : option === "Nurse Delegation for" ? 1 : option === "Other:" ? 2 : null;
    return `<p>${isLabelOnly || checkIndex === null ? "" : `${ncpCheckText("rangeOfMotionTherapies", checkIndex)} `}<span class="${isBlue ? "ncp-blue" : ""}">${escapeHtml(option)}</span></p>`;
  }).join("");
  const treatmentProgramCheckboxHtml = treatmentProgramOptions.map((option, index) => {
    const isNested = option === "Hospice Plan";
    const isBlue = option === "Home Health" || option === "Hospice - Agency:";
    return `<p class="${isNested ? "ncp-nested-checkbox" : ""}">${ncpCheckText("programsAttended", index)} <span class="${isBlue ? "ncp-blue" : ""}">${escapeHtml(option)}</span></p>`;
  }).join("");
  const renderPsychLineHtml = (line) => {
    if (line.section) return `<p class="ncp-psych-section-line">${escapeHtml(line.text)}</p>`;
    if (line.note) return `<p class="ncp-psych-note-line">${escapeHtml(line.text)}</p>`;
    const labelHtml = line.blueText
      ? `<span class="ncp-blue">${escapeHtml(line.blueText)}</span>${escapeHtml(line.suffix || "")}`
      : `<span class="${line.blue ? "ncp-blue" : ""}">${escapeHtml(line.text)}</span>${escapeHtml(line.suffix || "")}`;
    const checkText = line.checkbox ? ncpCheckText(line.checkKey || "behaviorCheckboxes", Number.isInteger(line.checkIndex) ? line.checkIndex : 0) : "";
    return `<p class="ncp-psych-check-line">${line.checkbox ? `${checkText}&nbsp;&nbsp;` : ""}${labelHtml}</p>`;
  };
  const psychStatusRowsHtml = psychStatusCells.map((cell) => `<div class="${cell.className}">${cell.lines.map(renderPsychLineHtml).join("")}</div>`).join("");
  const pageEightBehaviorRowsHtml = psychPageEightBehaviorRows.map((row, index) => `<div class="ncp-page-eight-behavior-row">${ncpCheckText("behaviorCheckboxes", index + 15)}&nbsp;&nbsp;${escapeHtml(row)}</div>`).join("");
  const pageEightSupportHtml = psychPageEightSupportLines.map((line) => {
    const supportCheckKey = line.text.includes("Requires psychopharmacological")
      ? "psychMeds"
      : line.text.includes("Behavioral Health Support") || line.text === "Counseling"
        ? "behavioralHealthSupport"
        : "";
    const supportCheckIndex = line.text === "Counseling" ? 1 : 0;
    const prefix = line.checkbox ? `${ncpCheckText(supportCheckKey, supportCheckIndex)} ` : "";
    const content = line.blue ? `<span class="ncp-blue ncp-doc-link">${escapeHtml(line.text)}</span>${escapeHtml(line.suffix || "")}` : `${escapeHtml(line.text)}${escapeHtml(line.suffix || "")}`;
    return `<p class="${line.spacer ? "ncp-spaced-line" : ""}">${prefix}${line.strong ? `<strong>${content}</strong>` : content}</p>`;
  }).join("");
  const pageEightProgramsHtml = specializedBehaviorProgramLines.map((line) => {
    const programIndex = line.text === "Meaningful Day" ? 0
      : line.text.includes("Expanded Community") ? 1
        : line.text.includes("Specialized Behavior") ? 2
          : line.text.includes("Mental Health Provider/Program") ? 3
            : null;
    const box = programIndex !== null ? `${ncpCheckText("specializedBehaviorPrograms", programIndex)} ` : "";
    return `<p class="${line.spacer ? "ncp-spaced-line" : ""}">${line.strong ? `<strong>${escapeHtml(line.text)}</strong>` : `${box}${escapeHtml(line.text)}`}</p>`;
  }).join("");
  const universalPrecautionHtml = universalPrecautionLines.map((line) => `<p class="${line.nested ? "ncp-nested-checkbox" : ""}">${line.noBox ? "" : `${ncpCheckText("universalPrecautions", line.checkIndex)} `}<span class="${line.blue ? "ncp-blue" : ""}">${escapeHtml(line.text)}</span></p>`).join("");
  const universalPrecautionPageTenHtml = universalPrecautionPageTenLines.map((line) => `<p>${ncpCheckText("universalPrecautions", line.checkIndex)} ${escapeHtml(line.text)}${line.blueSuffix ? `<br /><span class="ncp-blue">${escapeHtml(line.suffix)}</span>` : ""}</p>`).join("");
  const ncpPageOneDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-one-sheet">
    <div class="ncp-page-one-title">Adult Family Home Resident Negotiated Care Plan <span>(NCP)</span></div>
    <div class="ncp-page-one-note"><strong>NOTE: Place an X in the bracket &#91;&#93; - &#91;x&#93; - to indicate/select your choice.</strong><em>Form Version: ${escapeHtml(record?.formVersion || "")}</em></div>
    <div class="ncp-page-one-grid">
      <div class="ncp-cell ncp-provider-cell"><b>Provider's Name:</b><p>${escapeHtml(negotiatedFieldValue("providerName"))}</p></div>
      <div class="ncp-cell ncp-started-cell"><b>Date NCP Started:</b><p>${escapeHtml(negotiatedFieldValue("dateNcpStarted"))}</p></div>
      <div class="ncp-cell ncp-moved-cell"><b>Moved In Date:</b><p>${escapeHtml(negotiatedFieldValue("movedInDate"))}</p></div>
      <div class="ncp-cell ncp-completed-cell"><b><span>Date Completed:</span></b><p>${escapeHtml(negotiatedFieldValue("dateCompleted"))}</p></div>
      <div class="ncp-cell ncp-discharged-cell"><b>Date Discharged:</b><p>${escapeHtml(negotiatedFieldValue("dateDischarged"))}</p></div>
      <div class="ncp-cell ncp-resident-cell"><b>Resident's Name:</b><p>${escapeHtml(negotiatedFieldValue("residentName"))}</p><b>Pronouns:</b><p>${escapeHtml(negotiatedFieldValue("pronouns"))}</p></div>
      <div class="ncp-cell ncp-dob-cell"><b>Date of Birth/Age:</b><p>${escapeHtml(negotiatedFieldValue("dateOfBirthAge"))}</p></div>
      <div class="ncp-cell ncp-language-cell"><b>Primary Language:</b><p>${escapeHtml(negotiatedFieldValue("primaryLanguage"))}</p><b>Speaks English?</b><p>${escapeHtml(negotiatedFieldValue("speaksEnglish"))}</p><b>Interpreter needed?</b><p>${escapeHtml(negotiatedFieldValue("interpreterNeeded"))}</p></div>
      <div class="ncp-cell ncp-allergy-cell"><b class="ncp-red">ALLERGIES:</b><p class="ncp-allergy-copy">${ncpFieldHtml("allergies") || "&nbsp;"}</p></div>
      <div class="ncp-cell ncp-left-column"><b>Legal Documents: ${ncpCheckText("legalDocuments", 0)} None</b><p>${ncpCheckText("legalDocuments", 1)} Advanced Directives</p><p>${ncpCheckText("legalDocuments", 2)} POLST Form</p><p>${ncpCheckText("legalDocuments", 3)} Other: ${escapeHtml(negotiatedFieldValue("legalDocuments"))}</p><br /><b>Specialty Needs: ${ncpCheckText("specialtyNeeds", 0)} No</b><p>${ncpCheckText("specialtyNeeds", 1)} Dementia</p><p>${ncpCheckText("specialtyNeeds", 2)} Mental Health</p><p>${ncpCheckText("specialtyNeeds", 3)} Developmental Disability</p><p>${ncpCheckText("specialtyNeeds", 4)} Other: ${escapeHtml(negotiatedFieldValue("specialtyNeeds"))}</p></div>
      <div class="ncp-cell ncp-emergency-heading"><b>EMERGENCY EVACUATION</b></div>
      <div class="ncp-cell ncp-evacuation-copy"><b>EVACUATION ASSISTANCE REQUIRED:</b><p>${ncpCheckText("evacuationIndependent", 0)} <strong>NONE - RESIDENT IS INDEPENDENT:</strong> Resident is physically and mentally capable of independently evacuating the home without the assistance of another individual or the use of mobility aids. The department will consider a resident independent if capable of getting out of the home after one cue. ${escapeHtml(negotiatedFieldValue("evacuationIndependent"))}</p><p>${ncpCheckText("evacuationAssistance", 0)} <strong>ASSISTANCE REQUIRED:</strong> Resident is not physically or mentally capable of evacuating the home without assistance from another individual, mobility aids, or multiple cues.</p><p>${escapeHtml(negotiatedFieldValue("evacuationAssistance"))}</p><p><strong class="ncp-red">RESIDENT'S EVACUATION and SAFETY INSTRUCTIONS:</strong></p>${ncpFieldParagraph("evacuationSafetyInstructions")}<p>${ncpCaregiverHtml("evacuationCaregiverInstructions")}</p></div>
    </div>
    <div class="ncp-medical-band"><strong>MENTAL/PHYSICAL HEALTH - CURRENT MEDICAL STATUS/DIAGNOSIS</strong><p>${escapeHtml(negotiatedFieldValue("medicalDiagnosis"))}</p></div>
    <div class="ncp-index-grid">${ncpIndexHtml}</div>
    <div class="ncp-page-footer"><span>Resident's Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 1 of 19</strong></div>
  </section>`;
  const ncpPageTwoDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-two-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <table class="ncp-contacts-table">
      <thead>
        <tr><th colspan="5"><strong>RESPONSIBLE PARTIES - CONTACTS</strong><br />Add those involved in care planning for your resident: Case Manager, DPOA, Guardian, Medical/Mental Health Providers, Pharmacy, Hospice, etc.<br />- Indicate which contact method is preferred.</th></tr>
        <tr><th>Name</th><th><span>Relationship</span></th><th>Home/Business<br />Phone</th><th>Cell Phone/FAX</th><th>Address/Email</th></tr>
      </thead>
      <tbody>
        ${[1, 2, 3, 4, 5, 6].map((number) => `<tr>${ncpContactColumns.map((column, columnIndex) => `<td>${columnIndex === 0 ? `${number}. ` : ""}${escapeHtml(negotiatedFieldValue(ncpContactFieldKey(number, column.key)))}</td>`).join("")}</tr>`).join("")}
      </tbody>
    </table>
    <p class="ncp-page-two-tip">(Place your cursor in the last box and hit your tab key to add another row)</p>
    <div class="ncp-communication-title">COMMUNICATION</div>
    <div class="ncp-communication-grid">
      <div class="ncp-comm-head ncp-comm-topic">SPEECH/HEARING/VISION</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Caregiver Instructions<br />Who, How, When/How Often</div>
      <div class="ncp-comm-left"><b>Modes of Expression:</b><p>${escapeHtml(negotiatedFieldValue("communicationModes"))}</p><b>Problems with Mode of Expression - ${ncpCheckText("expressionProblems", 0)} Yes ${ncpCheckText("expressionProblems", 1)} No</b><p>Describe: ${escapeHtml(negotiatedFieldValue("expressionProblems"))}</p><p>Equipment: ${escapeHtml(negotiatedFieldValue("expressionEquipment"))}</p><hr /><b>Problems with Hearing - ${ncpCheckText("hearingProblems", 0)} Yes ${ncpCheckText("hearingProblems", 1)} No</b><p>Describe: ${escapeHtml(negotiatedFieldValue("hearingProblems"))}</p><p>Equipment:</p><hr /><b>Problems with Vision - ${ncpCheckText("visionProblems", 0)} Yes ${ncpCheckText("visionProblems", 1)} No</b><p>Describe: ${escapeHtml(negotiatedFieldValue("visionProblems"))}</p><p>Equipment:</p><hr /><b>Ability to Use the Phone</b><p>${ncpCheckText("phoneAbility", 0)} Independent ${ncpCheckText("phoneAbility", 1)} Assistance Needed ${ncpCheckText("phoneAbility", 2)} Dependent</p><p>${ncpCheckText("phoneAbility", 3)} Resident has own phone, number: ${escapeHtml(negotiatedFieldValue("phoneAbility"))}</p><hr /><b>Preferred Language:</b><p>${escapeHtml(negotiatedFieldValue("preferredLanguageComments"))}</p></div>
      <div class="ncp-comm-middle"><b>How resident makes self-understood:</b><p>${escapeHtml(negotiatedFieldValue("selfUnderstood"))}</p><b>How resident understands others:</b><p>${escapeHtml(negotiatedFieldValue("understandsOthers"))}</p><b class="ncp-blue">Strength and Abilities</b><p>${escapeHtml(negotiatedFieldValue("communicationStrengths"))}</p></div>
      <div class="ncp-comm-right"><b class="ncp-blue">Assistance Required</b><p>${ncpCaregiverHtml("communicationAssistance")}</p></div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 2 of 19</strong></div>
  </section>`;
  const ncpPageThreeDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-three-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-communication-title">COMMUNICATION</div>
    <div class="ncp-comments-grid">
      <div class="ncp-comm-head ncp-comm-topic">SPEECH/HEARING/VISION</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Caregiver Instructions<br />Who, How, When/How Often</div>
      <div class="ncp-comments-row"><b>Comments:</b>${ncpFieldParagraph("communicationComments")}</div>
    </div>
    <div class="ncp-med-overview">
      <div class="ncp-section-bar">MEDICATION MANAGEMENT - Overview</div>
      <div class="ncp-med-overview-grid">
        <div><p>${ncpCheckText("medicationAllergies", 0)} <strong class="ncp-red">MEDICATION ALLERGIES:</strong> ${ncpFieldHtml("medicationAllergies")}</p><p>${ncpCheckText("medicationAssistanceNeeds", 0)} Resident needs more than one kind of medication assistance need ${ncpFieldHtml("medicationAssistanceNeeds")}</p><p>${ncpCheckText("psychopharmacologicMeds", 0)} Resident is prescribed psychopharmacologic medications - <em>see behavior section for strategies and modification to address symptoms addressed by this/these medications</em> ${ncpFieldHtml("psychopharmacologicMeds")}</p></div>
        <div><p>${ncpCheckText("marReference", 0)} See MAR for current medications, dosage, frequency, and route ${ncpFieldHtml("marReference")}</p><br /><p>Meds are ordered by: ${ncpFieldHtml("medsOrderedBy")}</p><p>Meds are delivered by: ${ncpFieldHtml("medsDeliveredBy")}</p><p>${ncpCheckText("pharmacyPacked", 0)} Meds are <strong class="ncp-blue ncp-doc-link">Pharmacy Packed:</strong> ${ncpFieldHtml("pharmacyPacked")}</p><p>Note: ${ncpFieldHtml("medicationManagementType")}</p></div>
      </div>
    </div>
    <div class="ncp-med-management">
      <div class="ncp-section-bar">MEDICATION MANAGEMENT</div>
      <div class="ncp-med-management-grid">
        <div class="ncp-med-intro"><em>The amount of assistance required to receive prescription medications, over the counter medications, or herbal supplements.</em></div>
        <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
        <div class="ncp-comm-head">Assistance Required<br />Caregiver Instruction<br />Who, How, When/How Often</div>
        <div class="ncp-med-left"><p><strong class="ncp-blue ncp-doc-link">${ncpCheckText("medicationManagementType", 0)} SELF-ADMINISTRATION</strong></p><p><strong class="ncp-blue ncp-doc-link">${ncpCheckText("medicationManagementType", 1)} SELF-ADMINISTRATION W/ ASSISTANCE</strong></p><p><strong class="ncp-blue ncp-doc-link">${ncpCheckText("medicationManagementType", 2)} MEDICATION ADMINISTRATION</strong></p><br /><p>Equipment: ${ncpFieldHtml("medicationManagementType")}</p><p>Type of Medication Management is Needed: ${ncpFieldHtml("medicationRoutes")}</p><div class="ncp-checkbox-list">${medicationRouteCheckboxHtml}</div></div>
        <div class="ncp-med-middle"><strong class="ncp-blue">Strengths and Abilities</strong>${ncpFieldParagraph("medicationStrengths")}</div>
        <div class="ncp-med-right"><b>Describe the reason the resident needs this amount of medication assistance:</b>${ncpFieldParagraph("medicationAssistanceNeeds")}<br />${ncpCaregiverBlock("medicationAssistance")}<br /><p>CG will follow the 5 rights of medication administration + 2 <strong>every time:</strong></p><ul><li>Right resident</li><li>Right medication</li><li>Right dose</li></ul></div>
      </div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 3 of 19</strong></div>
  </section>`;
  const ncpPageFourDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-four-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-section-bar">MEDICATION MANAGEMENT</div>
    <div class="ncp-page-four-grid">
      <div class="ncp-med-intro"><em>The amount of assistance required to receive prescription medications, over the counter medications, or herbal supplements.</em></div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Caregiver Instruction<br />Who, How, When/How Often</div>
      <div class="ncp-page-four-left"><p><strong>${ncpCheckText("medicationRoutes", 5)}&nbsp;&nbsp;Injections:</strong> ${ncpFieldHtml("injectionSupport")}</p><div class="ncp-checkbox-list">${injectionSupportCheckboxHtml}</div><div class="ncp-checkbox-list">${pageFourMedicationRouteCheckboxHtml}</div><p>${ncpCheckText("nurseDelegation", 0)} Requires <strong class="ncp-blue ncp-doc-link">Nurse Delegation</strong> ${ncpFieldHtml("nurseDelegation")}</p><div class="ncp-rn-block"><strong>RN Delegator</strong><ul><li><b>Name:</b></li><li><b>Phone:</b></li><li><b>FAX:</b></li><li><b>Email:</b></li></ul></div></div>
      <div class="ncp-page-four-middle">${ncpFieldBlock("medicationStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-four-right"><ul><li>Right route</li><li>Right time</li></ul><p>+ Right documentation</p><p>+ Follow strategy for resident's right to refuse</p><br /><p>CG is to follow Dr. orders, follow RND instructions; document in MAR and report significant changes, concerns/adverse reactions to Dr. immediately.</p>${ncpFieldParagraph("medicationAssistance")}</div>
      <div class="ncp-page-four-plan-title"><strong class="ncp-blue">Medication Plan When Resident is not in the AFH</strong></div>
      <div class="ncp-page-four-plan-middle">${ncpFieldBlock("medicationOutsideAfhStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-four-plan-right">${ncpCaregiverBlock("medicationOutsideAfh")}</div>
      <div class="ncp-page-four-plan-title"><strong class="ncp-blue">Medication Refusal Plan</strong></div>
      <div class="ncp-page-four-plan-middle">${ncpFieldBlock("medicationRefusalStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-four-plan-right">${ncpCaregiverBlock("medicationRefusalPlan")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 4 of 19</strong></div>
  </section>`;
  const ncpPageFiveDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-five-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-section-bar ncp-underlined-bar">HEALTH INDICATORS</div>
    <div class="ncp-page-five-health-grid">
      <div class="ncp-health-topic"><strong>Health Indicator Monitoring and Support</strong></div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-health-left"><div class="ncp-checkbox-list">${healthIndicatorCheckboxHtml}</div>${ncpFieldParagraph("healthIndicatorMonitoring")}</div>
      <div class="ncp-health-middle">${ncpFieldBlock("healthStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-health-right">${ncpCaregiverBlock("healthAssistance")}<p class="ncp-health-reporting"><strong>Monitoring/Reporting significant changes and/or concerns:</strong> ${ncpFieldHtml("monitoringReporting") || "Caregiver is to report concerns and significant changes immediately to relevant individuals (health Care provider POA, CM, etc.)."}</p></div>
      <div class="ncp-health-allergy-title"><strong>Allergies</strong></div>
      <div class="ncp-health-allergy-cell"><strong>Substance:</strong> ${ncpFieldHtml("allergySubstanceReaction")}</div>
      <div class="ncp-health-allergy-cell"><strong>Reaction:</strong> ${ncpFieldHtml("allergySubstanceReaction")}</div>
    </div>
    <div class="ncp-section-bar ncp-page-five-treatment-bar">TREATMENTS/PROGRAMS/THERAPIES</div>
    <div class="ncp-page-five-treatment-grid">
      <div class="ncp-comm-head">Treatment/Program<br />Therapy/Interventions</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-treatment-left"><strong>Type of Treatment:</strong><div class="ncp-checkbox-list">${treatmentTypeCheckboxHtml}</div>${ncpFieldParagraph("treatmentTypes")}</div>
      <div class="ncp-treatment-middle">${ncpFieldBlock("treatmentStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-treatment-right">${ncpCaregiverBlock("treatmentAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 5 of 19</strong></div>
  </section>`;
  const ncpPageSixDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-six-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-section-bar">TREATMENTS/PROGRAMS/THERAPIES</div>
    <div class="ncp-page-six-grid">
      <div class="ncp-comm-head">Treatment/Program<br />Therapy/Interventions</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-six-left"><div class="ncp-checkbox-list">${treatmentTherapyCheckboxHtml}</div>${ncpFieldParagraph("rangeOfMotionTherapies")}</div>
      <div class="ncp-page-six-middle">${ncpFieldBlock("treatmentStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-six-right">${ncpCaregiverBlock("treatmentAssistance")}</div>
      <div class="ncp-page-six-left"><strong>Programs Resident Requires/Attends:</strong><div class="ncp-checkbox-list">${treatmentProgramCheckboxHtml}</div>${ncpFieldParagraph("programsAttended")}</div>
      <div class="ncp-page-six-middle">${ncpFieldBlock("programsAttended", "Strengths and Abilities")}</div>
      <div class="ncp-page-six-right">${ncpCaregiverBlock("programsAttended")}</div>
      <div class="ncp-page-six-left"><strong>Interventions</strong><p>${ncpCheckText("meaningfulDay", 0)}&nbsp;&nbsp;&nbsp;&nbsp;Meaningful Day ${ncpFieldHtml("meaningfulDay")}</p></div>
      <div class="ncp-page-six-middle">${ncpFieldBlock("meaningfulDay", "Strengths and Abilities")}</div>
      <div class="ncp-page-six-right">${ncpCaregiverBlock("meaningfulDay")}</div>
      <div class="ncp-page-six-plan-title"><strong class="ncp-blue">Physical Enablers</strong></div>
      <div class="ncp-page-six-plan-middle">${ncpFieldBlock("physicalEnablers", "Strengths and Abilities")}</div>
      <div class="ncp-page-six-plan-right">${ncpCaregiverBlock("physicalEnablers")}</div>
      <div class="ncp-page-six-plan-title"><strong>TREATMENT/PROGRAM/THERAPY REFUSAL PLAN</strong></div>
      <div class="ncp-page-six-plan-middle">${ncpFieldBlock("treatmentRefusalPlan", "Strengths and Abilities")}</div>
      <div class="ncp-page-six-plan-right">${ncpCaregiverBlock("treatmentRefusalPlan")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 6 of 19</strong></div>
  </section>`;
  const ncpPageSevenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-seven-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-seven-grid">
      <div class="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      ${psychStatusRowsHtml}
      <div class="ncp-psych-middle"><strong>Resident Strengths And Abilities</strong><strong>Prefers To Do Independently</strong><strong>Preferences and Personal Goals</strong>${ncpFieldParagraph("sleepStrengths")}${ncpFieldParagraph("memoryStrengths")}</div>
      <div class="ncp-psych-right"><strong>Assistance Required</strong><strong>Who Will Provide, When And How</strong>${ncpFieldParagraph("sleepAssistance")}${ncpFieldParagraph("memoryAssistance")}${ncpFieldParagraph("decisionMaking")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 7 of 19</strong></div>
  </section>`;
  const ncpPageEightDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-eight-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-eight-grid">
      <div class="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      ${pageEightBehaviorRowsHtml}
      <div class="ncp-page-eight-support">${pageEightSupportHtml}</div>
      <div class="ncp-page-eight-programs">${pageEightProgramsHtml}</div>
      <div class="ncp-page-eight-middle-top">${ncpFieldBlock("behaviors", "Strengths and Abilities")}</div>
      <div class="ncp-page-eight-right-top">${ncpCaregiverBlock("behaviorAssistance")}</div>
      <div class="ncp-page-eight-middle-bottom">${ncpFieldBlock("specializedBehaviorProgramStrengths", "Program strengths / goals")}</div>
      <div class="ncp-page-eight-right-bottom">${ncpCaregiverBlock("specializedBehaviorProgramAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 8 of 19</strong></div>
  </section>`;
  const ncpPageNineDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-nine-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-nine-psych-grid">
      <div class="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-nine-interventions"><strong>Interventions</strong><p>${ncpCheckText("psychInterventions", 0)}&nbsp;&nbsp;&nbsp;&nbsp;Meaningful Day ${ncpFieldHtml("psychInterventions")}</p><p>${ncpCheckText("psychInterventions", 1)}&nbsp;&nbsp;&nbsp;&nbsp;Other:</p></div>
      <div class="ncp-page-nine-psych-middle">${ncpFieldBlock("psychInterventions", "Strengths and Abilities")}</div>
      <div class="ncp-page-nine-psych-right">${ncpCaregiverBlock("psychInterventionAssistance")}</div>
      <div class="ncp-page-nine-narrative"><strong>Narrative (optional) - What does a typical day look like?</strong>${ncpFieldParagraph("typicalDayNarrative")}</div>
    </div>
    <div class="ncp-page-nine-left-grid">
      <div class="ncp-section-bar">Left Alone</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-nine-left-title"><strong class="ncp-blue">Ability of Resident to Be Left Unattended</strong>${ncpFieldParagraph("leftAloneAbility")}</div>
      <div class="ncp-page-nine-left-middle">${ncpFieldBlock("leftAloneStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-nine-left-right">${ncpCaregiverBlock("leftAloneAssistance")}</div>
    </div>
    <div class="ncp-page-nine-universal-grid">
      <div class="ncp-section-bar">UNIVERSAL PRECAUTIONS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-nine-universal-left">${universalPrecautionHtml}${ncpFieldParagraph("universalPrecautions")}</div>
      <div class="ncp-page-nine-universal-middle">${ncpFieldBlock("universalPrecautionsStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-nine-universal-right"><p>Caregiver will always use latex/plastic gloves when in contact with any secretions to prevent spread of infection.</p><p>Thorough hand washing with soap will be done before and after gloving. Gloves will be put on and discarded at the end of each task. If the AFH provider orders these gloves they can be paid for through the medical coupon.</p>${ncpCaregiverBlock("universalPrecautionsAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 9 of 19</strong></div>
  </section>`;
  const ncpPageTenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-ten-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-ten-universal-grid">
      <div class="ncp-section-bar">UNIVERSAL PRECAUTIONS</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-ten-universal-left">${universalPrecautionPageTenHtml}${ncpFieldParagraph("universalPrecautions")}</div>
      <div class="ncp-page-ten-universal-middle">${ncpFieldBlock("universalPrecautionsStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-ten-universal-right">${ncpCaregiverBlock("universalPrecautionsAssistance")}</div>
    </div>
    <div class="ncp-page-ten-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-ten-functional"><strong>Resident functional limitations that impact ADL functioning:</strong>${ncpFieldParagraph("functionalLimitations")}</div>
      <div class="ncp-page-ten-ambulation"><strong>AMBULATION/MOBILITY</strong><p><strong>Locomotion in room and immediate living environment:</strong> ${ncpFieldHtml("locomotionRoom")}</p><p>${ncpCheckText("locomotionRoom", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("locomotionRoom", 1)} Supervision/Cueing</p><p>${ncpCheckText("locomotionRoom", 2)} Assistance Needed ${ncpCheckText("locomotionRoom", 3)} Totally Dependent</p><br /><p><strong>Locomotion outside of immediate living environment (including outdoors):</strong> ${ncpFieldHtml("locomotionOutside")}</p><p>${ncpCheckText("locomotionOutside", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("locomotionOutside", 1)} Supervision/Cueing</p><p>${ncpCheckText("locomotionOutside", 2)} Assistance Needed ${ncpCheckText("locomotionOutside", 3)} Totally Dependent</p><br /><p><strong>${ncpCheckText("fallRisks", 0)} Risks for falls</strong> ${ncpFieldHtml("fallRisks")}</p><p><strong>${ncpCheckText("fallRisks", 1)} Fall prevention plan:</strong></p><p><strong>Equipment/Supplies:</strong> ${ncpFieldHtml("mobilityEquipment")}</p><p><strong>Limitations:</strong> ${ncpFieldHtml("mobilityLimitations")}</p></div>
      <div class="ncp-page-ten-adl-middle"><strong class="ncp-blue">Strengths and Abilities</strong>${ncpFieldParagraph("mobilityStrengths")}<br /><br /><p><strong>Evacuation addressed under Evacuation Plan</strong></p></div>
      <div class="ncp-page-ten-adl-right">${ncpMonitoringCaregiverBlock("mobilityAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 10 of 19</strong></div>
  </section>`;
  const ncpPageElevenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-eleven-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-eleven-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-eleven-top-left"><p>${ncpCheckText("bedroomDoorLock", 0)} <strong class="ncp-blue">Resident chooses bedroom door lock</strong> ${ncpFieldHtml("bedroomDoorLock")}</p><br /><p><strong>Equipment/Supplies:</strong> ${ncpFieldHtml("mobilityEquipment")}</p><p class="ncp-nested-checkbox">Vendor:</p><br /><p><strong>Limitations:</strong> ${ncpFieldHtml("mobilityLimitations")}</p></div>
      <div class="ncp-page-eleven-top-middle">${ncpFieldBlock("mobilityStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-eleven-top-right">${ncpCaregiverBlock("mobilityAssistance")}</div>
      <div class="ncp-page-eleven-transfer-left"><strong>BED MOBILITY/TRANSFER</strong><p><em>Transfer includes moving between bed, chair, wheelchair, standing position - excludes to/from bath/toilet. Bed mobility includes the ability to move in bed.</em></p><p>${ncpFieldHtml("transferLevel")}</p><p>${ncpCheckText("transferLevel", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("transferLevel", 1)} Supervision/Cueing</p><p>${ncpCheckText("transferLevel", 2)} Assistance Needed ${ncpCheckText("transferLevel", 3)} Totally Dependent</p><br /><p>${ncpCheckText("skinCarePositioning", 0)} Skin care required due to inability to position self: ${ncpFieldHtml("skinCarePositioning")}</p><p class="ncp-nested-checkbox">Equipment/Supplies: ${ncpFieldHtml("transferEquipment")}</p><br /><p>${ncpCheckText("turningRepositioning", 0)} Turning and Repositioning needed - Frequency: ${ncpFieldHtml("turningRepositioning")}</p><br /><p>${ncpCheckText("transferSafetyPlan", 0)} Safety assessment, alternatives explored; how to keep resident safe: ${ncpFieldHtml("transferSafetyPlan")}</p><p class="ncp-nested-checkbox">${ncpCheckText("transferSafetyPlan", 1)} Risks for falls: ${ncpFieldHtml("fallRisks")}</p><p class="ncp-nested-checkbox">${ncpCheckText("transferSafetyPlan", 2)} Fall prevention plan</p></div>
      <div class="ncp-page-eleven-transfer-middle"><strong class="ncp-blue">Strengths and Abilities</strong>${ncpFieldParagraph("transferStrengths")}</div>
      <div class="ncp-page-eleven-transfer-right">${ncpMonitoringCaregiverBlock("transferAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 11 of 19</strong></div>
  </section>`;
  const ncpPageTwelveDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-twelve-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-twelve-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-twelve-device-left"><p>${ncpCheckText("transferSafetyPlan", 0)} Safety plan ${ncpFieldHtml("transferSafetyPlan")}</p><p>${ncpCheckText("medicalDevices", 0)} <strong class="ncp-blue">Medical Device(s):</strong> ${ncpFieldHtml("medicalDevices")}</p><p class="ncp-nested-checkbox">${ncpCheckText("medicalDevices", 1)} Enablers:</p><p class="ncp-nested-checkbox">${ncpCheckText("medicalDevices", 2)} <strong>Hoyer Lift</strong></p><p class="ncp-nested-checkbox">${ncpCheckText("medicalDevices", 3)} <strong>Transfer Pole</strong></p><p class="ncp-nested-checkbox">${ncpCheckText("medicalDevices", 4)} Medical Device/Enabler Risk Assessment:</p><p>${ncpCheckText("medicalDevices", 5)} Nighttime care needs</p><br /><p>Equipment/supplies: ${ncpFieldHtml("transferEquipment")}</p></div>
      <div class="ncp-page-twelve-device-middle">${ncpFieldBlock("transferStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-twelve-device-right">${ncpCaregiverBlock("transferAssistance")}</div>
      <div class="ncp-page-twelve-eating-left"><strong>EATING</strong><p><em>How the Resident eats and drinks (regardless of skill). Includes intake of nourishment by other means (e.g., tube feeding, total parenteral nutrition)</em></p><p>${ncpFieldHtml("eatingLevel")}</p><p>${ncpCheckText("eatingLevel", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("eatingLevel", 1)} Supervision/Cueing</p><p>${ncpCheckText("eatingLevel", 2)} Assistance Needed ${ncpCheckText("eatingLevel", 3)} Totally Dependent</p><br /><p><strong>${ncpCheckText("specialDiet", 0)} Special Diet/Supplements:</strong> ${ncpFieldHtml("specialDiet")}</p><p><strong>${ncpCheckText("specialDiet", 1)} Eating Habits:</strong> ${ncpFieldHtml("eatingLevel")}</p><p><strong>${ncpCheckText("specialDiet", 2)} Food Allergies:</strong> ${ncpFieldHtml("allergies")}</p><p><strong>${ncpCheckText("specialDiet", 3)} Equipment/Supplies/Procedures:</strong> ${ncpFieldHtml("eatingEquipment")}</p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-twelve-eating-middle"><strong class="ncp-blue">Strengths and Abilities</strong>${ncpFieldParagraph("eatingStrengths")}</div>
      <div class="ncp-page-twelve-eating-right">${ncpMonitoringCaregiverBlock("eatingAssistance")}</div>
      <div class="ncp-page-twelve-toileting-left"><strong>TOILETING/CONTINENCE ISSUES</strong><p><em>How individual uses the toilet room (or commode, bed pan, urinal); transfers on/off toilet, cleanses, changes incontinence pads, manages ostomy or catheter, adjusts clothes</em></p>${ncpFieldParagraph("toiletingLevel")}${ncpFieldParagraph("continenceIssues")}</div>
      <div class="ncp-page-twelve-toileting-middle"><strong class="ncp-blue">Strengths and Abilities</strong>${ncpFieldParagraph("toiletingStrengths")}</div>
      <div class="ncp-page-twelve-toileting-right">${ncpMonitoringCaregiverBlock("toiletingAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 12 of 19</strong></div>
  </section>`;
  const ncpAdlMonitoringHtml = (key) => ncpMonitoringCaregiverBlock(key);
  const ncpPageThirteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-thirteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-thirteen-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-thirteen-toileting-left"><p>${ncpCheckText("toiletingLevel", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("toiletingLevel", 1)} Supervision/Cueing</p><p>${ncpCheckText("toiletingLevel", 2)} Assistance Needed ${ncpCheckText("toiletingLevel", 3)} Totally Dependent</p><p><strong>Frequency/How Often:</strong> ${ncpFieldHtml("toiletingLevel")}</p><br /><p>Continence Issues: ${ncpFieldHtml("continenceIssues")}</p><p><strong>${ncpCheckText("continenceIssues", 0)} Bladder Incontinence</strong></p><p><strong>${ncpCheckText("continenceIssues", 1)} Bowel Incontinence</strong></p><p><strong>${ncpCheckText("continenceIssues", 2)} Skin care due to bowel/bladder incontinence</strong></p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-thirteen-toileting-middle">${ncpFieldBlock("toiletingStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-thirteen-toileting-right">${ncpCaregiverBlock("toiletingAssistance")}</div>
      <div class="ncp-page-thirteen-dressing-left"><strong>DRESSING</strong><p><em>How individual puts on, fastens, and takes off all items of street clothing, including donning/removing prosthesis</em></p><p>${ncpCheckText("dressing", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("dressing", 1)} Supervision/Cueing</p><p>${ncpCheckText("dressing", 2)} Assistance Needed ${ncpCheckText("dressing", 3)} Totally Dependent</p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p>${ncpFieldParagraph("dressing")}</div>
      <div class="ncp-page-thirteen-dressing-middle">${ncpFieldBlock("dressingStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-thirteen-dressing-right">${ncpAdlMonitoringHtml("dressingAssistance")}</div>
      <div class="ncp-page-thirteen-hygiene-left"><strong>PERSONAL HYGIENE</strong><span> - </span><em>How individual maintains personal hygiene, including combing hair, brushing teeth, shaving, applying makeup, washing/drying face, hands, and perineum</em><p>${ncpCheckText("personalHygieneLevel", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("personalHygieneLevel", 1)} Supervision/Cueing</p><p>${ncpCheckText("personalHygieneLevel", 2)} Assistance Needed ${ncpCheckText("personalHygieneLevel", 3)} Totally Dependent</p>${ncpFieldParagraph("personalHygiene")}</div>
      <div class="ncp-page-thirteen-hygiene-middle">${ncpFieldBlock("personalHygieneStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-thirteen-hygiene-right">${ncpAdlMonitoringHtml("personalHygieneAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 13 of 19</strong></div>
  </section>`;
  const ncpPageFourteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-fourteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-fourteen-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-fourteen-hygiene-left"><p>${ncpCheckText("personalHygieneCheckboxes", 0)} Own teeth ${ncpCheckText("personalHygieneCheckboxes", 1)} Partials ${ncpCheckText("personalHygieneCheckboxes", 2)} Dentures</p><p>${ncpCheckText("personalHygieneCheckboxes", 3)} <strong>Oral Hygiene</strong> (including dentures): ${ncpFieldHtml("personalHygiene")}</p><p class="ncp-nested-checkbox">${ncpCheckText("personalHygieneCheckboxes", 4)} Flossing</p><p class="ncp-nested-checkbox">${ncpCheckText("personalHygieneCheckboxes", 5)} Brushing</p><p class="ncp-nested-checkbox">${ncpCheckText("personalHygieneCheckboxes", 6)} Soaking</p><p>${ncpCheckText("personalHygieneCheckboxes", 7)} <strong>Hair Care:</strong></p><p>${ncpCheckText("personalHygieneCheckboxes", 8)} <strong>Menses Care:</strong></p><br /><p><strong>When/how often:</strong></p><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-fourteen-hygiene-middle">${ncpFieldBlock("personalHygieneStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-fourteen-hygiene-right">${ncpAdlMonitoringHtml("personalHygieneAssistance")}</div>
      <div class="ncp-page-fourteen-bathing-left"><strong>BATHING</strong><p><em>How individual takes full-body shower, sponge bath, and transfer in/out of tub/shower</em></p><p>${ncpCheckText("bathing", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("bathing", 1)} Supervision/Cueing</p><p>${ncpCheckText("bathing", 2)} Assistance Needed ${ncpCheckText("bathing", 3)} Totally Dependent</p><br /><p><strong>When/how often:</strong></p><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p>${ncpFieldParagraph("bathing")}</div>
      <div class="ncp-page-fourteen-bathing-middle">${ncpFieldBlock("bathingStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-fourteen-bathing-right">${ncpAdlMonitoringHtml("bathingAssistance")}</div>
      <div class="ncp-page-fourteen-foot-left"><strong>FOOT CARE</strong><p>${ncpCheckText("footCare", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("footCare", 1)} Supervision/Cueing</p><p>${ncpCheckText("footCare", 2)} Assistance Needed ${ncpCheckText("footCare", 3)} Totally Dependent</p><br /><p>${ncpCheckText("footCareDetails", 0)} <strong class="ncp-blue">Foot Care:</strong> How Often: ${ncpFieldHtml("footCare")}</p></div>
      <div class="ncp-page-fourteen-foot-middle">${ncpFieldBlock("footCareStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-fourteen-foot-right">${ncpAdlMonitoringHtml("footCareAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 14 of 19</strong></div>
  </section>`;
  const ncpPageFifteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-fifteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-fifteen-adl-grid">
      <div class="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
      <div class="ncp-page-fifteen-foot-left"><p>${ncpCheckText("footCareDetails", 0)} <strong class="ncp-blue">Diabetic Foot Care</strong></p><p>${ncpCheckText("footCareDetails", 1)} Nail Care</p><p><strong>When/how often:</strong> ${ncpFieldHtml("footCare")}</p><br /><p>${ncpCheckText("footCareDetails", 2)} <strong class="ncp-blue">Home Health Agency:</strong></p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-fifteen-foot-middle">${ncpFieldBlock("footCareStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-fifteen-foot-right">${ncpCaregiverBlock("footCareAssistance")}</div>
      <div class="ncp-page-fifteen-skin-left"><strong>SKIN CARE</strong><p>${ncpCheckText("skinCare", 0)} Independent&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${ncpCheckText("skinCare", 1)} Supervision/Cueing</p><p>${ncpCheckText("skinCare", 2)} Assistance Needed ${ncpCheckText("skinCare", 3)} Totally Dependent</p><br /><p>${ncpCheckText("skinCareDetails", 0)} <strong>Skin Care</strong> - How Often: ${ncpFieldHtml("skinCare")}</p><p class="ncp-nested-checkbox">${ncpCheckText("skinCareDetails", 1)} Status:</p><p>${ncpCheckText("skinCareDetails", 2)} <strong>Skin Problems</strong> - Describe:</p><p class="ncp-nested-checkbox">${ncpCheckText("skinCareDetails", 3)} Status:</p><p>${ncpCheckText("skinCareDetails", 4)} <strong>Pressure Injuries</strong> - Describe:</p><p>${ncpCheckText("skinCareDetails", 5)} <strong>Dressing Changes</strong> - How Often:</p><p class="ncp-nested-checkbox">${ncpCheckText("skinCareDetails", 6)} <strong>Nurse Delegated</strong></p><br /><p><strong>When/how often:</strong></p><br /><p>${ncpCheckText("skinCareDetails", 7)} <strong class="ncp-blue">Home Health Agency:</strong></p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-fifteen-skin-middle">${ncpFieldBlock("skinCareStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-fifteen-skin-right">${ncpCaregiverBlock("skinCareAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 15 of 19</strong></div>
  </section>`;
  const ncpPageSixteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-sixteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-sixteen-iadl-grid">
      <div class="ncp-section-bar">INSTRUMENTAL ACTIVITIES OF DAILY LIVING</div>
      <div class="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
      <div class="ncp-comm-head">Assistance Required<br />Who Will Provide, When And How</div>
      <div class="ncp-page-sixteen-finances-left"><strong>MANAGING FINANCES</strong><p>${ncpCheckText("finances", 0)} Independent ${ncpCheckText("finances", 1)} Assistance Needed ${ncpCheckText("finances", 2)} Dependent</p><br /><p>${ncpCheckText("finances", 3)} <strong>Who Manages Finances:</strong> ${ncpFieldHtml("finances")}</p><p>${ncpCheckText("finances", 4)} <strong>Who Manages Financial Records:</strong></p><br /><p>${ncpCheckText("finances", 5)} Payee Name and Contact information:</p></div>
      <div class="ncp-page-sixteen-finances-middle"><strong class="ncp-blue">Strengths and Abilities</strong><p><strong>Prefers to do independently:</strong></p>${ncpFieldParagraph("financesStrengths")}</div>
      <div class="ncp-page-sixteen-finances-right">${ncpCaregiverBlock("financesAssistance")}</div>
      <div class="ncp-page-sixteen-shopping-left"><strong>SHOPPING</strong><p>${ncpCheckText("shopping", 0)} Independent ${ncpCheckText("shopping", 1)} Assistance Needed ${ncpCheckText("shopping", 2)} Dependent</p><br /><p>${ncpCheckText("shopping", 3)} <strong class="ncp-blue">Special transportation needs:</strong></p><p><strong>How often/when:</strong> ${ncpFieldHtml("shopping")}</p><br /><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-sixteen-shopping-middle">${ncpFieldBlock("shoppingStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-sixteen-shopping-right">${ncpCaregiverBlock("shoppingAssistance")}</div>
      <div class="ncp-page-sixteen-transport-left"><strong>TRANSPORTATION</strong><p>${ncpCheckText("transportation", 0)} Independent ${ncpCheckText("transportation", 1)} Assistance Needed ${ncpCheckText("transportation", 2)} Dependent</p><br /><p>${ncpCheckText("transportation", 3)} <strong>Medical services:</strong> ${ncpFieldHtml("transportation")}</p><p>${ncpCheckText("transportation", 4)} <strong class="ncp-blue">Special transportation needs:</strong></p><p>${ncpCheckText("transportation", 5)} <strong>Escort Required</strong></p><p><strong>How often/when:</strong></p><p><strong>Equipment/Supplies/Procedures:</strong></p><p><strong>Limitations:</strong></p></div>
      <div class="ncp-page-sixteen-transport-middle">${ncpFieldBlock("transportationStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-sixteen-transport-right">${ncpCaregiverBlock("transportationAssistance")}</div>
      <div class="ncp-page-sixteen-social-left"><strong>ACTIVITIES/SOCIAL</strong><p><em>Social/Cultural considerations, traditions, or preferences</em></p><p>See Activities/Social details on the following page.</p></div>
      <div class="ncp-page-sixteen-social-middle">${ncpFieldBlock("activitiesSocialStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-sixteen-social-right">${ncpCaregiverBlock("activitiesSocialAssistance")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 16 of 19</strong></div>
  </section>`;
  const ncpPageSeventeenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-seventeen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-seventeen-grid">
      <div class="ncp-page-seventeen-social-left"><p>${ncpCheckText("activitiesSocialLevel", 0)} Independent ${ncpCheckText("activitiesSocialLevel", 1)} Assistance Needed ${ncpCheckText("activitiesSocialLevel", 2)} Dependent</p><br /><p>${ncpCheckText("activitiesSocial", 0)} Interests/Activities/Religious Activities: See Activity Narrative below.</p><p>${ncpCheckText("activitiesSocial", 1)} Social/Cultural Traditions/Preferences: See Activity Narrative below.</p><p>${ncpCheckText("activitiesSocial", 2)} Family/Friends/Relationships:</p><p>${ncpCheckText("activitiesSocial", 3)} Employment Support:</p><p>${ncpCheckText("activitiesSocial", 4)} Clubs/Groups/Day Health:</p><p>${ncpCheckText("activitiesSocial", 5)} Special Arrangements: See assistance column.</p><p>${ncpCheckText("activitiesSocial", 6)} Participation Issues:</p><br /><p>Emergency Numbers: <strong><em>See face sheet in resident binder</em></strong></p></div>
      <div class="ncp-page-seventeen-social-middle">${ncpFieldBlock("activitiesSocialStrengths", "Strengths and Abilities")}</div>
      <div class="ncp-page-seventeen-social-right"><p><strong class="ncp-blue">Special Arrangements:</strong> ${ncpCompactFieldHtml("specialArrangements", 80)}</p><br /><p><strong class="ncp-blue">Participation Assistance:</strong> See Activity Narrative below.</p></div>
      <div class="ncp-page-seventeen-preferences-title"><strong>ACTIVITY PREFERENCES AT A GLANCE</strong></div>
      <div class="ncp-page-seventeen-preferences-left">${["Reading book and/or magazines", "Listening to audio books and/or podcasts", "Storytelling", "Phone conversation/visiting", "Reminiscing", "Current events", "Discussion group", "Bible study or church", "Visitors"].map((label, index) => ncpCheckLine("activityPreferenceCheckboxes", index, label)).join("")}</div>
      <div class="ncp-page-seventeen-preferences-middle">${["Gardening", "Outings with family or provider", "Visiting zoos and/ or spending time with pets and animals", "Exercises/range of motion", "Therapeutic Walking", "Cooking or baking", "House chore activities", "Watching TV, movies, or favorite shows"].map((label, index) => ncpCheckLine("activityPreferenceCheckboxes", index + 9, label)).join("")}</div>
      <div class="ncp-page-seventeen-preferences-right">${["Parties and social gatherings", "Arts and crafts", "Table games, Bingo, cards, puzzles", "Beauty time, beautician visit", "Music appreciation/therapy/singing", "Employment support", "Community Integration", "Other:", "Other:"].map((label, index) => ncpCheckLine("activityPreferenceCheckboxes", index + 17, label)).join("")}</div>
      <div class="ncp-page-seventeen-narrative"><strong>ACTIVITY NARRATIVE:</strong>${ncpFieldParagraph("activityNarrative")}</div>
      <div class="ncp-page-seventeen-smoking-left"><strong>SMOKING</strong><p>${ncpCheckText("smoking", 0)} Resident Smokes ${ncpFieldHtml("smoking")}</p><p>${ncpCheckText("smoking", 1)} Safety Concerns:</p><p>${ncpCheckText("smoking", 2)} Smoking policy reviewed.</p><br /><p>Storage of Cigarettes/lighter:</p></div>
      <div class="ncp-page-seventeen-smoking-middle">${ncpFieldBlock("smoking", "Strengths and Abilities")}</div>
      <div class="ncp-page-seventeen-smoking-right">${ncpCaregiverBlock("smoking")}</div>
    </div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 17 of 19</strong></div>
  </section>`;
  const ncpPageEighteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-eighteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-eighteen-grid">
      <div class="ncp-page-eighteen-case-left"><strong>CASE MANAGEMENT</strong><p>${ncpCheckText("caseManagement", 0)} Resident receives Case Management</p><br /><p>Name: ${ncpFieldHtml("caseManagement")}</p><p>Agency:</p><p>Phone:</p><p>Email:</p><p>FAX:</p></div>
      <div class="ncp-page-eighteen-case-middle">${ncpFieldBlock("caseManagement", "Case manager notes")}</div>
      <div class="ncp-page-eighteen-case-right"><p>CM will be contacted when: ${ncpFieldHtml("cmContactedWhen")}</p><ul><li>The resident needs assistive device or other services to meet their needs</li><li>When the provider need help with the care plan</li><li>Significant changes with the condition/needs that necessitate changes with the care plan</li></ul></div>
      <div class="ncp-page-eighteen-other"><strong>OTHER ISSUES/CONCERNS/PROBLEMS</strong>${ncpFieldParagraph("otherIssues")}</div>
      <div class="ncp-page-eighteen-review-left"><strong class="ncp-blue">NEGOTIATED CARE PLAN REVIEW</strong>${ncpFieldParagraph("reviewSchedule")}</div>
      <div class="ncp-page-eighteen-review-middle">${ncpFieldParagraph("residentParticipation")}</div>
      <div class="ncp-page-eighteen-review-right">${ncpFieldParagraph("reviewRevisionSchedule")}</div>
    </div>
    <div class="ncp-page-eighteen-abbreviations"><strong>Abbreviations used in this NCP:</strong><div><p>ADL= Activities of Daily Living</p><p>AFH = Adult Family Home</p><p>CG = Caregiver</p><p>Dr. = Doctor</p></div><div><p>DPOA = Durable Power of Attorney</p><p>D/t = due to</p><p>Hx = history</p><p>MAR = medication assistance record</p></div><div><p>MD = medical doctor</p><p>PCP = primary care physician</p><p>PRN = As needed</p><p>RND = Register Nurse Delegator</p></div><div><p>W/c= Wheelchair</p></div><p>${ncpFieldHtml("abbreviations")}</p></div>
    <div class="ncp-page-eighteen-approval"><h3>Negotiated Care Plan Review and Approval</h3><strong>DATE OF ORIGINAL PLAN:</strong> ${ncpFieldHtml("reviewDates")}<p>Dates of Review/<span class="ncp-blue">Revision</span>: ${ncpFieldHtml("reviewDates")}</p></div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 18 of 19</strong></div>
  </section>`;
  const ncpPageNineteenApprovers = [
    { approver: `<strong class="ncp-blue">PROVIDER</strong><p>${ncpFieldHtml("approvingPerson")}</p>`, signature: ncpSignatureCellHtml("provider", "providerSignature") },
    { approver: "&nbsp;", signature: "" },
    { approver: "&nbsp;", signature: "" },
    { approver: "&nbsp;", signature: "" },
    { approver: `<strong class="ncp-blue">RESIDENT</strong><p>${ncpFieldHtml("residentName")}</p>`, signature: ncpSignatureCellHtml("resident", "residentSignature") },
    { approver: "&nbsp;", signature: "" },
    { approver: "&nbsp;", signature: "" },
    { approver: "&nbsp;", signature: "" },
    { approver: `<strong>RESIDENT REPRESENTATIVE</strong><p>${ncpFieldHtml("residentRepresentativeSignature").replace(/Digitally signed by.*$/s, "").trim()}</p>`, signature: ncpSignatureCellHtml("residentRepresentative", "residentRepresentativeSignature") },
    { approver: "&nbsp;", signature: "" },
  ];
  const ncpPageNineteenRows = ncpPageNineteenApprovers.map((row) =>
    `<div class="ncp-page-nineteen-approver">${row.approver || "&nbsp;"}</div><div class="ncp-page-nineteen-signature">${row.signature || ""}</div><div class="ncp-page-nineteen-signature"></div>`,
  ).join("");
  const ncpPageNineteenDocumentHtml = `<section class="ncp-preview-page-sheet ncp-page-nineteen-sheet">
    <div class="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
    <div class="ncp-page-nineteen-grid">
      <div class="ncp-page-nineteen-involved"><strong class="ncp-blue">INVOLVED IN NCP DEVELOPMENT</strong><p>${ncpCheckText("involvedInDevelopment", 0)} Resident</p><p>${ncpCheckText("involvedInDevelopment", 1)} Resident Representative</p><p>${ncpCheckText("involvedInDevelopment", 2)} Parent</p><p>${ncpCheckText("involvedInDevelopment", 3)} Health Professional</p><p>${ncpCheckText("involvedInDevelopment", 4)} Other: ${ncpFieldHtml("involvedInDevelopment")}</p><p>${ncpCheckText("involvedInDevelopment", 5)} Other:</p><p>${ncpCheckText("involvedInDevelopment", 6)} Other:</p></div>
      <div class="ncp-page-nineteen-head">PERSON APPROVING PLAN</div>
      <div class="ncp-page-nineteen-head">SIGNATURE <span>DATE*</span></div>
      <div class="ncp-page-nineteen-head">SIGNATURE <span>DATE*</span></div>
      ${ncpPageNineteenRows}
      <div class="ncp-page-nineteen-verbal">${ncpCheckText("residentVerbalAgreement", 0)} Resident verbally agreed to NCP - Date: ${ncpFieldHtml("residentVerbalAgreement")}</div>
      <div class="ncp-page-nineteen-sent">${ncpCheckText("sentToDshs", 0)} NCP sent to DSHS CM on: ${ncpFieldHtml("sentToDshs")}</div>
      <div class="ncp-page-nineteen-recommendations"><strong class="ncp-blue">Resident Recommendations:</strong>${ncpFieldParagraph("residentRecommendations")}</div>
    </div>
    <div class="ncp-page-nineteen-note"><strong>*</strong>The person signing writes <u>the date they actually read and agreed to the plan</u>. If the participant has verbally agreed to the plan, the provider should note below: (a) the name and role of the participant; (b) the date the participant had the plan to read to them; and (c) what if any changes the participant recommended for the plan. ${ncpFieldHtml("signatureNote")}</div>
    <div class="ncp-page-footer"><span>Residents Name: ${escapeHtml(negotiatedFieldValue("residentName"))}</span><strong>Page 19 of 19</strong></div>
  </section>`;
  const ncpDocumentPagesHtml = [
    ncpPageOneDocumentHtml,
    ncpPageTwoDocumentHtml,
    ncpPageThreeDocumentHtml,
    ncpPageFourDocumentHtml,
    ncpPageFiveDocumentHtml,
    ncpPageSixDocumentHtml,
    ncpPageSevenDocumentHtml,
    ncpPageEightDocumentHtml,
    ncpPageNineDocumentHtml,
    ncpPageTenDocumentHtml,
    ncpPageElevenDocumentHtml,
    ncpPageTwelveDocumentHtml,
    ncpPageThirteenDocumentHtml,
    ncpPageFourteenDocumentHtml,
    ncpPageFifteenDocumentHtml,
    ncpPageSixteenDocumentHtml,
    ncpPageSeventeenDocumentHtml,
    ncpPageEighteenDocumentHtml,
    ncpPageNineteenDocumentHtml,
  ];
  const negotiatedTemplateDocumentHtml = makeNcpCheckboxesFillable(ncpDocumentPagesHtml.join(""));

  const applyPreliminaryAiAction = (nextDescription) => {
    setPreliminaryPlanHistory((current) => [...current, planDescription]);
    setPreliminaryPlanDescription(nextDescription);
  };

  const refinePreliminaryPlan = () => {
    applyPreliminaryAiAction(`${planDescription}<p><strong>AI refinement:</strong> Clarified care priorities, tightened language, and aligned observations with assessment, MaG, onboarding, and daily note data.</p>`);
  };

  const summarizePreliminaryPlan = () => {
    applyPreliminaryAiAction("<p><strong>Summary:</strong> Resident needs calm routines, standby transfer support, medication prompts, family contact coordination, and continued observation of discrepancies before final negotiated care planning.</p>");
  };

  const formatPreliminaryPlan = () => {
    applyPreliminaryAiAction(`
      <h3>Preliminary Service Plan</h3>
      <p>${planDescription.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</p>
      <h4>Resident Profile and Responsible Parties</h4>
      <p>Confirm resident demographics, move-in date, responsible party, DPOA/DPOH/guardian/POA/POH status, family history, close friends, and significant relatives.</p>
      <h4>Personal History, Religion, Interests, and Social Preferences</h4>
      <p>Document celebrations, special events, religious participation, church visits, dietary practices, hobbies, entertainment, music, reading, group preferences, outdoor activities, and pets.</p>
      <h4>Food Preferences and Dietary Needs</h4>
      <p>Capture meal times, favorite snacks, dining location preference, alcohol permission, physician diet orders, favorite foods, dislikes, beverages, meats, vegetables, fruits, and comfort foods.</p>
      <h4>Communication, Sensory, and Safety</h4>
      <p>Document speech, hearing, eyesight, phone use, bedroom lock/privacy preference, allergies, evacuation level, and evacuation assistance instructions.</p>
      <h4>Medication, Treatments, and Health Monitoring</h4>
      <p>Record medication management level, nurse delegation, injections, oxygen, pain, vital signs, blood sugar checks, weight monitoring, wound care, home health, PT/OT/speech/RN, and hospice.</p>
      <h4>Mental Capacity, Attitude, Behavior, and Interventions</h4>
      <p>Summarize orientation, memory, mood, behavior risks, wandering/elopement, psycho-pharmacological medications, door alarms, location checks, and intervention strategies.</p>
      <h4>Mobility, Transfers, ADLs, Continence, and Equipment</h4>
      <ul>
        <li>Ambulation, transfer, bed mobility, restorative support, repositioning, and paralysis status.</li>
        <li>Continence, toileting, dressing, bathing, skin care, grooming, mouth care, eating, fluids, and hydration plan.</li>
        <li>Equipment such as walker, hospital bed, oxygen, bath bench, gait belt, lift, transfer board, blood pressure machine, and O2 saturation meter.</li>
      </ul>
      <h4>Goals, Preferences, and Refusal Plan</h4>
      <p>Document resident goals, preferences, medication refusal plan, other refusal behaviors, and how staff should respond.</p>
    `);
  };

  const undoPreliminaryPlanAction = () => {
    setPreliminaryPlanHistory((current) => {
      if (!current.length) return current;
      const previous = current[current.length - 1];
      setPreliminaryPlanDescription(previous);
      return current.slice(0, -1);
    });
  };

  const updateNegotiatedTemplateField = (key, value) => {
    setNegotiatedPlanPayload((current) => updateNegotiatedPayloadField(ensureNegotiatedPlanPayloadFields(current, negotiatedPlanTemplate), key, (field) => ({ ...field, value })));
  };

  const updateNegotiatedTemplateCheck = (key, index, checked) => {
    setNegotiatedPlanPayload((current) => updateNegotiatedPayloadField(ensureNegotiatedPlanPayloadFields(current, negotiatedPlanTemplate), key, (field) => {
      const checkCount = Math.max(field.checks?.length || 0, index + 1);
      const checks = Array.from({ length: checkCount }, (_, checkIndex) => (
        field.checks?.[checkIndex] || { index: checkIndex, checked: false }
      ));
      checks[index] = { ...checks[index], checked };
      return { ...field, checks };
    }));
  };

  const applyNegotiatedAiAction = (transform) => {
    const snapshot = cloneNegotiatedPlanPayload(negotiatedPlanPayload);
    setNegotiatedPlanHistory((current) => [...current, snapshot]);
    setNegotiatedPlanPayload((current) => ({
      ...current,
      pages: (current.pages || []).map((page) => ({
        ...page,
        sections: (page.sections || []).map((section) => ({
          ...section,
          fields: (section.fields || []).map((field) => ({
            ...field,
            value: transform({
              ...field,
              value: field.value || negotiatedTemplateDefaults[field.key] || "",
            }, section),
          })),
        })),
      })),
    }));
  };

  const improveNegotiatedPlan = () => {
    applyNegotiatedAiAction((field) => `${field.value}${field.value ? "\n\n" : ""}AI improvement: Align this field with assessment, preliminary plan, MaG, onboarding, daily notes, and annual progress data before final approval.`);
  };

  const formatNegotiatedPlan = () => {
    applyNegotiatedAiAction((field) => String(field.value || "").replace(/\s+/g, " ").trim());
  };

  const undoNegotiatedPlanAction = () => {
    setNegotiatedPlanHistory((current) => {
      if (!current.length) return current;
      const previous = current[current.length - 1];
      setNegotiatedPlanPayload(previous);
      return current.slice(0, -1);
    });
  };

  const ncpPayloadForSave = (payload) => ({
    ...ensureNegotiatedPlanPayloadFields(payload, negotiatedPlanTemplate),
    templateKey: negotiatedPlanTemplate?.templateKey || payload.templateKey || "afh-hcs-ncp",
    version: negotiatedPlanTemplate?.version || payload.version || "AFH-HCS-NCP-3.12.2024",
    latestGeneratedAt: payload.latestGeneratedAt || payload.generatedAt || new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
    sourceData: {
      ...(payload.sourceData || {}),
      assessmentExtraction: ncpExtractionDocument || payload.sourceData?.assessmentExtraction || null,
      dailyNotes: ncpDailyNotes,
      weeklyReports: ncpWeeklyReports,
      dailyNotesAnalysis: ncpDailyAnalysis,
      dailyNoteGenerationRules: ncpDailyNoteGenerationRules,
    },
  });

  const saveNegotiatedPlan = async (nextPayload = negotiatedPlanPayload, successMessage = "Negotiated care plan draft saved.") => {
    setNegotiatedPlanSaving(true);
    setNegotiatedPlanError("");
    setNegotiatedPlanSuccess("");
    try {
      if (!assessmentProcessId) {
        throw new Error("The negotiated care plan can be saved after opening the assessment process.");
      }
      const payload = ncpPayloadForSave(nextPayload);
      const savedRecord = await careService.assessmentRecords.saveForProcess(
        assessmentProcessId,
        "negotiated-plan",
        { payload },
      );
      const savedPayload = savedRecord?.payload && typeof savedRecord.payload === "object"
        ? savedRecord.payload
        : payload;
      setNegotiatedPlanPayload(ensureNegotiatedPlanPayloadFields(savedPayload, negotiatedPlanTemplate));
      setRecord((current) => ({ ...defaultNegotiatedPlanRecord(), ...current, ...savedPayload, payload: savedPayload }));
      setNegotiatedPlanSuccess(successMessage);
      return savedPayload;
    } catch (err) {
      setNegotiatedPlanError(err?.response?.data?.message || err?.message || "Unable to save negotiated care plan.");
      return null;
    } finally {
      setNegotiatedPlanSaving(false);
    }
  };

  const openDigitalSignatureModal = () => {
    const signerName = personName(user);
    setDigitalSignatureHasInk(false);
    setDigitalSignatureForm({
      role: "provider",
      signerName: signerName || "",
      signerTitle: "Care Provider",
      note: "",
      includeLocation: true,
      acknowledged: false,
    });
    setDigitalSignatureOpen(true);
    window.setTimeout(prepareSignatureCanvas, 0);
  };

  const updateDigitalSignatureForm = (field, value) => {
    setDigitalSignatureForm((current) => ({ ...current, [field]: value }));
  };

  const prepareSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    canvas.height = Math.max(1, Math.floor(rect.height * ratio));
    const context = canvas.getContext("2d");
    if (!context) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, rect.width, rect.height);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 4;
    context.strokeStyle = "#0b63ce";
  };

  const clearDigitalSignatureInk = () => {
    prepareSignatureCanvas();
    signatureDrawingRef.current = false;
    signatureLastPointRef.current = null;
    setDigitalSignatureHasInk(false);
  };

  const signaturePointFromEvent = (event) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const startDigitalSignatureStroke = (event) => {
    const point = signaturePointFromEvent(event);
    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext("2d");
    if (!point || !context) return;
    event.preventDefault();
    canvas.setPointerCapture?.(event.pointerId);
    signatureDrawingRef.current = true;
    signatureLastPointRef.current = point;
    context.beginPath();
    context.arc(point.x, point.y, 1.8, 0, Math.PI * 2);
    context.fillStyle = "#0b63ce";
    context.fill();
    setDigitalSignatureHasInk(true);
  };

  const moveDigitalSignatureStroke = (event) => {
    if (!signatureDrawingRef.current) return;
    const point = signaturePointFromEvent(event);
    const previousPoint = signatureLastPointRef.current;
    const context = signatureCanvasRef.current?.getContext("2d");
    if (!point || !previousPoint || !context) return;
    event.preventDefault();
    context.beginPath();
    context.moveTo(previousPoint.x, previousPoint.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    signatureLastPointRef.current = point;
  };

  const endDigitalSignatureStroke = (event) => {
    signatureDrawingRef.current = false;
    signatureLastPointRef.current = null;
    signatureCanvasRef.current?.releasePointerCapture?.(event.pointerId);
  };

  const captureDigitalSignatureImage = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !digitalSignatureHasInk) return "";
    const context = canvas.getContext("2d");
    if (!context) return "";
    const { width, height } = canvas;
    const imageData = context.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        if (pixels[index + 3] > 0) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }
    if (minX > maxX || minY > maxY) return "";
    const padding = Math.round(12 * (window.devicePixelRatio || 1));
    const cropX = Math.max(0, minX - padding);
    const cropY = Math.max(0, minY - padding);
    const cropWidth = Math.min(width - cropX, maxX - minX + padding * 2);
    const cropHeight = Math.min(height - cropY, maxY - minY + padding * 2);
    const normalizedCanvas = document.createElement("canvas");
    normalizedCanvas.width = 720;
    normalizedCanvas.height = 170;
    const normalizedContext = normalizedCanvas.getContext("2d");
    if (!normalizedContext) return canvas.toDataURL("image/png");
    const targetWidth = normalizedCanvas.width * 0.82;
    const targetHeight = normalizedCanvas.height * 0.7;
    const scale = Math.min(targetWidth / cropWidth, targetHeight / cropHeight);
    const drawWidth = cropWidth * scale;
    const drawHeight = cropHeight * scale;
    const drawX = normalizedCanvas.width * 0.06;
    const drawY = (normalizedCanvas.height - drawHeight) / 2;
    normalizedContext.imageSmoothingEnabled = true;
    normalizedContext.imageSmoothingQuality = "high";
    normalizedContext.drawImage(canvas, cropX, cropY, cropWidth, cropHeight, drawX, drawY, drawWidth, drawHeight);
    return normalizedCanvas.toDataURL("image/png");
  };

  const applyDigitalSignature = async () => {
    const signerName = normalizeSourceText(digitalSignatureForm.signerName);
    if (!signerName) {
      setNegotiatedPlanError("Enter the signer name before applying the digital signature.");
      return;
    }
    const signatureImageDataUrl = captureDigitalSignatureImage();
    if (!signatureImageDataUrl) {
      setNegotiatedPlanError("Draw the signer signature before applying it to the NCP.");
      return;
    }
    if (!digitalSignatureForm.acknowledged) {
      setNegotiatedPlanError("Confirm that the signer read and agreed to the negotiated care plan.");
      return;
    }
    setDigitalSignatureSaving(true);
    setNegotiatedPlanError("");
    setNegotiatedPlanSuccess("");
    try {
      const signedAt = new Date().toISOString();
      const role = digitalSignatureRoleByValue(digitalSignatureForm.role);
      const accountName = personName(user);
      const location = digitalSignatureForm.includeLocation
        ? await browserSignatureLocation()
        : { status: "declined", label: "Location capture skipped by signer" };
      const signedAtLabel = formatDateTime(signedAt);
      const signatureValue = compactLines(
        `Digitally signed by ${signerName}`,
        `${role.label}${digitalSignatureForm.signerTitle ? ` - ${normalizeSourceText(digitalSignatureForm.signerTitle)}` : ""}`,
        signedAtLabel,
      );
      const auditEntry = {
        id: `ncp-signature-${Date.now()}`,
        role: role.value,
        roleLabel: role.label,
        signerName,
        signerTitle: normalizeSourceText(digitalSignatureForm.signerTitle),
        signedAt,
        signedAtLabel,
        signedByUserId: user?.id || user?.userId || user?.uid || "",
        signedByName: accountName || "",
        signedByEmail: user?.email || "",
        signatureImageDataUrl,
        location,
        locationLabel: location.label,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        pageUrl: typeof window !== "undefined" ? window.location.href : "",
        note: normalizeSourceText(digitalSignatureForm.note),
      };
      const auditLine = compactSentence(
        `${role.label} ${signerName} signed digitally on ${signedAtLabel}`,
        accountName ? `account ${accountName}${user?.email ? ` (${user.email})` : ""}` : "",
        `location ${location.label}`,
        auditEntry.note,
      );
      let nextPayload = ensureNegotiatedPlanPayloadFields(negotiatedPlanPayload, negotiatedPlanTemplate);
      nextPayload = updateNegotiatedPayloadField(nextPayload, role.fieldKey, (field) => ({ ...field, value: signatureValue }));
      nextPayload = updateNegotiatedPayloadField(nextPayload, "signatureNote", (field) => ({
        ...field,
        value: compactLines(field.value, `Digital signature audit: ${auditLine}`),
      }));
      if (Number.isInteger(role.involvedIndex)) {
        nextPayload = updateNegotiatedPayloadField(nextPayload, "involvedInDevelopment", (field) => {
          const checkCount = Math.max(field.checks?.length || 0, role.involvedIndex + 1);
          const checks = Array.from({ length: checkCount }, (_, index) => (
            field.checks?.[index] || { index, checked: false }
          ));
          checks[role.involvedIndex] = { ...checks[role.involvedIndex], checked: true };
          return { ...field, checks };
        });
      }
      nextPayload = {
        ...nextPayload,
        digitalSignatures: [...ncpDigitalSignatures, auditEntry],
      };
      setNegotiatedPlanHistory((current) => [...current, cloneNegotiatedPlanPayload(negotiatedPlanPayload)]);
      const savedPayload = await saveNegotiatedPlan(nextPayload, `${role.label} digital signature applied and saved.`);
      if (savedPayload) setDigitalSignatureOpen(false);
    } finally {
      setDigitalSignatureSaving(false);
    }
  };

  const generateNegotiatedPlanFromResources = async () => {
    const initialValues = negotiatedInitialValuesFromAssessment(assessment, ncpDailyNotes, ncpExtractionDocument, ncpWeeklyReports);
    if (!ncpExtractionDocument && !ncpDailyNotes.length && !Object.values(initialValues).some(Boolean)) {
      setNegotiatedPlanError("Add assessment, resident, or daily-note information before generating the negotiated care plan.");
      return;
    }
    if (!assessmentProcessId) {
      setNegotiatedPlanError("Open the assessment process before populating the negotiated care plan.");
      return;
    }
    setNegotiatedPlanSaving(true);
    setNegotiatedPlanError("");
    setNegotiatedPlanSuccess("");
    try {
      const generated = await careService.negotiatedCarePlans.generate(assessmentProcessId, {
        assessmentExtraction: ncpExtractionDocument || null,
        dailyNotes: ncpDailyNotes,
        weeklyReports: ncpWeeklyReports,
        initialValues,
        generationRules: ncpDailyNoteGenerationRules,
        dailyNotesEvidenceSummary: sourceObservationText(ncpDailyNotes, ncpWeeklyReports, 3200),
      });
      const dailyAnalysis = sourceObservationText(ncpDailyNotes, ncpWeeklyReports);
      const dailyFieldSummaries = ncpDailyFieldSummariesFromNotes(ncpDailyNotes);
      const graphValues = ncpValuesFromSourceGraph(ncpExtractionDocument, dailyAnalysis, negotiatedPlanTemplate, dailyFieldSummaries);
      const generatedValues = Object.entries(generated?.fieldValues || {}).reduce((values, [key, value]) => {
        const text = normalizeSourceText(value);
        if (!text) return values;
        if (behaviorNcpDailyFieldKeys.has(key) && dailyFieldSummaries[key]) {
          values[key] = dailyFieldSummaries[key];
          return values;
        }
        values[key] = value;
        return values;
      }, {});
      const basePayload = applyNegotiatedInitialValues(
        ensureNegotiatedPlanPayloadFields(
          createNegotiatedPlanPayload({ assessmentId: assessmentProcessId, recordId, template: negotiatedPlanTemplate }),
          negotiatedPlanTemplate,
        ),
        initialValuesForNcpTemplate({
          ...initialValues,
          residentName: initialValues.residentName || processResidentName(assessment),
          dateNcpStarted: initialValues.dateNcpStarted || assessment.processStartDate || assessment.startDate || "",
        }),
      );
      const nextPayload = {
        ...applyNcpFieldValues(basePayload, { ...graphValues, ...dailyFieldSummaries, ...generatedValues }, generated?.fieldChecks || {}),
        assessmentId: assessmentProcessId,
        recordId,
        status: "draft",
        generatedAt: new Date().toISOString(),
        generatedFrom: [
          ncpExtractionDocument ? "Saved assessment extraction" : null,
          Object.values(initialValues).some(Boolean) ? "Assessment and resident information" : null,
          ncpDailyNotes.length || ncpWeeklyReports.length ? "Daily-note and weekly-report analysis" : null,
        ].filter(Boolean),
        sourceData: {
          assessmentExtraction: ncpExtractionDocument || null,
          dailyNotes: Array.isArray(ncpDailyNotes) ? ncpDailyNotes : [],
          weeklyReports: Array.isArray(ncpWeeklyReports) ? ncpWeeklyReports : [],
          dailyNotesAnalysis: sourceObservationText(ncpDailyNotes, ncpWeeklyReports),
          dailyNoteGenerationRules: ncpDailyNoteGenerationRules,
          ncpGeneration: generated || null,
        },
      };
      setNegotiatedPlanHistory((current) => [...current, cloneNegotiatedPlanPayload(negotiatedPlanPayload)]);
      setNegotiatedPlanPayload(nextPayload);
      await saveNegotiatedPlan(nextPayload, "Negotiated care plan generated and saved from assessment and daily-note sources.");
    } catch (err) {
      setNegotiatedPlanError(err?.response?.data?.message || err?.message || "Unable to generate negotiated care plan.");
    } finally {
      setNegotiatedPlanSaving(false);
    }
  };

  const printWindowAfterImagesLoad = (printWindow) => {
    let printed = false;
    const printNow = () => {
      if (printed || printWindow.closed) return;
      printed = true;
      printWindow.focus();
      printWindow.print();
    };
    const images = Array.from(printWindow.document.images || []);
    if (!images.length) {
      window.setTimeout(printNow, 120);
      return;
    }
    const imageLoads = images.map((image) => {
      if (image.complete && image.naturalWidth > 0) return Promise.resolve();
      if (typeof image.decode === "function") {
        return image.decode().catch(() => undefined);
      }
      return new Promise((resolve) => {
        image.onload = resolve;
        image.onerror = resolve;
      });
    });
    Promise.all(imageLoads).then(() => window.setTimeout(printNow, 180));
    window.setTimeout(printNow, 2500);
  };

  const printNegotiatedPlan = () => {
    const printWindow = window.open("", "_blank", "width=1100,height=760");
    if (!printWindow) return;
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${recordName} - ${processResidentName(assessment)}</title>
          <style>
            @page { size: landscape; margin: 0.16in; }
            * { box-sizing: border-box; }
            body { color: #172b21; font-family: Arial, sans-serif; font-size: 9.2px; line-height: 1.18; margin: 0; }
            h1 { background: #f2f2f2; border: 1px solid #a6a6a6; color: #111827; font-size: 16px; margin: 0; padding: 12px; text-align: center; }
            .note { color: #b45309; font-style: italic; font-weight: 700; margin: 6px 0 10px; }
            .ncp-preview-page-sheet { break-after: page; page-break-after: always; position: relative; }
            .ncp-preview-page-sheet:last-child { break-after: auto; page-break-after: auto; }
            .ncp-preview-page-head { align-items: center; background: #f2f2f2; border: 1px solid #a6a6a6; display: flex; justify-content: space-between; margin-bottom: 6px; padding: 6px 8px; }
            .ncp-preview-page-head strong { color: #111827; font-size: 11px; text-transform: uppercase; }
            .ncp-preview-page-head span { color: #475569; font-weight: 700; }
            .ncp-fillable-check { appearance: none; -webkit-appearance: none; background: transparent; border: 0; color: #000; cursor: pointer; display: inline-block; font-family: Arial, sans-serif; font-size: 1em; font-weight: 900; height: 1em; line-height: 1; margin: 0 1px; min-width: 1.45em; outline: none; position: relative; text-align: center; vertical-align: -0.08em; width: 1.45em; }
            .ncp-fillable-check::after { color: #000; content: "[ ]"; font-size: 1em; font-weight: 900; left: 50%; line-height: 1; position: absolute; top: 48%; transform: translate(-50%, -50%); white-space: nowrap; }
            .ncp-fillable-check:checked::after { content: "[x]"; }
            .ncp-page-one-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; }
            .ncp-page-one-title { background: #f2f2f2; border: 1px solid #8f8f8f; color: #000; font-size: 13px; font-weight: 900; padding: 5px; text-align: center; text-transform: uppercase; }
            .ncp-page-one-title span { color: #2385db; text-decoration: underline; }
            .ncp-page-one-note { align-items: center; border-left: 1px solid #8f8f8f; border-right: 1px solid #8f8f8f; border-bottom: 2px solid #2385db; display: flex; justify-content: space-between; padding: 3px 4px 1px; }
            .ncp-page-one-note strong { color: #c45911; font-style: italic; }
            .ncp-page-one-note em { color: #2385db; font-weight: 900; }
            .ncp-page-one-grid { border-left: 1px solid #8f8f8f; display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); grid-template-areas: "provider provider provider provider started started moved moved completed completed discharged discharged" "resident resident resident resident dob dob language language language allergies allergies allergies" "legal legal legal legal emergency emergency emergency emergency emergency emergency emergency emergency" "legal legal legal legal evacuation evacuation evacuation evacuation evacuation evacuation evacuation evacuation"; }
            .ncp-cell { border-right: 1px solid #8f8f8f; border-bottom: 1px solid #8f8f8f; min-height: 31px; padding: 3px 4px; }
            .ncp-span-2 { grid-column: span 2; }
            .ncp-provider-cell { grid-area: provider; }
            .ncp-started-cell { grid-area: started; }
            .ncp-moved-cell { grid-area: moved; }
            .ncp-completed-cell { grid-area: completed; }
            .ncp-discharged-cell { grid-area: discharged; }
            .ncp-resident-cell { grid-area: resident; }
            .ncp-dob-cell { grid-area: dob; }
            .ncp-language-cell { grid-area: language; }
            .ncp-allergy-cell { grid-area: allergies; }
            .ncp-cell b { color: #000; display: block; font-size: 9.4px; line-height: 1.1; }
            .ncp-cell b span, .ncp-red { color: #f00 !important; }
            .ncp-cell p { font-size: 8.8px; line-height: 1.16; margin: 2px 0 0; white-space: pre-wrap; }
            .ncp-allergy-cell .ncp-allergy-copy { color: #000 !important; font-size: 9.2px; font-weight: 800; min-height: 14px; }
            .ncp-left-column { grid-area: legal; min-height: 195px; }
            .ncp-emergency-heading { background: #808080; color: #fff; grid-area: emergency; min-height: 18px; padding: 3px 5px; }
            .ncp-emergency-heading b { color: #fff; font-size: 10px; }
            .ncp-evacuation-copy { grid-area: evacuation; min-height: 177px; }
            .ncp-medical-band { border: 1px solid #8f8f8f; margin-top: 7px; min-height: 35px; }
            .ncp-medical-band strong { background: #808080; color: #fff; display: block; font-size: 10px; padding: 3px 6px; }
            .ncp-medical-band p { font-size: 8.8px; margin: 0; padding: 5px; }
            .ncp-index-grid { display: grid; gap: 3px 28px; grid-template-columns: repeat(5, 1fr); margin-top: 13px; padding-bottom: 40px; padding-top: 4px; }
            .ncp-index-grid span { color: #0563c1; font-size: 9.4px; font-weight: 700; text-decoration: underline; }
            .ncp-page-footer { bottom: 24px; color: #808080; display: grid; font-size: 11px; font-weight: 700; left: 24px; margin-top: 10px; position: absolute; right: 18px; }
            .ncp-page-two-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-two-title { color: #7a7a7a; font-size: 11px; font-weight: 900; margin: 0 0 8px; text-align: center; text-transform: uppercase; }
            .ncp-contacts-table { border-collapse: collapse; table-layout: fixed; width: 100%; }
            .ncp-contacts-table th, .ncp-contacts-table td { border: 1px solid #a6a6a6; color: #000; font-size: 9px; padding: 3px 5px; vertical-align: top; }
            .ncp-contacts-table thead tr:first-child th { background: #808080; color: #fff; font-size: 8.8px; line-height: 1.2; text-align: center; }
            .ncp-contacts-table thead tr:first-child th *, .ncp-communication-title * { color: #fff !important; }
            .ncp-contacts-table thead tr:nth-child(2) th { background: #f2f2f2; font-size: 9px; text-align: center; }
            .ncp-contacts-table th span, .ncp-blue { color: #0563c1 !important; }
            .ncp-doc-link { text-decoration: underline; }
            .ncp-contacts-table td { height: 12px; }
            .ncp-page-two-tip { color: #000; font-size: 8px; font-style: italic; font-weight: 700; margin: 3px 0 6px; text-align: right; }
            .ncp-communication-title { background: #808080; border: 1px solid #8f8f8f; color: #fff; font-size: 10px; font-weight: 900; padding: 3px 5px; }
            .ncp-communication-grid { border-left: 1px solid #a6a6a6; display: grid; grid-template-columns: 1.42fr 0.98fr 0.98fr; }
            .ncp-communication-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; padding: 4px 6px; }
            .ncp-comm-head { align-items: center; display: flex; font-size: 9px; font-weight: 900; justify-content: center; line-height: 1.18; min-height: 31px; text-align: center; }
            .ncp-comm-topic { justify-content: flex-start; text-align: left; }
            .ncp-comm-left, .ncp-comm-middle, .ncp-comm-right { font-size: 8.2px; line-height: 1.18; min-height: 150px; }
            .ncp-comm-left b, .ncp-comm-middle b, .ncp-comm-right b { color: #000; display: block; font-size: 9px; }
            .ncp-comm-left p, .ncp-comm-middle p, .ncp-comm-right p { margin: 1px 0 4px; white-space: pre-wrap; }
            .ncp-comm-left hr { border: 0; border-top: 1px solid #a6a6a6; margin: 4px -6px; }
            .ncp-page-three-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-comments-grid { border-left: 1px solid #a6a6a6; display: grid; grid-template-columns: 1.42fr 0.98fr 0.98fr; }
            .ncp-comments-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; padding: 3px 5px; }
            .ncp-comments-grid .ncp-comm-head { min-height: 52px; }
            .ncp-comments-row { grid-column: 1 / -1; min-height: 16px; font-size: 8.2px; }
            .ncp-section-bar { background: #808080; border: 1px solid #8f8f8f; color: #fff; font-size: 10px; font-weight: 900; padding: 3px 5px; }
            .ncp-med-overview { margin-top: 25px; }
            .ncp-med-overview-grid { border-left: 1px solid #000; display: grid; grid-template-columns: 1.25fr 0.95fr; }
            .ncp-med-overview-grid > div { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; font-size: 8.6px; line-height: 1.22; min-height: 74px; padding: 3px 6px; }
            .ncp-med-overview-grid p { margin: 0 0 4px; }
            .ncp-med-management { display: flex; flex: 1; flex-direction: column; margin-top: 25px; }
            .ncp-med-management-grid { border-left: 1px solid #a6a6a6; display: grid; flex: 1; grid-template-columns: 1fr 0.96fr 1.24fr; }
            .ncp-med-management-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; padding: 4px 6px; }
            .ncp-med-intro { font-size: 9px; line-height: 1.38; min-height: 56px; }
            .ncp-med-left, .ncp-med-middle, .ncp-med-right { font-size: 8.4px; line-height: 1.22; min-height: 172px; }
            .ncp-med-left p, .ncp-med-middle p, .ncp-med-right p { margin: 1px 0 5px; white-space: pre-wrap; }
            .ncp-checkbox-list p { margin: 1px 0; }
            .ncp-page-four-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-four-grid { border-left: 1px solid #a6a6a6; display: grid; flex: 1; grid-template-columns: 1fr 0.96fr 1.24fr; }
            .ncp-page-four-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; padding: 4px 6px; }
            .ncp-page-four-left, .ncp-page-four-middle, .ncp-page-four-right { font-size: 8.4px; line-height: 1.22; min-height: 228px; }
            .ncp-page-four-left p, .ncp-page-four-right p { margin: 1px 0 4px; }
            .ncp-page-four-right ul, .ncp-rn-block ul { margin: 2px 0 8px 16px; padding: 0; }
            .ncp-page-four-right li, .ncp-rn-block li { margin: 1px 0; }
            .ncp-nested-checkbox { padding-left: 16px; }
            .ncp-indent-line { padding-left: 18px; }
            .ncp-rn-block { margin-top: 7px; padding-left: 16px; }
            .ncp-page-four-plan-title, .ncp-page-four-plan-middle, .ncp-page-four-plan-right { font-size: 8.5px; line-height: 1.24; min-height: 49px; }
            .ncp-page-four-plan-title strong, .ncp-page-four-plan-middle strong, .ncp-page-four-plan-right strong { display: block; font-size: 8.5px; margin-bottom: 4px; }
            .ncp-page-five-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-underlined-bar { text-decoration: underline; }
            .ncp-page-five-health-grid, .ncp-page-five-treatment-grid { border-left: 1px solid #a6a6a6; display: grid; grid-template-columns: 1fr 0.96fr 1.24fr; }
            .ncp-page-five-health-grid > div, .ncp-page-five-treatment-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; padding: 4px 6px; }
            .ncp-health-topic { align-items: center; display: flex; font-size: 9px; min-height: 48px; }
            .ncp-health-left, .ncp-health-middle, .ncp-health-right { font-size: 8.4px; line-height: 1.24; min-height: 168px; }
            .ncp-health-middle strong, .ncp-health-right strong, .ncp-health-allergy-title strong, .ncp-health-allergy-cell strong { display: block; font-size: 8.5px; }
            .ncp-health-reporting { font-size: 8.4px; line-height: 1.24; margin-top: 0; padding-top: 68px; }
            .ncp-health-monitoring-spacer { margin-top: 0; padding-top: 52px; }
            .ncp-health-reporting strong { display: inline; font-size: 9.5px; }
            .ncp-health-allergy-title, .ncp-health-allergy-cell { align-items: center; display: flex; font-size: 8.5px; min-height: 32px; }
            .ncp-page-five-treatment-bar { margin-top: 18px; }
            .ncp-page-five-treatment-grid { flex: 1; }
            .ncp-treatment-left, .ncp-treatment-middle, .ncp-treatment-right { font-size: 8.2px; line-height: 1.18; min-height: 174px; }
            .ncp-treatment-left strong, .ncp-treatment-middle strong, .ncp-treatment-right strong { display: block; font-size: 8.5px; }
            .ncp-treatment-right p { margin: 4px 0 0; }
            .ncp-page-six-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-six-grid { border-left: 1px solid #a6a6a6; display: grid; flex: 1; grid-template-columns: 1fr 0.96fr 1.24fr; }
            .ncp-page-six-grid > div { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; padding: 4px 6px; }
            .ncp-page-six-left, .ncp-page-six-middle, .ncp-page-six-right { font-size: 8.5px; line-height: 1.22; min-height: 82px; }
            .ncp-page-six-plan-title, .ncp-page-six-plan-middle, .ncp-page-six-plan-right { font-size: 8.5px; line-height: 1.24; min-height: 56px; }
            .ncp-page-six-plan-title strong, .ncp-page-six-plan-middle strong, .ncp-page-six-plan-right strong { display: block; font-size: 8.5px; margin-bottom: 4px; }
            .ncp-page-seven-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-seven-grid { border-left: 1px solid #a6a6a6; border-top: 1px solid #a6a6a6; display: grid; grid-template-columns: 1fr 0.91fr 1.21fr; grid-template-rows: 52px; margin-top: 32px; }
            .ncp-page-seven-grid > * { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; }
            .ncp-psych-title { align-items: center; background: #808080; color: #fff !important; display: flex; font-size: 10px; font-weight: 900; justify-content: center; min-height: 52px; text-align: center; }
            .ncp-page-seven-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-seven-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-psych-middle, .ncp-psych-right { font-size: 8.4px; grid-row: 2 / span 18; line-height: 1.1; min-height: 401px; }
            .ncp-psych-group, .ncp-psych-row { grid-column: 1; padding: 1.5px 4.5px; }
            .ncp-psych-sleep { min-height: 60px; }
            .ncp-psych-memory { min-height: 66px; }
            .ncp-psych-decision { min-height: 56px; }
            .ncp-psych-row { min-height: 16px; }
            .ncp-psych-section-line, .ncp-psych-note-line, .ncp-psych-check-line { margin: 0 0 3px; }
            .ncp-psych-note-line { line-height: 1.2; }
            .ncp-psych-middle, .ncp-psych-right { padding: 3px 5px; text-align: center; }
            .ncp-psych-middle { grid-column: 2; }
            .ncp-psych-right { grid-column: 3; }
            .ncp-psych-middle strong, .ncp-psych-right strong { display: block; font-size: 9.5px; line-height: 1.3; }
            .ncp-page-eight-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-eight-grid { border-left: 1px solid #a6a6a6; border-top: 1px solid #a6a6a6; display: grid; grid-template-columns: 1fr 0.91fr 1.21fr; grid-template-rows: 52px repeat(8, 16px) 182px 139px; margin-top: 16px; }
            .ncp-page-eight-grid > * { border-right: 1px solid #a6a6a6; border-bottom: 1px solid #a6a6a6; color: #000; }
            .ncp-page-eight-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-eight-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-eight-behavior-row { align-items: center; display: flex; font-size: 8px; font-weight: 800; gap: 4px; grid-column: 1; padding: 1px 4px; }
            .ncp-page-eight-support, .ncp-page-eight-programs { font-size: 8px; grid-column: 1; line-height: 1.14; padding: 4px; }
            .ncp-page-eight-support p, .ncp-page-eight-programs p { margin: 0 0 3px; }
            .ncp-spaced-line { margin-top: 16px !important; }
            .ncp-page-eight-middle-top, .ncp-page-eight-right-top, .ncp-page-eight-middle-bottom, .ncp-page-eight-right-bottom { font-size: 8px; padding: 3px 4px; }
            .ncp-page-eight-middle-top, .ncp-page-eight-right-top { grid-row: 2 / span 9; }
            .ncp-page-eight-middle-top { grid-column: 2; }
            .ncp-page-eight-right-top { grid-column: 3; }
            .ncp-page-eight-middle-bottom, .ncp-page-eight-right-bottom { grid-row: 11; }
            .ncp-page-eight-middle-bottom { grid-column: 2; }
            .ncp-page-eight-right-bottom { grid-column: 3; }
            .ncp-page-nine-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-nine-psych-grid, .ncp-page-nine-left-grid, .ncp-page-nine-universal-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; grid-template-columns: 1fr 0.98fr 1.25fr; }
            .ncp-page-nine-psych-grid > *, .ncp-page-nine-left-grid > *, .ncp-page-nine-universal-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-nine-left-grid > .ncp-section-bar, .ncp-page-nine-universal-grid > .ncp-section-bar, .ncp-page-ten-universal-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-nine-psych-grid { grid-template-rows: 48px 51px 34px; margin-top: 11px; }
            .ncp-page-nine-left-grid { grid-template-rows: 48px 47px; margin-top: 18px; }
            .ncp-page-nine-universal-grid { flex: 1; grid-template-rows: 48px 1fr; margin-top: 18px; }
            .ncp-page-nine-psych-grid > .ncp-comm-head:nth-child(2), .ncp-page-nine-left-grid > .ncp-comm-head:nth-child(2), .ncp-page-nine-universal-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-nine-psych-grid > .ncp-comm-head:nth-child(3), .ncp-page-nine-left-grid > .ncp-comm-head:nth-child(3), .ncp-page-nine-universal-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-nine-interventions, .ncp-page-nine-left-title, .ncp-page-nine-left-middle, .ncp-page-nine-left-right, .ncp-page-nine-universal-left, .ncp-page-nine-universal-middle, .ncp-page-nine-universal-right, .ncp-page-nine-psych-middle, .ncp-page-nine-psych-right, .ncp-page-nine-narrative { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-nine-interventions { display: grid; gap: 2px; }
            .ncp-page-nine-narrative { grid-column: 1 / -1; }
            .ncp-page-nine-universal-left { display: grid; gap: 2px; }
            .ncp-page-nine-universal-right p { margin: 0 0 6px; }
            .ncp-page-ten-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-ten-universal-grid, .ncp-page-ten-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; grid-template-columns: 1fr 0.96fr 1.02fr; }
            .ncp-page-ten-universal-grid > *, .ncp-page-ten-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-ten-universal-grid { grid-template-rows: 48px 110px; margin-top: 9px; }
            .ncp-page-ten-adl-grid { flex: 1; grid-template-rows: 48px 32px 1fr; margin-top: 18px; }
            .ncp-page-ten-universal-grid > .ncp-section-bar, .ncp-page-ten-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-ten-universal-grid > .ncp-comm-head:nth-child(2), .ncp-page-ten-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-ten-universal-grid > .ncp-comm-head:nth-child(3), .ncp-page-ten-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-ten-universal-left, .ncp-page-ten-universal-middle, .ncp-page-ten-universal-right, .ncp-page-ten-functional, .ncp-page-ten-ambulation, .ncp-page-ten-adl-middle, .ncp-page-ten-adl-right { font-size: 8.3px; line-height: 1.22; padding: 4px 5px; }
            .ncp-page-ten-universal-left { display: grid; gap: 4px; }
            .ncp-page-ten-functional { grid-column: 1 / -1; }
            .ncp-page-ten-ambulation p, .ncp-page-ten-adl-middle p, .ncp-page-ten-adl-right p { margin: 0 0 7px; }
            .ncp-page-eleven-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-eleven-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 214px 1fr; margin-top: 10px; }
            .ncp-page-eleven-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-eleven-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-eleven-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-eleven-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-eleven-top-left, .ncp-page-eleven-top-middle, .ncp-page-eleven-top-right, .ncp-page-eleven-transfer-left, .ncp-page-eleven-transfer-middle, .ncp-page-eleven-transfer-right { font-size: 8.3px; line-height: 1.22; padding: 4px 5px; }
            .ncp-page-eleven-top-left p, .ncp-page-eleven-transfer-left p, .ncp-page-eleven-transfer-middle p, .ncp-page-eleven-transfer-right p { margin: 0 0 7px; }
            .ncp-page-twelve-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-twelve-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 192px 232px 1fr; margin-top: 10px; }
            .ncp-page-twelve-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-twelve-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-twelve-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-twelve-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-twelve-device-left, .ncp-page-twelve-device-middle, .ncp-page-twelve-device-right, .ncp-page-twelve-eating-left, .ncp-page-twelve-eating-middle, .ncp-page-twelve-eating-right, .ncp-page-twelve-toileting-left, .ncp-page-twelve-toileting-middle, .ncp-page-twelve-toileting-right { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-twelve-device-left p, .ncp-page-twelve-eating-left p, .ncp-page-twelve-eating-middle p, .ncp-page-twelve-eating-right p, .ncp-page-twelve-toileting-left p, .ncp-page-twelve-toileting-middle p, .ncp-page-twelve-toileting-right p { margin: 0 0 5px; }
            .ncp-page-thirteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-thirteen-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 224px 184px 1fr; margin-top: 10px; }
            .ncp-page-thirteen-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-thirteen-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-thirteen-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-thirteen-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-thirteen-toileting-left, .ncp-page-thirteen-toileting-middle, .ncp-page-thirteen-toileting-right, .ncp-page-thirteen-dressing-left, .ncp-page-thirteen-dressing-middle, .ncp-page-thirteen-dressing-right, .ncp-page-thirteen-hygiene-left, .ncp-page-thirteen-hygiene-middle, .ncp-page-thirteen-hygiene-right { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-thirteen-toileting-left p, .ncp-page-thirteen-toileting-right p, .ncp-page-thirteen-dressing-left p, .ncp-page-thirteen-dressing-middle p, .ncp-page-thirteen-dressing-right p, .ncp-page-thirteen-hygiene-left p, .ncp-page-thirteen-hygiene-middle p, .ncp-page-thirteen-hygiene-right p { margin: 0 0 5px; }
            .ncp-page-fourteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-fourteen-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 230px 187px 1fr; margin-top: 10px; }
            .ncp-page-fourteen-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-fourteen-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-fourteen-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-fourteen-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-fourteen-hygiene-left, .ncp-page-fourteen-hygiene-middle, .ncp-page-fourteen-hygiene-right, .ncp-page-fourteen-bathing-left, .ncp-page-fourteen-bathing-middle, .ncp-page-fourteen-bathing-right, .ncp-page-fourteen-foot-left, .ncp-page-fourteen-foot-middle, .ncp-page-fourteen-foot-right { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-fourteen-hygiene-left p, .ncp-page-fourteen-hygiene-middle p, .ncp-page-fourteen-hygiene-right p, .ncp-page-fourteen-bathing-left p, .ncp-page-fourteen-bathing-middle p, .ncp-page-fourteen-bathing-right p, .ncp-page-fourteen-foot-left p, .ncp-page-fourteen-foot-middle p, .ncp-page-fourteen-foot-right p { margin: 0 0 5px; }
            .ncp-page-fifteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-fifteen-adl-grid { border-left: 1px solid #000; border-top: 1px solid #000; display: grid; flex: 1; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 204px 1fr; margin-top: 10px; }
            .ncp-page-fifteen-adl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-fifteen-adl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-fifteen-adl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-fifteen-adl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-fifteen-foot-left, .ncp-page-fifteen-foot-middle, .ncp-page-fifteen-foot-right, .ncp-page-fifteen-skin-left, .ncp-page-fifteen-skin-middle, .ncp-page-fifteen-skin-right { font-size: 8.3px; line-height: 1.18; padding: 4px 5px; }
            .ncp-page-fifteen-foot-left p, .ncp-page-fifteen-foot-middle p, .ncp-page-fifteen-foot-right p, .ncp-page-fifteen-skin-left p, .ncp-page-fifteen-skin-middle p, .ncp-page-fifteen-skin-right p { margin: 0 0 5px; }
            .ncp-page-sixteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: auto; min-height: 7.95in; padding: 0 0 34px; }
            .ncp-page-sixteen-iadl-grid { border-top: 1px solid #000; display: grid; flex: 0 0 auto; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 48px 110px 100px 116px minmax(192px, auto); margin-top: 10px; }
            .ncp-page-sixteen-iadl-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-sixteen-iadl-grid > .ncp-section-bar { color: #fff !important; }
            .ncp-page-sixteen-iadl-grid > .ncp-section-bar, .ncp-page-sixteen-finances-left, .ncp-page-sixteen-shopping-left, .ncp-page-sixteen-transport-left, .ncp-page-sixteen-social-left { border-left: 1px solid #000; }
            .ncp-page-sixteen-iadl-grid > .ncp-comm-head:nth-child(2) { grid-column: 2; grid-row: 1; }
            .ncp-page-sixteen-iadl-grid > .ncp-comm-head:nth-child(3) { grid-column: 3; grid-row: 1; }
            .ncp-page-sixteen-finances-left, .ncp-page-sixteen-finances-middle, .ncp-page-sixteen-finances-right, .ncp-page-sixteen-shopping-left, .ncp-page-sixteen-shopping-middle, .ncp-page-sixteen-shopping-right, .ncp-page-sixteen-transport-left, .ncp-page-sixteen-transport-middle, .ncp-page-sixteen-transport-right, .ncp-page-sixteen-social-left, .ncp-page-sixteen-social-middle, .ncp-page-sixteen-social-right { font-size: 7.7px; line-height: 1.08; padding: 3px 4px; }
            .ncp-page-sixteen-finances-left p, .ncp-page-sixteen-finances-middle p, .ncp-page-sixteen-finances-right p, .ncp-page-sixteen-shopping-left p, .ncp-page-sixteen-shopping-middle p, .ncp-page-sixteen-shopping-right p, .ncp-page-sixteen-transport-left p, .ncp-page-sixteen-transport-middle p, .ncp-page-sixteen-transport-right p, .ncp-page-sixteen-social-left p, .ncp-page-sixteen-social-middle p, .ncp-page-sixteen-social-right p { margin: 0 0 3px; }
            .ncp-page-sixteen-sheet .ncp-page-footer, .ncp-page-seventeen-sheet .ncp-page-footer { bottom: auto; left: auto; margin-top: 10px; position: static; right: auto; }
            .ncp-page-seventeen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: auto; min-height: 7.95in; padding: 0 0 34px; }
            .ncp-page-seventeen-grid { border-top: 1px solid #000; display: grid; flex: 0 0 auto; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: minmax(165px, auto) 15px minmax(170px, auto) minmax(56px, auto) minmax(76px, auto); margin-bottom: 15px; margin-top: 5px; }
            .ncp-page-seventeen-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-seventeen-social-left, .ncp-page-seventeen-preferences-title, .ncp-page-seventeen-preferences-left, .ncp-page-seventeen-narrative, .ncp-page-seventeen-smoking-left { border-left: 1px solid #000; }
            .ncp-page-seventeen-preferences-title, .ncp-page-seventeen-narrative { grid-column: 1 / -1; }
            .ncp-page-seventeen-social-left, .ncp-page-seventeen-social-middle, .ncp-page-seventeen-social-right, .ncp-page-seventeen-preferences-left, .ncp-page-seventeen-preferences-middle, .ncp-page-seventeen-preferences-right, .ncp-page-seventeen-narrative, .ncp-page-seventeen-smoking-left, .ncp-page-seventeen-smoking-middle, .ncp-page-seventeen-smoking-right { font-size: 7.6px; line-height: 1.08; padding: 3px 4px; }
            .ncp-page-seventeen-preferences-title { align-items: center; display: flex; font-size: 9px; justify-content: center; padding: 1px 4px; text-align: center; }
            .ncp-page-seventeen-social-left p, .ncp-page-seventeen-social-middle p, .ncp-page-seventeen-social-right p, .ncp-page-seventeen-preferences-left p, .ncp-page-seventeen-preferences-middle p, .ncp-page-seventeen-preferences-right p, .ncp-page-seventeen-smoking-left p, .ncp-page-seventeen-smoking-middle p, .ncp-page-seventeen-smoking-right p { margin: 0 0 3px; }
            .ncp-page-seventeen-preferences-left label, .ncp-page-seventeen-preferences-middle label, .ncp-page-seventeen-preferences-right label { display: block; font-size: 7.6px; line-height: 1.05; margin: 0 0 2px; }
            .ncp-page-eighteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-eighteen-grid { border-top: 1px solid #000; display: grid; grid-template-columns: 1fr 0.86fr 0.86fr; grid-template-rows: 126px 50px 158px; margin-top: 7px; }
            .ncp-page-eighteen-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-eighteen-case-left, .ncp-page-eighteen-other, .ncp-page-eighteen-review-left { border-left: 1px solid #000; }
            .ncp-page-eighteen-other { grid-column: 1 / -1; }
            .ncp-page-eighteen-case-left, .ncp-page-eighteen-case-middle, .ncp-page-eighteen-case-right, .ncp-page-eighteen-other, .ncp-page-eighteen-review-left, .ncp-page-eighteen-review-middle, .ncp-page-eighteen-review-right { color: #000; font-size: 9.2px; line-height: 1.1; padding: 3px 5px; }
            .ncp-page-eighteen-case-left p, .ncp-page-eighteen-case-right p, .ncp-page-eighteen-review-middle p, .ncp-page-eighteen-review-right p { margin: 0 0 4px; }
            .ncp-page-eighteen-case-right ul, .ncp-page-eighteen-review-right ul { margin: 1px 0 0 14px; padding: 0; }
            .ncp-page-eighteen-abbreviations { color: #000; display: grid; font-size: 9.2px; grid-template-columns: 1fr 1fr 1fr 1fr; line-height: 1.12; margin-top: 1px; }
            .ncp-page-eighteen-abbreviations > strong { grid-column: 1 / -1; font-size: 9.8px; }
            .ncp-page-eighteen-abbreviations p { margin: 0 0 3px; }
            .ncp-page-eighteen-approval { color: #000; font-size: 9.2px; line-height: 1.12; margin-top: 14px; }
            .ncp-page-eighteen-approval h3 { color: #000; font-size: 12px; margin: 0 0 4px; }
            .ncp-page-eighteen-approval p { margin: 10px 0 0; }
            .ncp-page-nineteen-sheet { display: flex; flex-direction: column; font-family: Arial, sans-serif; height: 7.95in; padding: 0; }
            .ncp-page-nineteen-grid { border-top: 1px solid #000; display: grid; grid-template-columns: 0.62fr 0.64fr 0.98fr 0.98fr; grid-template-rows: 37px repeat(10, 40px) 21px 58px; margin-top: 7px; }
            .ncp-page-nineteen-grid > * { border-right: 1px solid #000; border-bottom: 1px solid #000; color: #000; }
            .ncp-page-nineteen-involved, .ncp-page-nineteen-verbal, .ncp-page-nineteen-recommendations { border-left: 1px solid #000; }
            .ncp-page-nineteen-involved { font-size: 8.6px; grid-column: 1; grid-row: 1 / 12; line-height: 1.12; padding: 3px 5px; }
            .ncp-page-nineteen-head { align-items: center; display: flex; font-size: 8.6px; font-weight: 900; justify-content: space-between; padding: 3px 5px; }
            .ncp-page-nineteen-approver, .ncp-page-nineteen-signature { font-size: 8.2px; line-height: 1.1; padding: 3px 4px; }
            .ncp-signature-stamp { color: #111827; font-size: 6.1px; font-weight: 800; line-height: 1.05; }
            .ncp-signature-line { align-items: center; display: grid; gap: 3px; grid-template-columns: minmax(0, 1fr) auto; }
            .ncp-signature-date { color: #0b63ce; font-size: 14.8px; font-weight: 900; white-space: nowrap; }
            .ncp-handwritten-signature { display: block; filter: brightness(0) saturate(100%) invert(31%) sepia(93%) saturate(2054%) hue-rotate(199deg) brightness(91%) contrast(93%); height: 52px; margin: -5px 0 -3px; max-width: 100%; object-fit: contain; object-position: left center; width: 260px; }
            .ncp-page-nineteen-verbal { font-size: 8.4px; grid-column: 1 / 3; padding: 3px 5px; }
            .ncp-page-nineteen-sent { font-size: 8.4px; grid-column: 3 / 5; padding: 3px 5px; }
            .ncp-page-nineteen-recommendations { font-size: 8.4px; grid-column: 1 / 5; padding: 4px 5px; }
            .ncp-page-nineteen-involved p, .ncp-page-nineteen-approver p, .ncp-page-nineteen-signature p { margin: 0 0 3px; }
            .ncp-page-nineteen-note { color: #000; font-size: 10px; line-height: 1.18; margin-top: 4px; }
            .ncp-med-right ul { margin: 3px 0 0 18px; padding: 0; }
            .ncp-preview-table { border-collapse: collapse; margin: 0 0 8px; table-layout: fixed; width: 100%; }
            .ncp-preview-table th { background: #eef7ee; border: 1px solid #a6a6a6; color: #143f2b; font-size: 11px; padding: 6px; text-align: left; }
            .ncp-preview-table td { border: 1px solid #b8c2cc; padding: 5px 6px; vertical-align: top; }
            .ncp-preview-label { background: #f8fafc; color: #111827; font-weight: 700; width: 32%; }
            .ncp-preview-value { min-height: 18px; white-space: pre-wrap; }
          </style>
        </head>
        <body>
          ${negotiatedTemplateDocumentHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindowAfterImagesLoad(printWindow);
  };

  const printPreliminaryPlan = () => {
    const printWindow = window.open("", "_blank", "width=920,height=720");
    if (!printWindow) return;
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${recordName} - ${processResidentName(assessment)}</title>
          <style>
            body { color: #172b21; font-family: Arial, sans-serif; line-height: 1.5; margin: 40px; }
            h1 { color: #143f2b; margin-bottom: 4px; }
            .meta { border-bottom: 2px solid #d9ead6; color: #475569; font-weight: 700; margin-bottom: 24px; padding-bottom: 12px; }
            h2, h3, h4 { color: #4e8a3c; }
            strong { color: #143f2b; }
          </style>
        </head>
        <body>
          <h1>${recordName}</h1>
          <div class="meta">${processResidentName(assessment)} - ${assessment.name || assessment.title || "Assessment process"}</div>
          ${planDescription}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const renderNcpFillableLabel = (key, label) => {
    let checkboxIndex = 0;
    return String(label).split(/(\[\s*\])/g).map((part, index) => {
      if (!/\[\s*\]/.test(part)) return part;
      const currentIndex = checkboxIndex;
      checkboxIndex += 1;
      return (
        <button
          aria-label={`${label} option ${checkboxIndex}`}
          aria-pressed={ncpCheckValue(key, currentIndex)}
          className="ncp-bracket-toggle"
          key={`${label}-${index}`}
          onClick={(event) => {
            event.preventDefault();
            updateNegotiatedTemplateCheck(key, currentIndex, !ncpCheckValue(key, currentIndex));
          }}
          type="button"
        >
          {ncpCheckText(key, currentIndex)}
        </button>
      );
    });
  };

  const renderNcpCheckboxInput = (key, index, label) => (
    <input
      aria-label={label}
      checked={ncpCheckValue(key, index)}
      onChange={(event) => updateNegotiatedTemplateCheck(key, index, event.target.checked)}
      type="checkbox"
    />
  );

  const renderNegotiatedField = (key, label, className = "") => {
    const value = negotiatedFieldValue(key);
    const lineCount = String(value || "").split(/\n/).length;
    const estimatedRows = Math.min(7, Math.max(2, lineCount + Math.floor(String(value || "").length / 95)));
    return (
      <label className={`ncp-page-one-field ${className}`.trim()}>
        <span>{renderNcpFillableLabel(key, label)}</span>
        <textarea
          aria-label={label}
          rows={estimatedRows}
          value={value}
          onChange={(event) => updateNegotiatedTemplateField(key, event.target.value)}
        />
      </label>
    );
  };

  return (
    <CareShell embedded={embedded} className="dashboard-content residents-page assessments-page">
      {!embedded ? (
        <div className="residents-page-head">
          <div className="page-title">
            <h1>{recordName}</h1>
            <p>{processResidentName(assessment)} - {assessment.name || assessment.title || "Assessment process"}</p>
          </div>
          <div className="residents-actions">
            <Link className="mini-btn" to={`/assessments/${assessmentProcessId}/new`}><i className="ti ti-arrow-left" /> Assessment Process</Link>
          </div>
        </div>
      ) : null}

      {record && isNegotiatedPlan ? (
        <>
          <div className="assessment-section-heading-row assessment-section-heading-row--actions assessment-panel-heading-actions">
            <h2>{recordName}</h2>
            <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons preliminary-plan-actions">
              <button className="primary-action" type="button" disabled={negotiatedPlanSaving} onClick={generateNegotiatedPlanFromResources}>
                <i className="ti ti-sparkles" /> {negotiatedPlanSaving ? "Populating..." : "Populate Care Plan"}
              </button>
              <button type="button" disabled={negotiatedPlanSaving} onClick={() => saveNegotiatedPlan()}>
                <i className="ti ti-device-floppy" /> Save Draft
              </button>
              <button type="button" onClick={improveNegotiatedPlan}><i className="ti ti-wand" /> Improve</button>
              <button type="button" onClick={formatNegotiatedPlan}><i className="ti ti-brush" /> Format</button>
              <button type="button" disabled={!negotiatedPlanHistory.length} onClick={undoNegotiatedPlanAction}><i className="ti ti-arrow-back-up" /> Undo</button>
              <button type="button" disabled={negotiatedPlanSaving || digitalSignatureSaving} onClick={openDigitalSignatureModal}>
                <i className="ti ti-signature" /> Sign Digitally
              </button>
              <button data-readonly-allowed="true" type="button" onClick={() => setNegotiatedPreviewOpen(true)}><i className="ti ti-eye" /> Preview</button>
              <button data-readonly-allowed="true" type="button" onClick={printNegotiatedPlan}><i className="ti ti-printer" /> Print</button>
              <button className="success" type="button" onClick={confirmMarkCompleted}><i className="ti ti-circle-check" /> Mark as Completed</button>
            </div>
          </div>

          <div className="preliminary-plan-grid negotiated-plan-grid negotiated-plan-workflow">
            <div className="preliminary-plan-main negotiated-template-main">
              <section className="assessment-drawer-section negotiated-template-banner">
                <h3>Standard Template Document</h3>
                <p>The template structure is locked to the AFH/HCS Negotiated Care Plan form. Generate from the saved assessment extraction and daily-note observations, then review and edit each field before printing.</p>
              </section>
              <section className="assessment-drawer-section">
                <h3>Included Sources</h3>
                <p>
                  {ncpExtractionDocument ? "Saved assessment extraction is included." : "No saved assessment extraction is available yet."}
                  {ncpDailyNotes.length ? ` ${ncpDailyNotes.length} daily-note observation${ncpDailyNotes.length === 1 ? " is" : "s are"} included.` : " No daily-note observations are included yet."}
                </p>
                {ncpVisibleDailySummary ? <small>{ncpVisibleDailySummary}</small> : null}
                {negotiatedPlanError ? <p className="form-error">{negotiatedPlanError}</p> : null}
                {negotiatedPlanSuccess ? <p className="form-success">{negotiatedPlanSuccess}</p> : null}
              </section>
              {ncpDigitalSignatures.length ? (
                <section className="assessment-drawer-section ncp-signature-audit-panel">
                  <h3>Digital Signature Audit</h3>
                  <div className="ncp-signature-audit-list">
                    {ncpDigitalSignatures.map((signature) => (
                      <article key={signature.id || `${signature.role}-${signature.signedAt}`}>
                        <strong>{signature.signerName} <span>{signature.roleLabel}</span></strong>
                        <p>{signature.signedAtLabel || formatDateTime(signature.signedAt)} by {signature.signedByName || signature.signedByEmail || "unknown account"}</p>
                        <small>{signature.locationLabel || signature.location?.label || "Location not recorded"}</small>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}
              <section className="assessment-drawer-section ncp-recommendations-panel">
                <div className="ncp-recommendations-heading">
                  <div>
                    <h3>Assessment analysis and recommendations</h3>
                    <p>Gemini-derived guidance from the saved assessment extraction. Review it before populating the official form.</p>
                  </div>
                  <button
                    className="btn primary"
                    type="button"
                    disabled={negotiatedPlanSaving || (!ncpExtractionDocument && !ncpDailyNotes.length)}
                    onClick={generateNegotiatedPlanFromResources}
                  >
                    <i className="ti ti-arrow-down-to-arc" />
                    {negotiatedPlanSaving ? "Populating form..." : "Allocate to NCP form"}
                  </button>
                </div>
                {ncpRecommendationGroups.length ? (
                  <div className="ncp-recommendation-groups">
                    {ncpRecommendationGroups.map((group) => (
                      <article key={group.key}>
                        <h4><i className={`ti ${group.icon}`} /> {group.title}</h4>
                        <ul>
                          {group.items.map((item, index) => <li key={`${group.key}-${index}`}>{item}</li>)}
                        </ul>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="ncp-recommendations-empty">
                    <i className="ti ti-file-search" />
                    <div>
                      <strong>No assessment recommendations are available yet.</strong>
                      <span>Complete assessment extraction first, then return here to allocate the results.</span>
                    </div>
                  </div>
                )}
                {ncpVisibleDailySummary ? (
                  <div className="ncp-daily-analysis">
                    <strong><i className="ti ti-notes" /> Daily-form analysis included</strong>
                    <p>{ncpVisibleDailySummary}</p>
                  </div>
                ) : null}
              </section>
              <section className="assessment-drawer-section negotiated-template-document">
                <div className="negotiated-template-title">
                  <h3>Adult Family Home Resident Negotiated Care Plan (NCP)</h3>
                  <span>Form Version {record.formVersion}</span>
                </div>
                {negotiatedTemplatePages.map((page) => (
                  <div className="negotiated-form-page" key={page.pageNumber}>
                    <div className="negotiated-form-page-head">
                      <strong>Page {page.pageNumber} of 19</strong>
                      <span>Landscape NCP template page</span>
                    </div>
                    {page.pageNumber === 1 ? (
                      <div className="ncp-page-one-grid ncp-page-one-form-grid">
                        <div className="ncp-cell ncp-provider-cell">{renderNegotiatedField("providerName", "Provider's Name")}</div>
                        <div className="ncp-cell ncp-started-cell">{renderNegotiatedField("dateNcpStarted", "Date NCP Started")}</div>
                        <div className="ncp-cell ncp-moved-cell">{renderNegotiatedField("movedInDate", "Moved In Date")}</div>
                        <div className="ncp-cell ncp-completed-cell">{renderNegotiatedField("dateCompleted", "Date Completed")}</div>
                        <div className="ncp-cell ncp-discharged-cell">{renderNegotiatedField("dateDischarged", "Date Discharged")}</div>
                        <div className="ncp-cell ncp-resident-cell">
                          {renderNegotiatedField("residentName", "Resident's Name")}
                          {renderNegotiatedField("pronouns", "Pronouns")}
                        </div>
                        <div className="ncp-cell ncp-dob-cell">{renderNegotiatedField("dateOfBirthAge", "Date of Birth/Age")}</div>
                        <div className="ncp-cell ncp-language-cell">
                          {renderNegotiatedField("primaryLanguage", "Primary Language")}
                          {renderNegotiatedField("speaksEnglish", "Speaks English?")}
                          {renderNegotiatedField("interpreterNeeded", "Interpreter needed?")}
                        </div>
                        <div className="ncp-cell ncp-allergy-cell">{renderNegotiatedField("allergies", "ALLERGIES:", "ncp-allergy-form-field")}</div>
                        <div className="ncp-cell ncp-left-column">
                          {renderNegotiatedField("legalDocuments", "Legal Documents: [ ] None / [ ] Advanced Directives / [ ] POLST Form / [ ] Other")}
                          {renderNegotiatedField("specialtyNeeds", "Specialty Needs: [ ] No / [ ] Dementia / [ ] Mental Health / [ ] Developmental Disability / [ ] Other")}
                        </div>
                        <div className="ncp-cell ncp-emergency-heading"><strong>EMERGENCY EVACUATION</strong></div>
                        <div className="ncp-cell ncp-evacuation-copy">
                          {renderNegotiatedField("evacuationIndependent", "[] NONE - RESIDENT IS INDEPENDENT")}
                          {renderNegotiatedField("evacuationAssistance", "[] ASSISTANCE REQUIRED")}
                          {renderNegotiatedField("evacuationSafetyInstructions", "RESIDENT'S EVACUATION and SAFETY INSTRUCTIONS")}
                          {renderNegotiatedField("evacuationCaregiverInstructions", "Caregiver will")}
                        </div>
                        <div className="ncp-medical-band ncp-medical-form-band">
                          {renderNegotiatedField("medicalDiagnosis", "MENTAL/PHYSICAL HEALTH - CURRENT MEDICAL STATUS/DIAGNOSIS")}
                        </div>
                        <div className="ncp-index-grid">
                          {ncpPageOneIndexLinks.map((link) => <span key={link}>{link}</span>)}
                        </div>
                        <div className="ncp-page-footer">
                          <span>Resident's Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 1 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 2 ? (
                      <div className="ncp-page-two-sheet ncp-page-two-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-contacts-form-table">
                          <div className="ncp-contacts-form-title">
                            <strong>RESPONSIBLE PARTIES - CONTACTS</strong>
                            <span>Add those involved in care planning for your resident: Case Manager, DPOA, Guardian, Medical/Mental Health Providers, Pharmacy, Hospice, etc. - Indicate which contact method is preferred.</span>
                          </div>
                          <table className="ncp-contacts-edit-table">
                            <thead>
                              <tr>
                                {ncpContactColumns.map((column) => <th key={column.key}>{column.label}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {[1, 2, 3, 4, 5, 6].map((number) => (
                                <tr key={number}>
                                  {ncpContactColumns.map((column, columnIndex) => {
                                    const fieldKey = ncpContactFieldKey(number, column.key);
                                    return (
                                      <td key={fieldKey}>
                                        <label className="ncp-contact-cell-field">
                                          {columnIndex === 0 ? <span>{number}.</span> : null}
                                          <input
                                            aria-label={`Contact ${number} ${column.label}`}
                                            value={negotiatedFieldValue(fieldKey)}
                                            onChange={(event) => updateNegotiatedTemplateField(fieldKey, event.target.value)}
                                          />
                                        </label>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="ncp-page-two-tip">(Place your cursor in the last box and hit your tab key to add another row)</p>
                        <div className="ncp-communication-title">COMMUNICATION</div>
                        <div className="ncp-communication-grid ncp-communication-form-grid">
                          <div className="ncp-comm-head ncp-comm-topic">SPEECH/HEARING/VISION</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Caregiver Instructions<br />Who, How, When/How Often</div>
                          <div className="ncp-comm-left">
                            {renderNegotiatedField("communicationModes", "Modes of Expression")}
                            {renderNegotiatedField("expressionProblems", "Problems with Mode of Expression - [ ] Yes [ ] No / Describe")}
                            {renderNegotiatedField("expressionEquipment", "Equipment")}
                            {renderNegotiatedField("hearingProblems", "Problems with Hearing - [ ] Yes [ ] No / Describe / Equipment")}
                            {renderNegotiatedField("visionProblems", "Problems with Vision - [ ] Yes [ ] No / Describe / Equipment")}
                            {renderNegotiatedField("phoneAbility", "Ability to Use the Phone")}
                            {renderNegotiatedField("preferredLanguageComments", "Preferred Language")}
                          </div>
                          <div className="ncp-comm-middle">
                            {renderNegotiatedField("selfUnderstood", "How resident makes self-understood")}
                            {renderNegotiatedField("understandsOthers", "How resident understands others")}
                            {renderNegotiatedField("communicationStrengths", "Strength and Abilities")}
                          </div>
                          <div className="ncp-comm-right">
                            {renderNegotiatedField("communicationAssistance", "Assistance Required / Caregiver will")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 2 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 3 ? (
                      <div className="ncp-page-three-sheet ncp-page-three-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-communication-title">COMMUNICATION</div>
                        <div className="ncp-comments-grid ncp-comments-form-grid">
                          <div className="ncp-comm-head ncp-comm-topic">SPEECH/HEARING/VISION</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Caregiver Instructions<br />Who, How, When/How Often</div>
                          <div className="ncp-comments-row">{renderNegotiatedField("communicationComments", "Comments")}</div>
                        </div>
                        <div className="ncp-med-overview">
                          <div className="ncp-section-bar">MEDICATION MANAGEMENT - Overview</div>
                          <div className="ncp-med-overview-grid ncp-med-overview-form-grid">
                            <div>
                              <div className="ncp-red-field">{renderNegotiatedField("medicationAllergies", "[ ] MEDICATION ALLERGIES")}</div>
                              {renderNegotiatedField("medicationAssistanceNeeds", "[ ] Resident needs more than one kind of medication assistance need")}
                              {renderNegotiatedField("psychopharmacologicMeds", "[ ] Resident is prescribed psychopharmacologic medications")}
                            </div>
                            <div>
                              {renderNegotiatedField("marReference", "[ ] See MAR for current medications, dosage, frequency, and route")}
                              {renderNegotiatedField("medsOrderedBy", "Meds are ordered by")}
                              {renderNegotiatedField("medsDeliveredBy", "Meds are delivered by")}
                              <div className="ncp-blue-field ncp-link-field">{renderNegotiatedField("pharmacyPacked", "[ ] Meds are Pharmacy Packed / Note")}</div>
                            </div>
                          </div>
                        </div>
                        <div className="ncp-med-management">
                          <div className="ncp-section-bar">MEDICATION MANAGEMENT</div>
                          <div className="ncp-med-management-grid ncp-med-management-form-grid">
                            <div className="ncp-med-intro"><em>The amount of assistance required to receive prescription medications, over the counter medications, or herbal supplements.</em></div>
                            <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                            <div className="ncp-comm-head">Assistance Required<br />Caregiver Instruction<br />Who, How, When/How Often</div>
                            <div className="ncp-med-left">
                              <div className="ncp-blue-field ncp-link-field">{renderNegotiatedField("medicationManagementType", "[ ] SELF-ADMINISTRATION / [ ] SELF-ADMINISTRATION W/ ASSISTANCE / [ ] MEDICATION ADMINISTRATION")}</div>
                              <div className="ncp-route-checklist">
                                <strong>Equipment:</strong>
                                <span>Type of Medication Management is Needed:</span>
                                {ncpPageThreeMedicationRouteOptions.map((option, index) => (
                                  <label key={option}>
                                    {renderNcpCheckboxInput("medicationRoutes", index, option)}
                                    {option}
                                  </label>
                                ))}
                              </div>
                              {renderNegotiatedField("medicationRoutes", "Other / notes")}
                            </div>
                            <div className="ncp-med-middle">
                              <div className="ncp-blue-field">{renderNegotiatedField("medicationStrengths", "Strengths and Abilities")}</div>
                            </div>
                            <div className="ncp-med-right">
                              <div className="ncp-blue-field">{renderNegotiatedField("medicationAssistance", "Assistance Required / Caregiver will")}</div>
                            </div>
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 3 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 4 ? (
                      <div className="ncp-page-four-sheet ncp-page-four-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-section-bar">MEDICATION MANAGEMENT</div>
                        <div className="ncp-page-four-grid ncp-page-four-form-grid">
                          <div className="ncp-med-intro"><em>The amount of assistance required to receive prescription medications, over the counter medications, or herbal supplements.</em></div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Caregiver Instruction<br />Who, How, When/How Often</div>
                          <div className="ncp-page-four-left">
                            <div className="ncp-route-checklist">
                              <strong>[ ] Injections:</strong>
                              {injectionSupportOptions.map((option, index) => (
                                <label className={option === "(Insulin Only)" ? "ncp-indent-line" : ""} key={option}>
                                  {option === "(Insulin Only)" ? null : renderNcpCheckboxInput("injectionSupport", index, option)}
                                  {option}
                                </label>
                              ))}
                              {ncpPageFourMedicationRouteOptions.map((option, index) => (
                                <label key={option}>
                                  {renderNcpCheckboxInput("medicationRoutes", index + ncpPageThreeMedicationRouteOptions.length, option)}
                                  {option}
                                </label>
                              ))}
                            </div>
                            <div className="ncp-blue-field ncp-link-field">{renderNegotiatedField("nurseDelegation", "[ ] Requires Nurse Delegation / RN Delegator")}</div>
                          </div>
                          <div className="ncp-page-four-middle" />
                          <div className="ncp-page-four-right">
                            <div className="ncp-blue-field">{renderNegotiatedField("medicationAssistance", "Assistance Required / Caregiver will")}</div>
                          </div>
                          <div className="ncp-page-four-plan-title">
                            <strong className="ncp-blue">Medication Plan When Resident is not in the AFH</strong>
                          </div>
                          <div className="ncp-page-four-plan-middle">
                            {renderNegotiatedField("medicationOutsideAfhStrengths", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-page-four-plan-right ncp-blue-field">
                            {renderNegotiatedField("medicationOutsideAfh", "Assistance Required / Caregiver will")}
                          </div>
                          <div className="ncp-page-four-plan-title">
                            <strong className="ncp-blue">Medication Refusal Plan</strong>
                          </div>
                          <div className="ncp-page-four-plan-middle">
                            {renderNegotiatedField("medicationRefusalStrengths", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-page-four-plan-right">
                            {renderNegotiatedField("medicationRefusalPlan", "Assistance Required / Caregiver will")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 4 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 5 ? (
                      <div className="ncp-page-five-sheet ncp-page-five-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-section-bar ncp-underlined-bar">HEALTH INDICATORS</div>
                        <div className="ncp-page-five-health-grid ncp-page-five-form-grid">
                          <div className="ncp-health-topic"><strong>Health Indicator Monitoring and Support</strong></div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-health-left">
                            <div className="ncp-route-checklist">
                              {healthIndicatorOptions.map((option) => {
                                const optionText = typeof option === "string" ? option : option.text;
                                const checkIndex = typeof option === "object" ? option.checkIndex : null;
                                const isNested = typeof option === "object" ? option.nested : option === "Pain Impact:" || option === "Current Weight:" || option === "Current Height:";
                                const isBlue = optionText === "Pain" || optionText === "Other:";
                                return (
                                  <label className={isNested ? "ncp-indent-line" : ""} key={optionText}>
                                    {Number.isInteger(checkIndex) ? renderNcpCheckboxInput("healthIndicatorMonitoring", checkIndex, optionText) : null}
                                    <span className={isBlue ? "ncp-blue" : ""}>{optionText}</span>
                                  </label>
                                );
                              })}
                            </div>
                            {renderNegotiatedField("healthIndicatorMonitoring", "Health indicator notes")}
                          </div>
                          <div className="ncp-health-middle">
                            {renderNegotiatedField("healthStrengths", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-health-right">
                            <strong>Assistance Required</strong>
                            {renderNegotiatedField("healthAssistance", "Assistance details")}
                            <div className="ncp-health-monitoring-spacer">
                              {renderNegotiatedField("monitoringReporting", "Monitoring/Reporting significant changes and/or concerns")}
                            </div>
                          </div>
                          <div className="ncp-health-allergy-title"><strong>Allergies</strong></div>
                          <div className="ncp-health-allergy-cell">{renderNegotiatedField("allergySubstanceReaction", "Substance")}</div>
                          <div className="ncp-health-allergy-cell">{renderNegotiatedField("allergySubstanceReaction", "Reaction")}</div>
                        </div>
                        <div className="ncp-section-bar ncp-page-five-treatment-bar">TREATMENTS/PROGRAMS/THERAPIES</div>
                        <div className="ncp-page-five-treatment-grid ncp-page-five-form-grid">
                          <div className="ncp-comm-head">Treatment/Program<br />Therapy/Interventions</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-treatment-left">
                            <strong>Type of Treatment:</strong>
                            <div className="ncp-route-checklist">
                              {treatmentTypeOptions.map((option, index) => (
                                <label className={option === "INR/LAB - Health Provider:" || option === "Easily bruised/Anti coagulation therapy" ? "ncp-indent-line" : ""} key={option}>
                                  {renderNcpCheckboxInput("treatmentTypes", index, option)}
                                  {option}
                                </label>
                              ))}
                            </div>
                            {renderNegotiatedField("treatmentTypes", "Treatment notes")}
                          </div>
                          <div className="ncp-treatment-middle ncp-blue-field">
                            {renderNegotiatedField("treatmentStrengths", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-treatment-right ncp-blue-field">
                            {renderNegotiatedField("treatmentAssistance", "Assistance Required / Caregiver will")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 5 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 6 ? (
                      <div className="ncp-page-six-sheet ncp-page-six-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-section-bar">TREATMENTS/PROGRAMS/THERAPIES</div>
                        <div className="ncp-page-six-grid ncp-page-six-form-grid">
                          <div className="ncp-comm-head">Treatment/Program<br />Therapy/Interventions</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-six-left">
                            <div className="ncp-route-checklist">
                              {treatmentTherapyOptions.map((option) => {
                                const isBlue = option === "Range of Motion" || option === "Other:";
                                const isLabelOnly = option === "Treatments/Therapies - Tasks:";
                                const checkIndex = option === "PT/OT/ST" ? 0 : option === "Nurse Delegation for" ? 1 : option === "Other:" ? 2 : null;
                                return (
                                  <label key={option}>
                                    {isLabelOnly || checkIndex === null ? null : renderNcpCheckboxInput("rangeOfMotionTherapies", checkIndex, option)}
                                    <span className={isBlue ? "ncp-blue" : ""}>{option}</span>
                                  </label>
                                );
                              })}
                            </div>
                            {renderNegotiatedField("rangeOfMotionTherapies", "Treatment/therapy notes")}
                          </div>
                          <div className="ncp-page-six-middle" />
                          <div className="ncp-page-six-right" />
                          <div className="ncp-page-six-left">
                            <strong>Programs Resident Requires/Attends:</strong>
                            <div className="ncp-route-checklist">
                              {treatmentProgramOptions.map((option, index) => (
                                <label className={option === "Hospice Plan" ? "ncp-indent-line" : ""} key={option}>
                                  {renderNcpCheckboxInput("programsAttended", index, option)}
                                  <span className={option === "Home Health" || option === "Hospice - Agency:" ? "ncp-blue" : ""}>{option}</span>
                                </label>
                              ))}
                            </div>
                            {renderNegotiatedField("programsAttended", "Program notes")}
                          </div>
                          <div className="ncp-page-six-middle" />
                          <div className="ncp-page-six-right" />
                          <div className="ncp-page-six-left">
                            <strong>Interventions</strong>
                            <div className="ncp-route-checklist">
                              <label>{renderNcpCheckboxInput("meaningfulDay", 0, "Meaningful Day")}Meaningful Day</label>
                            </div>
                            {renderNegotiatedField("meaningfulDay", "Intervention notes")}
                          </div>
                          <div className="ncp-page-six-middle" />
                          <div className="ncp-page-six-right" />
                          <div className="ncp-page-six-plan-title">
                            <strong className="ncp-blue">Physical Enablers</strong>
                          </div>
                          <div className="ncp-page-six-plan-middle">
                            {renderNegotiatedField("physicalEnablers", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-page-six-plan-right ncp-blue-field">
                            {renderNegotiatedField("physicalEnablers", "Assistance Required / Caregiver will")}
                          </div>
                          <div className="ncp-page-six-plan-title">
                            <strong>TREATMENT/PROGRAM/THERAPY REFUSAL PLAN</strong>
                          </div>
                          <div className="ncp-page-six-plan-middle">
                            {renderNegotiatedField("treatmentRefusalPlan", "Strengths and Abilities")}
                          </div>
                          <div className="ncp-page-six-plan-right ncp-blue-field">
                            {renderNegotiatedField("treatmentRefusalPlan", "Assistance Required / Caregiver will")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 6 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 7 ? (
                      <div className="ncp-page-seven-sheet ncp-page-seven-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-seven-grid ncp-page-seven-form-grid">
                          <div className="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          {psychStatusCells.map((cell, cellIndex) => (
                            <div className={cell.className} key={cellIndex}>
                              {cell.lines.map((line, lineIndex) => {
                                if (line.section) return <p className="ncp-psych-section-line" key={lineIndex}>{line.text}</p>;
                                if (line.note) return <p className="ncp-psych-note-line" key={lineIndex}>{line.text}</p>;
                                return (
                                  <label className="ncp-psych-check-line" key={lineIndex}>
                                    {line.checkbox ? renderNcpCheckboxInput(line.checkKey, line.checkIndex, line.text) : null}
                                    {line.blueText ? (
                                      <><span className="ncp-blue">{line.blueText}</span>{line.suffix || ""}</>
                                    ) : (
                                      <><span className={line.blue ? "ncp-blue" : ""}>{line.text}</span>{line.suffix || ""}</>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          ))}
                          <div className="ncp-psych-middle">
                            {renderNegotiatedField("sleepStrengths", "Sleep strengths and preferences")}
                            {renderNegotiatedField("memoryStrengths", "Memory strengths and preferences")}
                          </div>
                          <div className="ncp-psych-right">
                            {renderNegotiatedField("sleepAssistance", "Sleep assistance")}
                            {renderNegotiatedField("memoryAssistance", "Memory / behavior assistance")}
                            {renderNegotiatedField("decisionMaking", "Decision making notes")}
                          </div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 7 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 8 ? (
                      <div className="ncp-page-eight-sheet ncp-page-eight-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-eight-grid ncp-page-eight-form-grid">
                          <div className="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          {psychPageEightBehaviorRows.map((row, index) => (
                            <label className="ncp-page-eight-behavior-row" key={row}>
                              {renderNcpCheckboxInput("behaviorCheckboxes", index + 15, row)}
                              {row}
                            </label>
                          ))}
                          <div className="ncp-page-eight-support">
                            {psychPageEightSupportLines.map((line, index) => (
                              <p className={line.spacer ? "ncp-spaced-line" : ""} key={index}>
                                {line.checkbox ? renderNcpCheckboxInput(line.checkKey, line.checkIndex, line.text) : null}
                                {line.blue ? <span className="ncp-blue ncp-doc-link">{line.text}</span> : line.strong ? <strong>{line.text}</strong> : line.text}
                                {line.suffix || ""}
                              </p>
                            ))}
                            {renderNegotiatedField("psychMeds", "Medication / symptoms notes")}
                            {renderNegotiatedField("behavioralHealthSupport", "Behavioral health support notes")}
                          </div>
                          <div className="ncp-page-eight-programs">
                            {specializedBehaviorProgramLines.map((line, index) => (
                              <p className={line.spacer ? "ncp-spaced-line" : ""} key={index}>
                                {line.strong ? <strong>{line.text}</strong> : Number.isInteger(line.checkIndex) ? <>{renderNcpCheckboxInput("specializedBehaviorPrograms", line.checkIndex, line.text)}{line.text}</> : line.text}
                              </p>
                            ))}
                            {renderNegotiatedField("specializedBehaviorPrograms", "Program contact info")}
                          </div>
                          <div className="ncp-page-eight-middle-top">{renderNegotiatedField("behaviors", "Behavior strengths / goals")}</div>
                          <div className="ncp-page-eight-right-top">{renderNegotiatedField("behaviorAssistance", "Assistance required")}</div>
                          <div className="ncp-page-eight-middle-bottom">{renderNegotiatedField("specializedBehaviorProgramStrengths", "Program strengths / goals")}</div>
                          <div className="ncp-page-eight-right-bottom">{renderNegotiatedField("specializedBehaviorProgramAssistance", "Program assistance")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 8 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 9 ? (
                      <div className="ncp-page-nine-sheet ncp-page-nine-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-nine-psych-grid">
                          <div className="ncp-psych-title">PSYCH/SOCIAL/COGNITIVE STATUS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-nine-interventions">
                            <strong>Interventions</strong>
                            <label>{renderNcpCheckboxInput("psychInterventions", 0, "Meaningful Day")}Meaningful Day</label>
                            <label>{renderNcpCheckboxInput("psychInterventions", 1, "Other")}Other:</label>
                          </div>
                          <div className="ncp-page-nine-psych-middle">{renderNegotiatedField("psychInterventions", "Intervention strengths / goals")}</div>
                          <div className="ncp-page-nine-psych-right">{renderNegotiatedField("psychInterventionAssistance", "Intervention assistance")}</div>
                          <div className="ncp-page-nine-narrative">{renderNegotiatedField("typicalDayNarrative", "Narrative (optional) - What does a typical day look like?")}</div>
                        </div>
                        <div className="ncp-page-nine-left-grid">
                          <div className="ncp-section-bar">Left Alone</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-nine-left-title ncp-blue-field">{renderNegotiatedField("leftAloneAbility", "Ability of Resident to Be Left Unattended")}</div>
                          <div className="ncp-page-nine-left-middle">{renderNegotiatedField("leftAloneStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-nine-left-right ncp-blue-field">{renderNegotiatedField("leftAloneAssistance", "Assistance Required / Caregiver will")}</div>
                        </div>
                        <div className="ncp-page-nine-universal-grid">
                          <div className="ncp-section-bar">UNIVERSAL PRECAUTIONS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-nine-universal-left">
                            {universalPrecautionLines.map((line) => (
                              <label className={line.nested ? "ncp-indent-line" : ""} key={line.text}>
                                {line.noBox ? null : renderNcpCheckboxInput("universalPrecautions", line.checkIndex, line.text)}
                                <span className={line.blue ? "ncp-blue" : ""}>{line.text}</span>
                              </label>
                            ))}
                            {renderNegotiatedField("universalPrecautions", "Universal precautions notes")}
                          </div>
                          <div className="ncp-page-nine-universal-middle">{renderNegotiatedField("universalPrecautionsStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-nine-universal-right">{renderNegotiatedField("universalPrecautionsCaregiver", "Caregiver instructions")}{renderNegotiatedField("universalPrecautionsAssistance", "Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 9 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 10 ? (
                      <div className="ncp-page-ten-sheet ncp-page-ten-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-ten-universal-grid">
                          <div className="ncp-section-bar">UNIVERSAL PRECAUTIONS</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-ten-universal-left">
                            {universalPrecautionPageTenLines.map((line) => (
                              <label key={line.text}>{renderNcpCheckboxInput("universalPrecautions", line.checkIndex, line.text)}{line.text}{line.blueSuffix ? <><br /><span className="ncp-blue">{line.suffix}</span></> : null}</label>
                            ))}
                            {renderNegotiatedField("universalPrecautions", "Vaccinations / equipment notes")}
                          </div>
                          <div className="ncp-page-ten-universal-middle">{renderNegotiatedField("universalPrecautionsStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-ten-universal-right">{renderNegotiatedField("universalPrecautionsAssistance", "Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-ten-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-ten-functional">{renderNegotiatedField("functionalLimitations", "Resident functional limitations that impact ADL functioning")}</div>
                          <div className="ncp-page-ten-ambulation">
                            {renderNegotiatedField("locomotionRoom", "Locomotion in room and immediate living environment")}
                            {renderNegotiatedField("locomotionOutside", "Locomotion outside of immediate living environment")}
                            {renderNegotiatedField("fallRisks", "Risks for falls / Fall prevention plan")}
                          </div>
                          <div className="ncp-page-ten-adl-middle ncp-blue-field">{renderNegotiatedField("mobilityStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-ten-adl-right ncp-blue-field">{renderNegotiatedField("mobilityAssistance", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 10 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 11 ? (
                      <div className="ncp-page-eleven-sheet ncp-page-eleven-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-eleven-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-eleven-top-left">
                            <div className="ncp-blue-field">{renderNegotiatedField("bedroomDoorLock", "[ ] Resident chooses bedroom door lock")}</div>
                            {renderNegotiatedField("mobilityEquipment", "Equipment/Supplies / Vendor")}
                            {renderNegotiatedField("mobilityLimitations", "Limitations")}
                          </div>
                          <div className="ncp-page-eleven-top-middle" />
                          <div className="ncp-page-eleven-top-right" />
                          <div className="ncp-page-eleven-transfer-left">
                            {renderNegotiatedField("transferLevel", "BED MOBILITY/TRANSFER")}
                            {renderNegotiatedField("skinCarePositioning", "Skin care required / equipment")}
                            {renderNegotiatedField("turningRepositioning", "Turning and repositioning frequency")}
                            {renderNegotiatedField("transferSafetyPlan", "Safety assessment / fall prevention plan")}
                          </div>
                          <div className="ncp-page-eleven-transfer-middle ncp-blue-field">{renderNegotiatedField("transferStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-eleven-transfer-right ncp-blue-field">{renderNegotiatedField("transferAssistance", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 11 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 12 ? (
                      <div className="ncp-page-twelve-sheet ncp-page-twelve-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-twelve-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-twelve-device-left">
                            {renderNegotiatedField("medicalDevices", "Safety plan / Medical Device(s) / Nighttime care needs")}
                            {renderNegotiatedField("transferEquipment", "Equipment/supplies")}
                          </div>
                          <div className="ncp-page-twelve-device-middle" />
                          <div className="ncp-page-twelve-device-right" />
                          <div className="ncp-page-twelve-eating-left">
                            {renderNegotiatedField("eatingLevel", "EATING")}
                            {renderNegotiatedField("specialDiet", "Special Diet / Supplements / Eating Habits / Food Allergies")}
                            {renderNegotiatedField("eatingEquipment", "Equipment / Supplies / Procedures / Limitations")}
                          </div>
                          <div className="ncp-page-twelve-eating-middle ncp-blue-field">{renderNegotiatedField("eatingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-twelve-eating-right ncp-blue-field">{renderNegotiatedField("eatingAssistance", "Monitoring / Assistance Required")}</div>
                          <div className="ncp-page-twelve-toileting-left">{renderNegotiatedField("toiletingLevel", "TOILETING/CONTINENCE ISSUES")}{renderNegotiatedField("continenceIssues", "Continence details")}</div>
                          <div className="ncp-page-twelve-toileting-middle ncp-blue-field">{renderNegotiatedField("toiletingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-twelve-toileting-right ncp-blue-field">{renderNegotiatedField("toiletingAssistance", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 12 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 13 ? (
                      <div className="ncp-page-thirteen-sheet ncp-page-thirteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-thirteen-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-thirteen-toileting-left">
                            {renderNegotiatedField("toiletingLevel", "Toileting: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / Frequency")}
                            {renderNegotiatedField("continenceIssues", "Continence Issues: [ ] Bladder Incontinence [ ] Bowel Incontinence [ ] Skin care due to bowel/bladder incontinence / Equipment / Limitations")}
                          </div>
                          <div className="ncp-page-thirteen-toileting-middle ncp-blue-field">{renderNegotiatedField("toiletingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-thirteen-toileting-right ncp-blue-field">{renderNegotiatedField("toiletingAssistance", "Caregiver will")}</div>
                          <div className="ncp-page-thirteen-dressing-left">{renderNegotiatedField("dressing", "DRESSING: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / Equipment / Limitations")}</div>
                          <div className="ncp-page-thirteen-dressing-middle ncp-blue-field">{renderNegotiatedField("dressingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-thirteen-dressing-right ncp-blue-field">{renderNegotiatedField("dressingAssistance", "Monitoring / Assistance Required")}</div>
                          <div className="ncp-page-thirteen-hygiene-left">
                            {renderNegotiatedField("personalHygieneLevel", "PERSONAL HYGIENE: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent")}
                            {renderNegotiatedField("personalHygiene", "Personal hygiene details")}
                          </div>
                          <div className="ncp-page-thirteen-hygiene-middle ncp-blue-field">{renderNegotiatedField("personalHygieneStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-thirteen-hygiene-right ncp-blue-field">{renderNegotiatedField("personalHygieneAssistance", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 13 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 14 ? (
                      <div className="ncp-page-fourteen-sheet ncp-page-fourteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-fourteen-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-fourteen-hygiene-left">
                            {renderNegotiatedField("personalHygieneCheckboxes", "[ ] Own teeth [ ] Partials [ ] Dentures [ ] Oral Hygiene [ ] Flossing [ ] Brushing [ ] Soaking [ ] Hair Care [ ] Menses Care")}
                            {renderNegotiatedField("personalHygiene", "Oral hygiene / hair care / menses care / equipment / limitations")}
                          </div>
                          <div className="ncp-page-fourteen-hygiene-middle ncp-blue-field">{renderNegotiatedField("personalHygieneStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fourteen-hygiene-right ncp-blue-field">{renderNegotiatedField("personalHygieneAssistance", "Assistance Required")}</div>
                          <div className="ncp-page-fourteen-bathing-left">{renderNegotiatedField("bathing", "BATHING: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / When / Equipment / Limitations")}</div>
                          <div className="ncp-page-fourteen-bathing-middle ncp-blue-field">{renderNegotiatedField("bathingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fourteen-bathing-right ncp-blue-field">{renderNegotiatedField("bathingAssistance", "Monitoring / Assistance Required")}</div>
                          <div className="ncp-page-fourteen-foot-left">{renderNegotiatedField("footCare", "FOOT CARE: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent / How Often")}</div>
                          <div className="ncp-page-fourteen-foot-middle ncp-blue-field">{renderNegotiatedField("footCareStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fourteen-foot-right ncp-blue-field">{renderNegotiatedField("footCareAssistance", "Monitoring / Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 14 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 15 ? (
                      <div className="ncp-page-fifteen-sheet ncp-page-fifteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-fifteen-adl-grid">
                          <div className="ncp-section-bar">ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When, And How</div>
                          <div className="ncp-page-fifteen-foot-left">
                            {renderNegotiatedField("footCareDetails", "[ ] Diabetic Foot Care [ ] Nail Care [ ] Home Health Agency")}
                            {renderNegotiatedField("footCare", "Foot care how often / equipment / limitations")}
                          </div>
                          <div className="ncp-page-fifteen-foot-middle ncp-blue-field">{renderNegotiatedField("footCareStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fifteen-foot-right ncp-blue-field">{renderNegotiatedField("footCareAssistance", "Caregiver will")}</div>
                          <div className="ncp-page-fifteen-skin-left">
                            {renderNegotiatedField("skinCare", "SKIN CARE: [ ] Independent [ ] Supervision/Cueing [ ] Assistance Needed [ ] Totally Dependent")}
                            {renderNegotiatedField("skinCareDetails", "[ ] Skin Care - How Often [ ] Status [ ] Skin Problems [ ] Status [ ] Pressure Injuries [ ] Dressing Changes [ ] Nurse Delegated [ ] Home Health Agency")}
                          </div>
                          <div className="ncp-page-fifteen-skin-middle ncp-blue-field">{renderNegotiatedField("skinCareStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-fifteen-skin-right ncp-blue-field">{renderNegotiatedField("skinCareAssistance", "Caregiver will")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 15 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 16 ? (
                      <div className="ncp-page-sixteen-sheet ncp-page-sixteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-sixteen-iadl-grid">
                          <div className="ncp-section-bar">INSTRUMENTAL ACTIVITIES OF DAILY LIVING</div>
                          <div className="ncp-comm-head">Resident Strengths And Abilities<br />Prefers To Do Independently<br />Preferences and Personal Goals</div>
                          <div className="ncp-comm-head">Assistance Required<br />Who Will Provide, When And How</div>
                          <div className="ncp-page-sixteen-finances-left">{renderNegotiatedField("finances", "MANAGING FINANCES")}</div>
                          <div className="ncp-page-sixteen-finances-middle ncp-blue-field">{renderNegotiatedField("financesStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-sixteen-finances-right ncp-blue-field">{renderNegotiatedField("financesAssistance", "Assistance Required")}</div>
                          <div className="ncp-page-sixteen-shopping-left">{renderNegotiatedField("shopping", "SHOPPING")}</div>
                          <div className="ncp-page-sixteen-shopping-middle ncp-blue-field">{renderNegotiatedField("shoppingStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-sixteen-shopping-right ncp-blue-field">{renderNegotiatedField("shoppingAssistance", "Assistance Required")}</div>
                          <div className="ncp-page-sixteen-transport-left">{renderNegotiatedField("transportation", "TRANSPORTATION")}</div>
                          <div className="ncp-page-sixteen-transport-middle ncp-blue-field">{renderNegotiatedField("transportationStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-sixteen-transport-right ncp-blue-field">{renderNegotiatedField("transportationAssistance", "Assistance Required")}</div>
                          <div className="ncp-page-sixteen-social-left">{renderNegotiatedField("activitiesSocial", "ACTIVITIES/SOCIAL")}</div>
                          <div className="ncp-page-sixteen-social-middle ncp-blue-field">{renderNegotiatedField("activitiesSocialStrengths", "Strengths and Abilities")}</div>
                          <div className="ncp-page-sixteen-social-right ncp-blue-field">{renderNegotiatedField("activitiesSocialAssistance", "Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 16 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 17 ? (
                      <div className="ncp-page-seventeen-sheet ncp-page-seventeen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-seventeen-grid">
                          <div className="ncp-page-seventeen-social-left">{renderNegotiatedField("activitiesSocial", "Activities / Social details")}</div>
                          <div className="ncp-page-seventeen-social-middle">{renderNegotiatedField("activityPreferences", "Resident strengths and preferences")}</div>
                          <div className="ncp-page-seventeen-social-right ncp-blue-field">{renderNegotiatedField("specialArrangements", "Special arrangements / participation assistance")}</div>
                          <div className="ncp-page-seventeen-preferences-title"><strong>ACTIVITY PREFERENCES AT A GLANCE</strong></div>
                          <div className="ncp-page-seventeen-preferences-left">
                            {["Reading book and/or magazines", "Listening to audio books and/or podcasts", "Storytelling", "Phone conversation/visiting", "Reminiscing", "Current events", "Discussion group", "Bible study or church", "Visitors"].map((label, index) => (
                              <label key={label}>{renderNcpCheckboxInput("activityPreferenceCheckboxes", index, label)}{label}</label>
                            ))}
                          </div>
                          <div className="ncp-page-seventeen-preferences-middle">
                            {["Gardening", "Outings with family or provider", "Visiting zoos and/ or spending time with pets and animals", "Exercises/range of motion", "Therapeutic Walking", "Cooking or baking", "House chore activities", "Watching TV, movies, or favorite shows"].map((label, index) => (
                              <label key={label}>{renderNcpCheckboxInput("activityPreferenceCheckboxes", index + 9, label)}{label}</label>
                            ))}
                          </div>
                          <div className="ncp-page-seventeen-preferences-right">
                            {["Parties and social gatherings", "Arts and crafts", "Table games, Bingo, cards, puzzles", "Beauty time, beautician visit", "Music appreciation/therapy/singing", "Employment support", "Community Integration", "Other:", "Other:"].map((label, index) => (
                              <label key={`${label}-${index}`}>{renderNcpCheckboxInput("activityPreferenceCheckboxes", index + 17, label)}{label}</label>
                            ))}
                          </div>
                          <div className="ncp-page-seventeen-narrative">{renderNegotiatedField("activityNarrative", "ACTIVITY NARRATIVE")}</div>
                          <div className="ncp-page-seventeen-smoking-left">{renderNegotiatedField("smoking", "SMOKING")}</div>
                          <div className="ncp-page-seventeen-smoking-middle ncp-blue-field">{renderNegotiatedField("smoking", "Strengths and Abilities")}</div>
                          <div className="ncp-page-seventeen-smoking-right ncp-blue-field">{renderNegotiatedField("smoking", "Assistance Required")}</div>
                        </div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 17 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 18 ? (
                      <div className="ncp-page-eighteen-sheet ncp-page-eighteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-eighteen-grid">
                          <div className="ncp-page-eighteen-case-left">{renderNegotiatedField("caseManagement", "CASE MANAGEMENT")}</div>
                          <div className="ncp-page-eighteen-case-middle">{renderNegotiatedField("caseManagement", "Case manager notes")}</div>
                          <div className="ncp-page-eighteen-case-right">{renderNegotiatedField("cmContactedWhen", "CM will be contacted when")}</div>
                          <div className="ncp-page-eighteen-other">{renderNegotiatedField("otherIssues", "OTHER ISSUES/CONCERNS/PROBLEMS")}</div>
                          <div className="ncp-page-eighteen-review-left ncp-blue-field">{renderNegotiatedField("reviewSchedule", "NEGOTIATED CARE PLAN REVIEW")}</div>
                          <div className="ncp-page-eighteen-review-middle">{renderNegotiatedField("residentParticipation", "Resident participation")}</div>
                          <div className="ncp-page-eighteen-review-right">{renderNegotiatedField("reviewRevisionSchedule", "Review/revision schedule")}</div>
                        </div>
                        <div className="ncp-page-eighteen-abbreviations">{renderNegotiatedField("abbreviations", "Abbreviations used in this NCP")}</div>
                        <div className="ncp-page-eighteen-approval">{renderNegotiatedField("reviewDates", "Negotiated Care Plan Review and Approval")}</div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 18 of 19</strong>
                        </div>
                      </div>
                    ) : page.pageNumber === 19 ? (
                      <div className="ncp-page-nineteen-sheet ncp-page-nineteen-form-sheet">
                        <div className="ncp-page-two-title">Adult Family Home Resident Negotiated Care Plan</div>
                        <div className="ncp-page-nineteen-grid">
                          <div className="ncp-page-nineteen-involved">{renderNegotiatedField("involvedInDevelopment", "INVOLVED IN NCP DEVELOPMENT")}</div>
                          <div className="ncp-page-nineteen-head">PERSON APPROVING PLAN</div>
                          <div className="ncp-page-nineteen-head">SIGNATURE <span>DATE*</span></div>
                          <div className="ncp-page-nineteen-head">SIGNATURE <span>DATE*</span></div>
                          <div className="ncp-page-nineteen-approver">{renderNegotiatedField("approvingPerson", "PROVIDER")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("providerSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("providerSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-approver">{renderNegotiatedField("approvingPerson", "RESIDENT")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("residentSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("residentSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-approver">{renderNegotiatedField("approvingPerson", "RESIDENT REPRESENTATIVE")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("residentRepresentativeSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-signature">{renderNegotiatedField("residentRepresentativeSignature", "Signature / Date")}</div>
                          <div className="ncp-page-nineteen-verbal">{renderNegotiatedField("residentVerbalAgreement", "Resident verbally agreed to NCP - Date")}</div>
                          <div className="ncp-page-nineteen-sent">{renderNegotiatedField("sentToDshs", "NCP sent to DSHS CM on")}</div>
                          <div className="ncp-page-nineteen-recommendations">{renderNegotiatedField("residentRecommendations", "Resident Recommendations")}</div>
                        </div>
                        <div className="ncp-page-nineteen-note">{renderNegotiatedField("signatureNote", "Signature note")}</div>
                        <div className="ncp-page-footer">
                          <span>Residents Name: {negotiatedFieldValue("residentName")}</span>
                          <strong>Page 19 of 19</strong>
                        </div>
                      </div>
                    ) : page.sections.map((section) => (
                      <article className="negotiated-template-section" key={section.title}>
                        <div className="negotiated-template-section-head">
                          <h4>{section.title}</h4>
                          <small>{section.fields.length} template field{section.fields.length === 1 ? "" : "s"}</small>
                        </div>
                        <div className="negotiated-template-field-grid">
                          {section.fields.map((field) => (
                            <label className="negotiated-template-field" key={field.key}>
                              <span>{field.label}</span>
                              <textarea
                                aria-label={field.label}
                                value={field.value}
                                onChange={(event) => updateNegotiatedTemplateField(field.key, event.target.value)}
                              />
                            </label>
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                ))}
              </section>
            </div>

            <aside className="preliminary-plan-side">
              <section className="assessment-drawer-section">
                <h3>Plan Metadata</h3>
                <div className="assessment-detail-grid">
                  <div><span>Status</span><strong><em className="pill blue">{record.status}</em></strong></div>
                  <div><span>Template</span><strong>{record.templateName}</strong></div>
                  <div><span>Latest generated at</span><strong>{formatDateTime(record.latestGeneratedAt)}</strong></div>
                  <div><span>Latest generated by</span><strong>{record.latestGeneratedBy}</strong></div>
                  <div><span>Last updated at</span><strong>{formatDateTime(record.lastUpdatedAt)}</strong></div>
                  <div><span>Last updated by</span><strong>{record.lastUpdatedBy}</strong></div>
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>Data Sources</h3>
                <div className="preliminary-source-list">
                  {(record.dataSources || []).map((source) => (
                    <span key={source}><i className="ti ti-database" /> {source}</span>
                  ))}
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>NCP Field Graph</h3>
                <div className="assessment-detail-grid">
                  <div><span>Template source</span><strong>{negotiatedPlanTemplate ? "Backend" : "Unavailable"}</strong></div>
                  <div><span>Pages</span><strong>{negotiatedPlanTemplate?.pageCount || negotiatedTemplatePages.length}</strong></div>
                  <div><span>Fields</span><strong>{ncpTemplateFields.length}</strong></div>
                  <div><span>Mapped fields</span><strong>{ncpMappedFieldRows.length}</strong></div>
                  <div><span>Filled fields</span><strong>{ncpFilledKeys.size}</strong></div>
                </div>
                {negotiatedPlanTemplateError ? <p className="form-hint">{negotiatedPlanTemplateError}</p> : null}
                {ncpEmptyMappedFields.length ? (
                  <div className="preliminary-source-list">
                    {ncpEmptyMappedFields.map((field) => (
                      <span key={field.key}>
                        <i className="ti ti-alert-circle" /> Page {field.pageNumber}: {field.key}
                      </span>
                    ))}
                  </div>
                ) : null}
              </section>
              <section className="assessment-drawer-section">
                <h3>Attached Files</h3>
                <div className="assessment-uploaded-doc-list">
                  {(record.attachedFiles || []).map((file) => (
                    <span key={file.name}>
                      <span className="assessment-attachment-thumb document"><i className="ti ti-file-description" /></span>
                      <strong>{file.name}<small>{file.caption}</small></strong>
                      <span className="assessment-document-actions">
                        <button aria-label={`Preview ${file.name}`} type="button"><i className="ti ti-eye" /></button>
                        <button aria-label={`Download ${file.name}`} type="button"><i className="ti ti-download" /></button>
                      </span>
                    </span>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        </>
      ) : record && isPreliminaryPlan ? (
        <>
          <div className="assessment-section-heading-row assessment-section-heading-row--actions assessment-panel-heading-actions">
            <h2>{recordName}</h2>
            <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons preliminary-plan-actions">
              <button className="primary-action" type="button" disabled={generationLocked}><i className="ti ti-sparkles" /> Generate Draft</button>
              <button className="danger-outline" type="button" onClick={() => setPreliminaryGenerationLocked(true)}><i className="ti ti-lock" /> Prevent Further Generation</button>
              <button type="button" onClick={() => setPreliminaryPreviewOpen(true)}><i className="ti ti-eye" /> Preview</button>
              <button type="button" onClick={printPreliminaryPlan}><i className="ti ti-printer" /> Print</button>
              <button className="success" type="button" onClick={confirmMarkCompleted}><i className="ti ti-circle-check" /> Mark as Completed</button>
            </div>
          </div>

          <div className="preliminary-plan-grid">
            <div className="preliminary-plan-main">
              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Description</h3>
                  <span>{generationLocked ? "Generation locked" : "Generation available"}</span>
                </div>
                <div className="preliminary-ai-actions">
                  <button type="button" onClick={refinePreliminaryPlan}><i className="ti ti-wand" /> Refine</button>
                  <button type="button" onClick={summarizePreliminaryPlan}><i className="ti ti-list-details" /> Summarize</button>
                  <button type="button" onClick={formatPreliminaryPlan}><i className="ti ti-brush" /> Format</button>
                  <button type="button" disabled={!preliminaryPlanHistory.length} onClick={undoPreliminaryPlanAction}><i className="ti ti-arrow-back-up" /> Undo</button>
                </div>
                <CareRichTextEditor autoGrow height={420} onChange={setPreliminaryPlanDescription} value={planDescription} />
                <input name="preliminaryPlanDescription" readOnly type="hidden" value={planDescription} />
              </section>

              <section className="assessment-drawer-section">
                <h3>Data Sources</h3>
                <div className="preliminary-source-list">
                  {(record.dataSources || []).map((source) => (
                    <span key={source}><i className="ti ti-database" /> {source}</span>
                  ))}
                </div>
              </section>
            </div>

            <aside className="preliminary-plan-side">
              <section className="assessment-drawer-section">
                <h3>Plan Metadata</h3>
                <div className="assessment-detail-grid">
                  <div><span>Status</span><strong><em className="pill blue">{record.status}</em></strong></div>
                  {record.templateName ? <div><span>Template</span><strong>{record.templateName}</strong></div> : null}
                  <div><span>Latest generated at</span><strong>{formatDateTime(record.latestGeneratedAt)}</strong></div>
                  <div><span>Latest generated by</span><strong>{record.latestGeneratedBy}</strong></div>
                  <div><span>Created at</span><strong>{formatDateTime(record.createdAt)}</strong></div>
                  <div><span>Created by</span><strong>{record.createdBy}</strong></div>
                  <div><span>Last updated at</span><strong>{formatDateTime(record.lastUpdatedAt)}</strong></div>
                  <div><span>Last updated by</span><strong>{record.lastUpdatedBy}</strong></div>
                </div>
              </section>

              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Attached Files</h3>
                  <label className="preliminary-upload-button">
                    <input className="assessment-file-input" multiple onChange={addPreliminaryAttachedFiles} type="file" />
                    <i className="ti ti-file-plus" /> Add files
                  </label>
                </div>
                <div className="assessment-uploaded-doc-list">
                  {(record.attachedFiles || []).map((file) => (
                    <span key={file.name}>
                      <span className={`assessment-attachment-thumb ${file.type === "photo" ? "photo" : "document"}`}>
                        <i className={`ti ${file.type === "photo" ? "ti-photo" : "ti-file-description"}`} />
                      </span>
                      <strong>{file.name}<small>{file.caption}</small></strong>
                      <span className="assessment-document-actions">
                        <button aria-label={`Preview ${file.name}`} type="button"><i className="ti ti-eye" /></button>
                        <button aria-label={`Download ${file.name}`} type="button"><i className="ti ti-download" /></button>
                      </span>
                    </span>
                  ))}
                  {preliminaryAttachedFiles.map((file, index) => (
                    <div className="assessment-uploaded-doc-row preliminary-uploaded-file-row" key={`${file.name}-${index}`}>
                      <span className={`assessment-attachment-thumb ${file.type === "photo" ? "photo" : "document"}`}>
                        {file.previewUrl ? <img alt="" src={file.previewUrl} /> : <i className={`ti ${file.type === "photo" ? "ti-photo" : "ti-file-description"}`} />}
                      </span>
                      <div className="preliminary-uploaded-file-fields">
                        <label>
                          <span>File name</span>
                          <input
                            onChange={(event) => updatePreliminaryAttachedFile(index, "name", event.target.value)}
                            type="text"
                            value={file.name}
                          />
                        </label>
                        <label>
                          <span>Description</span>
                          <input
                            onChange={(event) => updatePreliminaryAttachedFile(index, "caption", event.target.value)}
                            type="text"
                            value={file.caption}
                          />
                        </label>
                        <small className="preliminary-file-added-at">
                          <i className="ti ti-clock" /> Added at {formatDateTime(file.addedAt)}
                        </small>
                      </div>
                      <button aria-label={`Remove ${file.name}`} type="button" onClick={() => removePreliminaryAttachedFile(index)}>
                        <i className="ti ti-x" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        </>
      ) : record && (isResidentProbation || isAnnualResidencyReview) ? (
        <>
          <div className="assessment-section-heading-row assessment-section-heading-row--actions assessment-panel-heading-actions">
            <h2>{recordName}</h2>
            <div className="assessment-drawer-action-links assessment-page-action-links probation-action-buttons">
              <button className="primary-action" type="button" onClick={openAddDailyNoteDrawer}><i className="ti ti-note" /> Add Daily Note</button>
              <button type="button"><i className="ti ti-edit" /> Edit</button>
              <button className="success" type="button" onClick={confirmMarkCompleted}><i className="ti ti-circle-check" /> Mark as Completed</button>
            </div>
          </div>

          <div className="probation-page-grid">
            <div className="probation-main-column">
              <section className="assessment-drawer-section">
                <h3>{isAnnualResidencyReview ? "Annual Progress Period" : "Probation Period"}</h3>
                <div className="probation-summary-grid">
                  <div><span>Start date</span><strong>{formatDate(record.startDate)}</strong></div>
                  <div><span>End date</span><strong>{formatDate(record.endDate)}</strong></div>
                  <div><span>Status</span><strong><em className="pill blue">{record.status}</em></strong></div>
                </div>
                <p className="probation-description">{record.description}</p>
                {isAnnualResidencyReview ? (
                  <div className="annual-review-evaluation">
                    <h4>Overall Evaluation / Status Notes</h4>
                    <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(record.evaluationNotes)} />
                  </div>
                ) : null}
              </section>

              <section className="assessment-drawer-section">
                <div className="assessment-section-heading-row">
                  <h3>Daily Notes</h3>
                  <button className="mini-btn" type="button" onClick={openAddDailyNoteDrawer}><i className="ti ti-plus" /> Add daily note</button>
                </div>
                <div className="probation-note-toolbar">
                  <label className="ncp-form-field">
                    <span>From</span>
                    <input
                      max={probationNoteRange.end}
                      onChange={(event) => {
                        setProbationNoteRange((current) => ({ ...current, start: event.target.value }));
                        setVisibleProbationNoteCount(10);
                      }}
                      type="date"
                      value={probationNoteRange.start}
                    />
                  </label>
                  <label className="ncp-form-field">
                    <span>To</span>
                    <input
                      min={probationNoteRange.start}
                      onChange={(event) => {
                        setProbationNoteRange((current) => ({ ...current, end: event.target.value }));
                        setVisibleProbationNoteCount(10);
                      }}
                      type="date"
                      value={probationNoteRange.end}
                    />
                  </label>
                </div>
                <div className="probation-note-list">
                  {visibleProbationNotes.length ? visibleProbationNotes.map((note) => (
                    <button className="probation-note-card" type="button" onClick={() => setSelectedDailyNote(note)} key={`${note.title}-${note.date}`}>
                      <div className="probation-note-card-head">
                        <div>
                          <strong>{note.title}</strong>
                          <span>Note date: {formatDate(note.noteDate || dateInputValue(note.date))}</span>
                        </div>
                        <em>{note.category}</em>
                      </div>
                      <div className="probation-note-card-meta">
                        <span><i className="ti ti-user-plus" /> Added by {note.addedBy}</span>
                        <span><i className="ti ti-clock" /> {formatDateTime(note.date)}</span>
                        <span><i className="ti ti-edit" /> Updated by {note.updatedBy || note.addedBy}</span>
                        <span><i className="ti ti-refresh" /> {formatDateTime(note.updatedAt || note.date)}</span>
                        {note.photos?.length ? <span><i className="ti ti-photo" /> {note.photos.length} photo{note.photos.length === 1 ? "" : "s"}</span> : null}
                      </div>
                      <p>{note.summary}</p>
                    </button>
                  )) : (
                    <p className="probation-empty">No daily notes were found for the selected date range.</p>
                  )}
                  {probationNotes.length > visibleProbationNoteCount ? (
                    <button className="probation-view-more" type="button" onClick={() => setVisibleProbationNoteCount((current) => current + 10)}>
                      View more notes ({probationNotes.length - visibleProbationNoteCount} remaining)
                    </button>
                  ) : null}
                </div>
              </section>
            </div>

            <aside className="assessment-drawer-section probation-staff-panel">
              <div className="assessment-section-heading-row">
                <h3>Assigned Care Provider Staff</h3>
                <button className="mini-btn" type="button" onClick={addProbationStaff}><i className="ti ti-plus" /> Add staff</button>
              </div>
              <div className="probation-staff-list">
                {assignedProbationStaff.map((staff, index) => (
                  <article className="probation-staff-edit-card" key={`${staff.name}-${staff.from}-${index}`}>
                    <span className="tiny-icon"><i className="ti ti-user-heart" /></span>
                    <div className="probation-staff-readonly">
                      <div className="probation-staff-title-row">
                        <strong>{staff.name || "Unnamed staff member"}</strong>
                        <small>{staff.role || "Role not set"}</small>
                      </div>
                      <div className="probation-staff-time-list">
                        <p><i className="ti ti-clock-up" /> <span>From</span> {staff.from ? formatDateTime(staff.from) : "Not set"}</p>
                        <p><i className="ti ti-clock-down" /> <span>To</span> {staff.to ? formatDateTime(staff.to) : "Not set"}</p>
                      </div>
                    </div>
                    <div className="probation-staff-row-actions">
                      <button aria-label={`Edit ${staff.name || "staff assignment"}`} type="button" onClick={() => editProbationStaff(staff)}><i className="ti ti-edit" /></button>
                      <button aria-label={`Remove ${staff.name || "staff assignment"}`} type="button" onClick={() => removeProbationStaff(staff.originalIndex)}><i className="ti ti-trash" /></button>
                    </div>
                  </article>
                ))}
              </div>
            </aside>
          </div>
        </>
      ) : record && isCareMeetingRecord ? (
        <>
          <div className="assessment-section-heading-row assessment-section-heading-row--actions assessment-panel-heading-actions">
            <h2>{recordName}</h2>
            <div className="assessment-drawer-action-links assessment-page-action-links">
              <Link to={`/assessments/${assessmentProcessId}/records/${recordId}/edit`}><i className="ti ti-edit" /> Edit</Link>
              <button className="success" type="button" onClick={confirmMarkCompleted}><i className="ti ti-circle-check" /> Mark as Completed</button>
              <button className="warning" type="button"><i className="ti ti-player-skip-forward" /> Skip</button>
            </div>
          </div>
          <div className="meet-greet-detail-grid">
            <div className="meet-greet-main-column">
              <section className="assessment-drawer-section">
                <h3>{isClientOnboarding ? "Onboarding Details" : "Meeting Details"}</h3>
                <div className="assessment-detail-grid">
                  <div><span>{isClientOnboarding ? "Onboarding date" : "Date"}</span><strong>{formatDate(record.meetingDate)}</strong></div>
                <div><span>Location</span><strong>{record.location}</strong></div>
                <div><span>Social worker</span><strong>{record.socialWorker}</strong></div>
                <div><span>Facilitator</span><strong>{record.facilitator}</strong></div>
                <div className="wide"><span>Present care provider staff</span><strong>{record.careProviderStaff.join(", ")}</strong></div>
              </div>
            </section>
              <section className="assessment-drawer-section">
                <h3>Care Circle</h3>
                <div className="assessment-signed-document-list">
                  {(record.careCircle || []).map((member) => (
                    <span key={member}><i className="ti ti-users" /> {member}</span>
                  ))}
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>Questions and Responses</h3>
                <div className="assessment-question-list">
                  {record.questions.map(([question, answer]) => (
                    <article key={question}>
                      <strong>{question}</strong>
                      <p>{answer}</p>
                    </article>
                  ))}
                </div>
              </section>
            </div>
            <div className="assessment-mag-notes-column">
              <section className="assessment-drawer-section">
                <h3>Notes</h3>
                <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(record.notes)} />
              </section>
              <section className="assessment-drawer-section">
                <h3>Signed Documents</h3>
                <div className="assessment-signed-document-list">
                  {(record.signedDocuments || []).length ? (
                    (record.signedDocuments || []).map((document) => (
                      <span key={document}>
                        <i className="ti ti-circle-check" />
                        <strong>{document}</strong>
                        <span className="assessment-document-actions">
                          <button aria-label={`Preview ${document}`} type="button"><i className="ti ti-eye" /></button>
                          <button aria-label={`Download ${document}`} type="button"><i className="ti ti-download" /></button>
                        </span>
                      </span>
                    ))
                  ) : (
                    <span><i className="ti ti-file-alert" /> No signed documents recorded yet</span>
                  )}
                </div>
              </section>
              <section className="assessment-drawer-section">
                <h3>Uploaded Documents</h3>
                <div className="assessment-uploaded-doc-list">
                  {(record.uploadedDocuments || []).map((document) => (
                    <span key={document}>
                      <span className="assessment-attachment-thumb document"><i className="ti ti-file-description" /></span>
                      <strong>{document}</strong>
                      <span className="assessment-document-actions">
                        <button aria-label={`View ${document}`} type="button"><i className="ti ti-eye" /></button>
                        <button aria-label={`Download ${document}`} type="button"><i className="ti ti-download" /></button>
                      </span>
                    </span>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </>
      ) : record ? (
        <>
          <div className="assessment-section-heading-row assessment-section-heading-row--actions assessment-panel-heading-actions">
            <h2>{recordName}</h2>
            <div className="assessment-drawer-action-links assessment-page-action-links">
              <Link to={`/assessments/${assessmentProcessId}/records/${recordId}/edit`}><i className="ti ti-edit" /> Edit</Link>
              <button className="danger" type="button"><i className="ti ti-trash" /> Delete</button>
              {canPreviewDocument ? <button type="button" onClick={() => setPreviewOpen(true)}><i className="ti ti-eye" /> Preview</button> : null}
              <button className="success" type="button"><i className="ti ti-download" /> Download</button>
            </div>
          </div>
          <div className="assessment-detail-page-grid">
            <section className="assessment-drawer-section">
              <h3>Document</h3>
              <div className="assessment-detail-grid">
                <div className="wide">
                  <span>Document</span>
                  <strong className="assessment-document-title">
                    <i className="ti ti-file-description" /> {record.document}
                    {canPreviewDocument ? (
                      <button type="button" onClick={() => setPreviewOpen(true)}>
                        <i className="ti ti-eye" /> Preview
                      </button>
                    ) : null}
                  </strong>
                </div>
                <div><span>Date uploaded</span><strong>{formatDate(record.dateUploaded)}</strong></div>
                <div><span>Uploaded by</span><strong>{record.uploadedBy}</strong></div>
                <div><span>Social worker</span><strong>{record.socialWorker}</strong></div>
                <div><span>Issuer</span><strong>{record.issuer}</strong></div>
              </div>
            </section>
            <section className="assessment-drawer-section">
              <h3>Notes</h3>
              <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(record.notes)} />
            </section>
          </div>
        </>
      ) : isCareMeetingRecord ? (
        <Panel title={`Add ${recordName}`}>
          <form className="assessment-page-form assessment-edit-form">
            <div className="assessment-mag-edit-grid">
              <div className="assessment-mag-edit-main">
                <section className="assessment-drawer-section">
                  <h3>{isClientOnboarding ? "Onboarding Details" : "Meeting Details"}</h3>
                  <div className="assessment-drawer-grid">
                    <label className="ncp-form-field">
                      <span>{isClientOnboarding ? "Onboarding date" : "Meeting date"}</span>
                      <input name="meetingDate" type="date" />
                    </label>
                    <label className="ncp-form-field">
                      <span>Location</span>
                      <input name="location" placeholder="Hospital, facility, home, or virtual meeting location" />
                    </label>
                    <label className="ncp-form-field">
                      <span>Social worker</span>
                      <input name="socialWorker" defaultValue={assessment.socialWorker} />
                    </label>
                    <label className="ncp-form-field">
                      <span>Facilitator</span>
                      <input name="facilitator" />
                    </label>
                    <div className="ncp-form-field-wide">
                      <span className="assessment-inline-label">Present care provider staff members</span>
                      <div className="assessment-care-circle-form-list">
                        {careProviderStaffMembers.map(([name, role], index) => (
                          <div className="assessment-care-circle-row" key={`staff-${index}`}>
                            <label className="ncp-form-field">
                              <span>Person name</span>
                              <input defaultValue={name} name={`staffName-${index}`} placeholder="Full name" />
                            </label>
                            <label className="ncp-form-field">
                              <span>Role</span>
                              <input defaultValue={role} name={`staffRole-${index}`} placeholder="Role present" />
                            </label>
                            <button className="assessment-remove-row" type="button" onClick={() => removeCareProviderStaffMember(index)}>
                              <i className="ti ti-x" /> Remove
                            </button>
                          </div>
                        ))}
                      </div>
                      <button className="mini-btn assessment-add-row" type="button" onClick={addCareProviderStaffMember}>
                        <i className="ti ti-plus" /> Add staff member
                      </button>
                    </div>
                  </div>
                </section>
                <section className="assessment-drawer-section">
                  <h3>Care Circle</h3>
                  <div className="assessment-care-circle-form-list">
                    {careCircleMembers.map(([name, relationship], index) => (
                      <div className="assessment-care-circle-row" key={`${name}-${relationship}-${index}`}>
                        <label className="ncp-form-field">
                          <span>Person name</span>
                          <input defaultValue={name} name={`careCircleName-${index}`} placeholder="Full name" />
                        </label>
                        <label className="ncp-form-field">
                          <span>Relationship</span>
                          <input defaultValue={relationship} name={`careCircleRelationship-${index}`} placeholder="Relationship to resident" />
                        </label>
                        <button className="assessment-remove-row" type="button" onClick={() => removeCareCircleMember(index)}>
                          <i className="ti ti-x" /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <button className="mini-btn" type="button" onClick={addCustomCareCircleMember}><i className="ti ti-plus" /> Add member</button>
                  </div>
                </section>
                <section className="assessment-drawer-section">
                  <div className="assessment-section-heading-row">
                    <h3>Questions and Responses</h3>
                    <span>{meetAndGreetQuestions.length + customQuestions.length} questions</span>
                  </div>
                  <div className="assessment-question-form-list">
                    {[...meetAndGreetQuestions, ...customQuestions].map((question) => (
                      <label className="ncp-form-field ncp-form-field-wide" key={question}>
                        <span>{question}</span>
                        <textarea name={`question-${question}`} rows={2} />
                      </label>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <input value={customQuestion} placeholder="Add custom question" onChange={(event) => setCustomQuestion(event.target.value)} />
                    <button className="mini-btn" type="button" onClick={addCustomQuestion}><i className="ti ti-plus" /> Add question</button>
                  </div>
                </section>
              </div>
              <aside className="assessment-mag-notes-column assessment-mag-notes-panel">
                <section className="assessment-drawer-section">
                  <h3>Notes</h3>
                  <CareRichTextEditor height={360} onChange={setMeetNotes} value={meetNotes} />
                  <input name="notes" readOnly type="hidden" value={meetNotes} />
                </section>
                <section className="assessment-drawer-section">
                  <h3>Signed Documents</h3>
                  <div className="assessment-document-checklist">
                    {[...signedDocumentOptions, ...customDocuments].map((document) => (
                      <label key={document}>
                        <input name={`document-${document}`} type="checkbox" />
                        <span>{document}</span>
                      </label>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <input value={customDocument} placeholder="Add custom signed document" onChange={(event) => setCustomDocument(event.target.value)} />
                    <button className="mini-btn" type="button" onClick={addCustomDocument}><i className="ti ti-plus" /> Add document</button>
                  </div>
                </section>
                <section className="assessment-drawer-section">
                  <h3>Upload Documents</h3>
                  <label className="assessment-upload assessment-upload-compact">
                    <input className="assessment-file-input" multiple name="uploadedDocuments" onChange={addUploadedDocuments} type="file" />
                    <i className="ti ti-file-upload" />
                    <strong>Attach documents</strong>
                    <small>Upload PDF, Word, images, or care files related to this record.</small>
                  </label>
                  {uploadedDocuments.length ? (
                    <div className="assessment-uploaded-doc-list">
                      {uploadedDocuments.map((document, index) => (
                        <div className="assessment-uploaded-doc-row" key={`${document.fileName}-${index}`}>
                          <span className="assessment-attachment-thumb document"><i className="ti ti-file-description" /></span>
                          <div>
                            <strong>{document.fileName}</strong>
                            <label className="ncp-form-field">
                              <span>Document name</span>
                              <input
                                name={`uploadedDocumentName-${index}`}
                                onChange={(event) => updateUploadedDocumentName(index, event.target.value)}
                                placeholder="Document name"
                                value={document.name}
                              />
                            </label>
                          </div>
                          <button type="button" onClick={() => removeUploadedDocument(index)}><i className="ti ti-x" /></button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
              </aside>
            </div>
            <div className="assessment-drawer-actions">
              <Link className="btn secondary" to={`/assessments/${assessmentProcessId}/new`}>Cancel</Link>
              <button className="btn primary" type="button">Save {recordName}</button>
            </div>
          </form>
        </Panel>
      ) : (
        <Panel title={`Add ${recordName}`}>
          <form className="assessment-page-form">
            <section className="assessment-drawer-section">
              <h3>Document</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>Document</span>
                  <input name="document" type="file" />
                </label>
                <label className="ncp-form-field">
                  <span>Date uploaded</span>
                  <input name="dateUploaded" type="date" />
                </label>
                <label className="ncp-form-field">
                  <span>Uploaded by</span>
                  <input name="uploadedBy" />
                </label>
                <label className="ncp-form-field">
                  <span>Social worker</span>
                  <input name="socialWorker" defaultValue={assessment.socialWorker} />
                </label>
                <label className="ncp-form-field">
                  <span>Issuer</span>
                  <input name="issuer" placeholder="State, agency, provider, or organization" />
                </label>
                <div className="ncp-form-field-wide">
                  <CareRichTextEditor label="Notes" onChange={setRecordNotes} value={recordNotes} />
                  <input name="notes" readOnly type="hidden" value={recordNotes} />
                </div>
              </div>
            </section>
            <div className="assessment-drawer-actions">
              <Link className="btn secondary" to={`/assessments/${assessmentProcessId}/new`}>Cancel</Link>
              <button className="btn primary" type="button">Save record</button>
            </div>
          </form>
        </Panel>
      )}
      <Modal
        className="assessment-document-preview-modal"
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        open={previewOpen}
        title={`${recordName} Preview`}
        width={920}
      >
        {record ? (
          <div className="assessment-document-preview-window">
            <div className="assessment-document-preview-toolbar">
              <div>
                <span>{record.document}</span>
                <strong>{processResidentName(assessment)}</strong>
              </div>
              <button className="mini-btn" type="button"><i className="ti ti-download" /> Download</button>
            </div>
            <div className="assessment-document-page">
              <div className="assessment-document-page-head">
                <strong>State Initial Assessment</strong>
                <span>{formatDate(record.dateUploaded)}</span>
              </div>
              <div className="assessment-document-lines">
                <b>Resident</b><p>{processResidentName(assessment)}</p>
                <b>Social worker</b><p>{record.socialWorker}</p>
                <b>Issuer</b><p>{record.issuer}</p>
                <b>Assessment summary</b>
                <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(record.notes)} />
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
      <Modal
        className="assessment-document-preview-modal"
        footer={null}
        onCancel={() => setPreliminaryPreviewOpen(false)}
        open={preliminaryPreviewOpen}
        title={`${recordName} Preview`}
        width={920}
      >
        <div className="assessment-document-preview-window preliminary-plan-preview-window">
          <div className="assessment-document-preview-toolbar">
            <div>
              <span>{assessment.name || assessment.title || "Assessment process"}</span>
              <strong>{processResidentName(assessment)}</strong>
            </div>
            <button className="mini-btn" type="button" onClick={printPreliminaryPlan}><i className="ti ti-printer" /> Print</button>
          </div>
          <div className="assessment-document-page">
            <div className="assessment-document-page-head">
              <strong>{recordName}</strong>
              <span>{formatDate(new Date())}</span>
            </div>
            <div className="assessment-record-notes preliminary-plan-preview-content" dangerouslySetInnerHTML={richTextMarkup(planDescription)} />
          </div>
        </div>
      </Modal>
      <Modal
        className="ncp-digital-signature-modal"
        okButtonProps={{
          disabled: digitalSignatureSaving || !digitalSignatureForm.acknowledged || !normalizeSourceText(digitalSignatureForm.signerName) || !digitalSignatureHasInk,
          loading: digitalSignatureSaving,
        }}
        okText={digitalSignatureSaving ? "Signing..." : "Apply Signature"}
        afterOpenChange={(open) => {
          if (open) window.setTimeout(prepareSignatureCanvas, 0);
        }}
        onCancel={() => setDigitalSignatureOpen(false)}
        onOk={applyDigitalSignature}
        open={digitalSignatureOpen}
        title="Sign Negotiated Care Plan Digitally"
        width={640}
      >
        <div className="ncp-digital-signature-form">
          <p>
            Draw the signer signature below. The signed NCP keeps the handwriting on page 19 plus an audit record with the account,
            date/time, browser context, and location when shared.
          </p>
          <label className="ncp-form-field">
            <span>Signing as</span>
            <select
              value={digitalSignatureForm.role}
              onChange={(event) => {
                const role = digitalSignatureRoleByValue(event.target.value);
                setDigitalSignatureForm((current) => ({
                  ...current,
                  role: role.value,
                  signerTitle: current.signerTitle && current.signerTitle !== digitalSignatureRoleByValue(current.role).title
                    ? current.signerTitle
                    : role.title,
                }));
              }}
            >
              {digitalSignatureRoleOptions.map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </label>
          <label className="ncp-form-field">
            <span>Signer legal name</span>
            <input
              value={digitalSignatureForm.signerName}
              onChange={(event) => updateDigitalSignatureForm("signerName", event.target.value)}
            />
          </label>
          <label className="ncp-form-field">
            <span>Title / relationship</span>
            <input
              value={digitalSignatureForm.signerTitle}
              onChange={(event) => updateDigitalSignatureForm("signerTitle", event.target.value)}
            />
          </label>
          <div className="ncp-signature-pad-block">
            <div className="ncp-signature-pad-head">
              <span>Draw signature</span>
              <button className="mini-btn" type="button" onClick={clearDigitalSignatureInk}>
                <i className="ti ti-eraser" /> Clear
              </button>
            </div>
            <div className="ncp-signature-pad-shell">
              <canvas
                aria-label="Draw digital signature"
                className="ncp-signature-pad"
                onPointerDown={startDigitalSignatureStroke}
                onPointerLeave={endDigitalSignatureStroke}
                onPointerMove={moveDigitalSignatureStroke}
                onPointerUp={endDigitalSignatureStroke}
                ref={signatureCanvasRef}
              />
              <span className="ncp-signature-pad-line" />
            </div>
          </div>
          <label className="ncp-form-field">
            <span>Audit note</span>
            <textarea
              rows={2}
              value={digitalSignatureForm.note}
              onChange={(event) => updateDigitalSignatureForm("note", event.target.value)}
              placeholder="Optional signer note or recommended changes"
            />
          </label>
          <label className="ncp-signature-check">
            <input
              checked={digitalSignatureForm.includeLocation}
              onChange={(event) => updateDigitalSignatureForm("includeLocation", event.target.checked)}
              type="checkbox"
            />
            <span>Capture current browser location for the audit trail</span>
          </label>
          <label className="ncp-signature-check">
            <input
              checked={digitalSignatureForm.acknowledged}
              onChange={(event) => updateDigitalSignatureForm("acknowledged", event.target.checked)}
              type="checkbox"
            />
            <span>I confirm the signer has read and agreed to the negotiated care plan.</span>
          </label>
          <div className="ncp-signature-account-note">
            Signed-in account: {personName(user) || "Unknown user"}{user?.email ? ` (${user.email})` : ""}
          </div>
        </div>
      </Modal>
      <Modal
        className="assessment-document-preview-modal"
        footer={null}
        afterOpenChange={(open) => {
          if (open) {
            window.setTimeout(() => negotiatedPreviewRef.current?.scrollTo({ top: 0, left: 0 }), 0);
          }
        }}
        onCancel={() => setNegotiatedPreviewOpen(false)}
        open={negotiatedPreviewOpen}
        title={`${recordName} Preview`}
        width={1180}
      >
        <div className="assessment-document-preview-window negotiated-preview-window" ref={negotiatedPreviewRef}>
          <div className="assessment-document-preview-toolbar">
            <div>
              <span>{record?.templateName}</span>
              <strong>{processResidentName(assessment)}</strong>
            </div>
            <button className="mini-btn" type="button" onClick={printNegotiatedPlan}><i className="ti ti-printer" /> Print</button>
          </div>
          <div className="assessment-document-page negotiated-preview-page">
            <p className="ncp-document-note">NOTE: Place an X in the bracket [] - [x] - to indicate/select your choice. Form Version: {record?.formVersion}</p>
            <div className="assessment-record-notes preliminary-plan-preview-content negotiated-template-preview-content" dangerouslySetInnerHTML={richTextMarkup(negotiatedTemplateDocumentHtml)} />
          </div>
        </div>
      </Modal>
      <Drawer
        title={dailyNoteEditingNote ? "Edit Daily Note" : "Add Daily Note"}
        placement="right"
        open={dailyNoteDrawerOpen}
        onClose={() => {
          setDailyNoteDrawerOpen(false);
          setDailyNoteEditingNote(null);
        }}
        width={640}
        className="resident-action-drawer daily-note-drawer"
      >
        <form
          className="assessment-drawer-form"
          onSubmit={(event) => {
            event.preventDefault();
            saveDailyNote();
          }}
        >
          <section className="assessment-drawer-section">
            <h3>Note Details</h3>
            <div className="assessment-drawer-grid">
              <label className="ncp-form-field">
                <span>Note date</span>
                <input name="noteDate" type="date" value={dailyNoteDate} onChange={(event) => setDailyNoteDate(event.target.value)} />
              </label>
              <label className="ncp-form-field">
                <span>Category</span>
                <select name="category" value={dailyNoteCategory} onChange={(event) => setDailyNoteCategory(event.target.value)}>
                  {dailyNoteCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </label>
              {dailyNoteCategory === "Other" ? (
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>Specify category</span>
                  <input name="otherCategory" placeholder="Enter note category" value={dailyNoteOtherCategory} onChange={(event) => setDailyNoteOtherCategory(event.target.value)} />
                </label>
              ) : null}
            </div>
          </section>

          <section className="assessment-drawer-section">
            <h3>Notes</h3>
            <CareRichTextEditor height={260} onChange={setDailyNoteRichText} value={dailyNoteRichText} />
            <input name="noteBody" readOnly type="hidden" value={dailyNoteRichText} />
          </section>

          <section className="assessment-drawer-section">
            <h3>Attach Photo</h3>
            <label className="assessment-upload assessment-upload-compact">
              <input accept="image/*,.heic,.heif" className="assessment-file-input" disabled={dailyNotePhotoProcessing} multiple name="photos" onChange={addDailyNotePhotos} type="file" />
              <i className="ti ti-photo-plus" />
              <strong>{dailyNotePhotoProcessing ? "Converting HEIC photos..." : "Attach photos"}</strong>
              <small>Add one or more photos related to this daily note.</small>
            </label>
            {dailyNotePhotos.length ? (
              <div className="assessment-uploaded-doc-list">
                {dailyNotePhotos.map((photo, index) => (
                  <div className="assessment-uploaded-doc-row" key={`${photo.fileName}-${index}`}>
                    <span className="assessment-attachment-thumb photo">
                      {photo.previewUrl ? <img alt="" src={photo.previewUrl} /> : <i className="ti ti-photo" />}
                    </span>
                    <div>
                      <strong>{photo.fileName}</strong>
                      {photo.convertedFromHeic || photo.conversionWarning ? (
                        <small>
                          {photo.convertedFromHeic ? `Converted from ${photo.originalFileName}` : photo.conversionWarning}
                        </small>
                      ) : null}
                      <label className="ncp-form-field">
                        <span>Photo description</span>
                        <input
                          name={`dailyNotePhotoDescription-${index}`}
                          onChange={(event) => updateDailyNotePhotoDescription(index, event.target.value)}
                          placeholder="Photo description"
                          value={photo.description}
                        />
                      </label>
                    </div>
                    <button type="button" onClick={() => removeDailyNotePhoto(index)}><i className="ti ti-x" /></button>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <div className="assessment-drawer-actions">
            <button className="btn secondary" type="button" onClick={() => {
              setDailyNoteDrawerOpen(false);
              setDailyNoteEditingNote(null);
            }}>Cancel</button>
            <button
              className="btn primary"
              disabled={dailyNoteSaving || dailyNotePhotoProcessing}
              type="submit"
            >
              {dailyNoteSaving ? (
                <>
                  <i className="ti ti-loader-2" />
                  Saving...
                </>
              ) : dailyNoteEditingNote ? "Save Changes" : "Save Daily Note"}
            </button>
          </div>
        </form>
      </Drawer>
      <Drawer
        title="Daily Note Details"
        placement="right"
        open={Boolean(selectedDailyNote)}
        onClose={() => setSelectedDailyNote(null)}
        width={560}
        className="resident-action-drawer daily-note-drawer"
      >
        {selectedDailyNote ? (
          <div className="resident-drawer-content">
            <div className="assessment-drawer-action-links">
              <button type="button" onClick={() => openEditDailyNoteDrawer(selectedDailyNote)}><i className="ti ti-edit" /> Edit</button>
              <button
                className="danger"
                disabled={dailyNoteSaving}
                type="button"
                onClick={() => {
                  Modal.confirm({
                    title: "Delete daily note?",
                    content: "This will remove this daily note from the assessment record.",
                    okText: "Delete",
                    okButtonProps: { danger: true },
                    onOk: () => deleteDailyNote(selectedDailyNote),
                  });
                }}
              >
                <i className="ti ti-trash" /> Delete
              </button>
            </div>
            <section className="assessment-drawer-section">
              <h3>Note Details</h3>
              <div className="assessment-detail-grid">
                <div className="wide"><span>Title</span><strong>{selectedDailyNote.title}</strong></div>
                <div><span>Category</span><strong>{selectedDailyNote.category}</strong></div>
                <div><span>Note date</span><strong>{formatDate(selectedDailyNote.noteDate || dateInputValue(selectedDailyNote.date))}</strong></div>
                <div className="wide audit-pair"><span>Added</span><strong>{selectedDailyNote.addedBy} at {formatDateTime(selectedDailyNote.date)}</strong></div>
                <div className="wide audit-pair"><span>Updated</span><strong>{selectedDailyNote.updatedBy || selectedDailyNote.addedBy} at {formatDateTime(selectedDailyNote.updatedAt || selectedDailyNote.date)}</strong></div>
              </div>
            </section>
            <section className="assessment-drawer-section">
              <h3>Notes</h3>
              <div className="assessment-record-notes" dangerouslySetInnerHTML={richTextMarkup(selectedDailyNote.notes || `<p>${selectedDailyNote.summary || ""}</p>`)} />
            </section>
            <section className="assessment-drawer-section">
              <h3>Attached Photos</h3>
              {selectedDailyNote.photos?.length ? (
                <div className="assessment-uploaded-doc-list">
                  {selectedDailyNote.photos.map((photo, index) => (
                    <div className="assessment-uploaded-doc-row" key={`${photo.fileName}-${index}`}>
                      <span className="assessment-attachment-thumb photo">
                        {photo.previewUrl ? <img alt="" src={photo.previewUrl} /> : <i className="ti ti-photo" />}
                      </span>
                      <div>
                        <strong>{photo.fileName}</strong>
                        <small>{photo.description || photo.fileName}</small>
                        {photo.convertedFromHeic || photo.conversionWarning ? (
                          <small>
                            {photo.convertedFromHeic ? `Converted from ${photo.originalFileName}` : photo.conversionWarning}
                          </small>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="probation-empty">No photos attached to this note.</p>
              )}
            </section>
          </div>
        ) : null}
      </Drawer>
      <Drawer
        title={probationStaffDrawer?.index === null ? "Add Assigned Staff" : "Edit Assigned Staff"}
        placement="right"
        open={Boolean(probationStaffDrawer)}
        onClose={() => setProbationStaffDrawer(null)}
        width={560}
        className="resident-action-drawer daily-note-drawer"
      >
        {probationStaffDrawer ? (
          <form className="assessment-drawer-form">
            <section className="assessment-drawer-section">
              <h3>Staff Assignment</h3>
              <div className="assessment-drawer-grid">
                <label className="ncp-form-field">
                  <span>Name</span>
                  <input
                    value={probationStaffDrawer.values.name}
                    onChange={(event) => updateProbationStaffDrawerField("name", event.target.value)}
                    placeholder="Staff name"
                  />
                </label>
                <label className="ncp-form-field">
                  <span>Role</span>
                  <input
                    value={probationStaffDrawer.values.role}
                    onChange={(event) => updateProbationStaffDrawerField("role", event.target.value)}
                    placeholder="Care provider role"
                  />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>From</span>
                  <input
                    type="datetime-local"
                    value={(probationStaffDrawer.values.from || "").slice(0, 16)}
                    onChange={(event) => updateProbationStaffDrawerField("from", event.target.value)}
                  />
                </label>
                <label className="ncp-form-field ncp-form-field-wide">
                  <span>To</span>
                  <input
                    type="datetime-local"
                    value={(probationStaffDrawer.values.to || "").slice(0, 16)}
                    onChange={(event) => updateProbationStaffDrawerField("to", event.target.value)}
                  />
                </label>
              </div>
            </section>
            <div className="assessment-drawer-actions">
              <button className="btn secondary" type="button" onClick={() => setProbationStaffDrawer(null)}>Cancel</button>
              <button className="btn primary" type="button" onClick={saveProbationStaffDrawer}>Save Staff</button>
            </div>
          </form>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
